import React, { Suspense, lazy, useEffect, useLayoutEffect, useMemo, useState, useCallback, useRef } from 'react';
import MonthlyGstExpenseModal from '../components/reports/MonthlyGstExpenseModal';
import MonthlyReportTableRow from '../components/reports/MonthlyReportTableRow';
import CollapsibleReportSection from '../components/reports/CollapsibleReportSection';
import {
  newMonthlyExpenseLineId,
  sumMonthlyExpenseLines,
  sumMonthlyExpenseLinesExcludingSalaryLike,
  sumSalaryLikeExpenseLines,
  gstClaimableTotalFromLines,
  gstEligibleExpenseLines,
  expenseLinesGstAllocationComplete,
  nonSalaryExpenseLines,
} from '../lib/monthlyReportExpenseHelpers';
import { useSoftKeyboardField } from '../hooks/useSoftKeyboardField';
import type {
  Category,
  CurrentUser,
  DailyExpense,
  IntegrationSettings,
  ManualSalesOverride,
  InventoryItem,
  KitchenSLASettings,
  MenuItem,
  Order,
  Recipe,
  RegisterSession,
  StaffMember,
  Table,
  AuditLogEntry,
  ReportsDailySummary,
  MonthlyReportExpenseLine,
  MonthlyReportDayEntry,
  PayrollPeriodImport,
  Page,
} from '../types';
import { config } from '../lib/config';
import { ensureVpsAuditFeedRegistered, ensureVpsReportsFeedRegistered, isVpsApiEnabled } from '../lib/vpsApi';
import LiveDataStatusBadge from '../components/common/LiveDataStatusBadge';
import { buildEventStallUnitCostMap } from '../lib/normalizeEventStallSettings';
import {
  getDailySales,
  getMonthlySalesBreakdown,
  getRegisterLogs,
  getSalesInsights,
  getSalesByInvoicePage,
  getExecutiveDashboardMetrics,
  getTimesheetLaborSummary,
  getTheoreticalVsActualFoodCostReport,
  getFlashPnlReport,
  getWeekEndingDate,
  getWeekStartTimestamp,
  getCouponDashboardAnalytics,
  DailySalesRow,
  MonthlySalesRow,
  RegisterLogRow,
  SalesInsights,
  SalesByInvoiceRow,
  PaymentBreakdownRow,
  CategoryPerformanceRow,
  TopItemRow,
  ExecutiveDashboardMetrics,
  TheoreticalVsActualFoodCostReport,
  FlashPnlReport,
  type TimesheetLaborSummary,
  type RosterShiftForReport,
  type TimesheetEntryForReport,
  type CouponDashboardAnalytics,
} from '../lib/reports';
import { exportToCSV, exportToExcel, exportToPDF, ExportColumn } from '../lib/exportUtils';
import { printRegisterCloseSummary } from '../lib/registerCloseSummaryPrint';
import {
  ordersService,
  manualSalesOverridesService,
  registerSessionsService,
  mergeRegisterSessionsById,
  rosterService,
  timesheetService,
  createWithId,
  auditLogsService,
  reportsDailySummaryService,
  dailyExpensesService,
  monthlyReportDayEntriesService,
  monthlyReportImportTransactionsService,
  payrollPeriodImportsService,
  bankExpenseCategorizationRulesService,
} from '../lib/database';
import ReportErrorBoundary from '../components/reports/ReportErrorBoundary';
import { sanitizeOrderItemsForFirestore } from '../lib/orderPersistence';
import { uploadExpenseReceipt } from '../lib/expenseReceiptUpload';
import { getOrderTotalFromItems } from '../lib/orderUtils';
import { isSampleOrDemoOrderId } from '../lib/sampleOrderIds';
import {
  collectActiveRegisterSessions,
  isRegisterSessionActive,
  mergeRegisterSessionRecords,
  normalizeRegisterSession,
  registerSessionStartMs,
} from '../lib/registerSessionUtils';
import { toMillis } from '../lib/orderTimestamps';
import { isOpenUnpaidOrder } from '../lib/pendingOrderUtils';
import { reportableGrossPayFromImport } from '../lib/payrollSalaryAdjustments';
import { buildAnzDebitExpenseLine } from '../lib/anzStatementExpenseLine';
import { GRANULAR_EXPENSE_CATEGORIES, DAILY_EXPENSE_STAFF_WAGES_CATEGORY } from '../lib/granularExpenseCategories';

/** Human-readable labels for audit log actions (stored as JSON in Firestore) */
const AUDIT_ACTION_LABELS: Record<string, string> = {
  'register.open': 'Open register',
  'register.close': 'Close register',
  'register.force_close': 'Force close register',
  'order.placed': 'Order placed (send to kitchen)',
  'order.status_change': 'Order status changed',
  'order.cancelled': 'Order cancelled',
  'order.paid': 'Order marked as paid',
  'order.payment': 'Payment processed',
  'order.kitchen_start': 'Kitchen started order',
  'order.soft_delete': 'Order removed (soft delete)',
  'order.hide_from_kitchen': 'Order hidden from kitchen',
  'receipt.print': 'Receipt printed',
  'kitchen.print': 'Kitchen order printed',
  'inventory.update': 'Inventory item updated',
  'inventory.create': 'Inventory item created',
  'inventory.adjust_stock': 'Inventory stock adjusted',
  'inventory.stock_take': 'Stock take completed',
  'manual_sales.override_set': 'Manual sales override set',
};

/** Expense categories for daily expense entry (shared with bank/GST granular list). */
const EXPENSE_CATEGORIES = GRANULAR_EXPENSE_CATEGORIES;

function lastCalendarDayOfMonth(ym: string): string {
  const [ys, ms] = ym.split('-');
  const y = Number(ys);
  const m = Number(ms);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return `${ym}-28`;
  const last = new Date(y, m, 0);
  return `${ym}-${String(last.getDate()).padStart(2, '0')}`;
}

function formatAuditDetails(details: Record<string, unknown> | undefined): string {
  if (!details || Object.keys(details).length === 0) return '—';
  try {
    return JSON.stringify(details, null, 2);
  } catch {
    return String(details);
  }
}

function getAuditOrderId(entry: AuditLogEntry): string | null {
  if (entry.entityType === 'order' && entry.entityId) return String(entry.entityId);
  const detailsOrderId = entry.details?.orderId;
  if (detailsOrderId != null && String(detailsOrderId).trim()) return String(detailsOrderId).trim();
  return null;
}

function getStatementField(row: Record<string, unknown>, ...candidates: string[]): unknown {
  for (const c of candidates) {
    if (c in row) {
      const v = row[c];
      if (v !== undefined && v !== null && v !== '') return v;
    }
  }
  const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
  const want = new Set(candidates.map((c) => norm(c)));
  for (const k of Object.keys(row)) {
    if (want.has(norm(k))) {
      const v = row[k];
      if (v !== undefined && v !== null && v !== '') return v;
    }
  }
  return undefined;
}

function getStatementStringField(row: Record<string, unknown>, ...candidates: string[]): string {
  return String(getStatementField(row, ...candidates) ?? '').trim();
}

type XlsxForDateSerial = { SSF: { parse_date_code: (n: number) => { y?: number; m?: number; d?: number } } };

/**
 * Parse ANZ / common NZ bank export dates: Excel/JS Date, serial, "DD MMM YYYY", ISO, DD/MM/YYYY.
 * For Excel serial numbers, pass the loaded `xlsx` module (e.g. from `import('xlsx')`).
 */
function parseAnzStatementDate(value: unknown, xlsxForSerial?: XlsxForDateSerial): string | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (!xlsxForSerial) return null;
    const parsed = xlsxForSerial.SSF.parse_date_code(value);
    if (parsed?.y && parsed?.m && parsed?.d) {
      return `${String(parsed.y).padStart(4, '0')}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`;
    }
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{1,2}-\d{1,2}/.test(trimmed)) {
    const iso = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (iso) {
      return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
    }
  }
  const slash = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slash) {
    const d = Number(slash[1]);
    const m = Number(slash[2]);
    let y = Number(slash[3]);
    if (y < 100) y += 2000;
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 2000 && y <= 2100) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }
  const match = trimmed.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if (!match) return null;
  const day = Number(match[1]);
  const monthMap: Record<string, string> = {
    jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
    jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
  };
  const month = monthMap[match[2].toLowerCase()];
  if (!month || day < 1 || day > 31) return null;
  return `${match[3]}-${month}-${String(day).padStart(2, '0')}`;
}

function parseAnzAmount(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed
    .replace(/\$/g, '')
    .replace(/,/g, '')
    .replace(/\s+/g, '')
    .replace(/^-\$/, '-');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Signed amount: prefer "Amount"; else Debits (negative) / Credits (positive) columns used by some bank CSVs.
 */
function parseAnzRowSignedAmount(row: Record<string, unknown>): number | null {
  const direct = parseAnzAmount(getStatementField(row, 'Amount', 'Amount ($)', 'Net Amount', 'Net'));
  if (direct != null) return direct;
  const debitRaw = getStatementField(row, 'Debits', 'Debit', 'Debit amount', 'Withdrawals', 'Money Out');
  const creditRaw = getStatementField(row, 'Credits', 'Credit', 'Credit amount', 'Deposits', 'Money In');
  const deb = parseAnzAmount(debitRaw);
  const cred = parseAnzAmount(creditRaw);
  if (deb != null && Math.abs(deb) > 0) return -Math.abs(deb);
  if (cred != null && Math.abs(cred) > 0) return Math.abs(cred);
  return null;
}

/** Strip `@@dup@@n` suffix from import transaction ids (same baseKey, multiple bank lines). */
function stripAnzImportDupSuffix(id: string): string {
  return id.replace(/@@dup@@\d+$/i, '');
}

/** Stable key from row fields; duplicate rows in a statement (same payee/amount) still share this until disambiguation. */
function buildAnzImportBaseKey(
  date: string,
  amount: number,
  row: Record<string, unknown>
): string {
  const code = getStatementStringField(row, 'Code', 'OP code', 'Tran code');
  const details = getStatementStringField(row, 'Details', 'Particulars', 'Description', 'Narration', 'Payee', 'Transaction Details', 'Transaction detail');
  const reference = getStatementStringField(row, 'Reference', 'Code reference', 'Your reference', 'Our reference', 'Ref');
  const type = getStatementStringField(row, 'Type', 'Transaction Type', 'Trans type');
  const balance = getStatementStringField(
    row,
    'Balance',
    'Running Balance',
    'Balance ($)',
    'Closing Balance',
    'Closing balance'
  );
  const payee = getStatementStringField(row, 'OP name', 'Other party');
  const extra = [balance, payee].filter(Boolean).join('|');
  return [date, amount.toFixed(2), code, details, reference, type, extra].join('|');
}


function isFirestorePermissionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = String((error as { code?: unknown }).code ?? '').toLowerCase();
  const message = String((error as { message?: unknown }).message ?? '').toLowerCase();
  return code.includes('permission-denied') || message.includes('missing or insufficient permissions');
}

const MONTHLY_REPORT_STORAGE_PREFIX = 'monthly-report-import-v1';

type MonthlyPersistedImportState = {
  /** Per-day expense lines (preferred); GST is set per line in the modal */
  expenseLinesByDate?: Record<string, MonthlyReportExpenseLine[]>;
  /** Legacy single total per day (migrated to expenseLines on load) */
  expensesByDate?: Record<string, string>;
  expenseGstClaimableByDate?: Record<string, boolean>;
  smartPayByDate: Record<string, number>;
  importedTxnIds: Record<string, true>;
  completionFilter?: 'all' | 'complete' | 'incomplete';
  dateFromFilter?: string;
  dateToFilter?: string;
  terminalIdsFilter?: string;
  discountFilter?: 'all' | 'with-discount' | 'without-discount';
  columnVisibility?: Record<string, boolean>;
  isColumnVisibilityOpen?: boolean;
};

function getMonthlyStorageKey(month: string): string {
  return `${MONTHLY_REPORT_STORAGE_PREFIX}:${month}`;
}

function migrateLegacyMonthlyExpenseMaps(
  expensesByDate: Record<string, string> | undefined,
  gstClaimableByDate: Record<string, boolean> | undefined
): Record<string, MonthlyReportExpenseLine[]> {
  const out: Record<string, MonthlyReportExpenseLine[]> = {};
  if (!expensesByDate) return out;
  Object.entries(expensesByDate).forEach(([date, raw]) => {
    const amt = Number(raw) || 0;
    if (amt <= 0) return;
    out[date] = [
      {
        id: newMonthlyExpenseLineId(),
        amount: amt,
        gstClaimable: gstClaimableByDate?.[date] === true,
        zeroGst: false,
        label: 'Expense (legacy)',
        source: 'manual',
      },
    ];
  });
  return out;
}

function mergeExpenseLinesFromRemoteEntry(entry: MonthlyReportDayEntry): MonthlyReportExpenseLine[] {
  if (entry.expenseLines && entry.expenseLines.length > 0) {
    return entry.expenseLines.map((line) => ({
      ...line,
      id: line.id || newMonthlyExpenseLineId(),
      amount: Number(line.amount) || 0,
      gstClaimable: line.gstClaimable === true,
      zeroGst: line.zeroGst === true,
      isWageOrPayrollExpense: line.isWageOrPayrollExpense === true,
      isWageGstClaimable: line.isWageGstClaimable === true,
      expenseCategory: line.expenseCategory,
      autoCategorized: line.autoCategorized === true,
    }));
  }
  const total = Number(entry.expenseTotal) || 0;
  if (total <= 0) return [];
  return [
    {
      id: `${entry.id}-legacy`,
      amount: total,
      gstClaimable: entry.expenseGstClaimable === true,
      zeroGst: false,
      label: 'Expense (legacy)',
      source: 'manual',
    },
  ];
}

type MonthlyColumnVisibilityKey =
  | 'date'
  | 'totalOrders'
  | 'dineInOrders'
  | 'dineInAmount'
  | 'takeawayOrders'
  | 'takeawayAmount'
  | 'deliveryOrders'
  | 'deliveryAmount'
  | 'uberDoordashOrders'
  | 'uberDoordashAmount'
  | 'totalSales'
  | 'cashSales'
  | 'cardSales'
  | 'bankTransferSales'
  | 'discount'
  | 'gstCollected'
  | 'smartPay'
  | 'cardTerminalIds'
  | 'expenses';

const DEFAULT_MONTHLY_COLUMN_VISIBILITY: Record<MonthlyColumnVisibilityKey, boolean> = {
  date: true,
  totalOrders: false,
  dineInOrders: false,
  dineInAmount: true,
  takeawayOrders: false,
  takeawayAmount: true,
  deliveryOrders: false,
  deliveryAmount: false,
  uberDoordashOrders: false,
  uberDoordashAmount: true,
  totalSales: true,
  cashSales: true,
  cardSales: true,
  bankTransferSales: false,
  discount: true,
  gstCollected: false,
  smartPay: true,
  cardTerminalIds: false,
  expenses: true,
};

interface ReportsPageProps {
  orders: Order[];
  registerSessions: RegisterSession[];
  /** Live active sessions from subscribeActive (authoritative for open-register UI). */
  activeRegisterSessions?: RegisterSession[];
  /** Authoritative active session from POS (subscribeActive); fills gaps when the bounded hook lags. */
  currentRegisterSession?: RegisterSession | null;
  inventoryItems: InventoryItem[];
  onForceCloseSession: (sessionId: string, values: { closingFloat: number; cashSales: number; cardSales: number; variance: number; cashDenominations?: { denom: number; count: number }[] }) => Promise<void>;
  onRequireManagerPin: (action: 'open' | 'close', onApproved: () => void) => void;
  currentUser?: CurrentUser | null;
  tables?: Table[];
  recipes?: Recipe[];
  menuItems?: MenuItem[];
  staffMembers?: StaffMember[];
  kitchenSLA?: KitchenSLASettings | null;
  categories?: Category[];
  integrationSettings?: IntegrationSettings | null;
  onSaveIntegrationSettings?: (settings: IntegrationSettings) => Promise<void> | void;
  /** Opens Coupon Campaigns; pass campaignId to focus that campaign. */
  onNavigateToCouponCampaigns?: (campaignId?: string) => void;
  detailedSection?: 'home' | 'sales-insights' | 'event-stall' | 'financial' | 'reconciliation' | 'food-cost';
  onNavigateToReportPage?: (page: Page) => void;
}

type SalesByHourPoint = {
  label: string;
  total: number;
  dineInOrders: number;
  takeawayOrders: number;
  deliveryOrders: number;
  buffetOrders: number;
};

const SalesByHourEChart = lazy(() => import('../components/reports/SalesByHourEChart'));
const EventStallCategoryByHourEChart = lazy(() => import('../components/reports/EventStallCategoryByHourEChart'));
const PayrollReconciliationPanel = lazy(() => import('../components/reports/PayrollReconciliationPanel'));
const FinancialDateRangePanel = lazy(() => import('../components/reports/FinancialDateRangePanel'));

// Sales by Hour: total as bars ($), order types as lines (counts)
const BarChart: React.FC<{ data: SalesByHourPoint[]; title?: string; containerHeight?: number }> = ({ data, title = 'Sales by Hour', containerHeight = 224 }) => {
  return (
    <Suspense
      fallback={
        <div className="rounded-xl bg-gray-50/80 p-4">
          <h3 className="text-base font-semibold text-gray-800 mb-3">{title}</h3>
          <div className="rounded-lg bg-white/70 border border-gray-200 animate-pulse" style={{ height: containerHeight }} />
        </div>
      }
    >
      <SalesByHourEChart data={data} title={title} containerHeight={containerHeight} />
    </Suspense>
  );
};

// Use local date, not UTC (toISOString would give yesterday in NZ/east-of-UTC morning)
const formatInputDate = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
const getDateNDaysAgo = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return formatInputDate(d);
};

// Helper function to get date range (moved here for use in component)
function getDateRangeHelper(startDate: string, endDate: string) {
  const start = new Date(startDate ? `${startDate}T00:00:00` : Date.now());
  const end = new Date(endDate ? `${endDate}T23:59:59` : Date.now());
  return { start: start.getTime(), end: end.getTime() };
}

/** Legacy stall sales recorded as takeaway etc.: match lines against current Event menu IDs */
const EVENT_STALL_LEGACY_CATALOG_SHARE = 0.85;

function collectEventStallMenuItemIds(settings: IntegrationSettings | null | undefined): Set<number> {
  const s = new Set<number>();
  for (const row of settings?.eventStall?.eventMenuItems ?? []) {
    const id = Number(row.id);
    if (Number.isFinite(id)) s.add(id);
  }
  for (const evt of settings?.eventStall?.events ?? []) {
    for (const row of evt.menuSnapshot ?? []) {
      const id = Number(row.id);
      if (Number.isFinite(id)) s.add(id);
    }
  }
  return s;
}

function orderIncludedInEventStallSalesReport(
  o: Order,
  range: { start: number; end: number },
  eventMenuIds: Set<number>,
): boolean {
  if (o.paymentStatus !== 'paid' || o.status === 'cancelled' || o.status === 'refunded') return false;
  if (o.timestamp < range.start || o.timestamp > range.end) return false;
  if (o.orderType === 'event') return true;
  if (o.eventOperatorId != null && String(o.eventOperatorId).trim() !== '') return true;
  if (o.eventFulfillmentType) return true;
  if (eventMenuIds.size === 0) return false;
  const lines = (o.items ?? []).filter((l) => !l.removed);
  if (lines.length === 0) return false;
  let matchedRev = 0;
  let totalRev = 0;
  for (const line of lines) {
    const rev = (Number(line.price) || 0) * (Number(line.quantity) || 0);
    totalRev += rev;
    const id = Number(line.id);
    if (Number.isFinite(id) && eventMenuIds.has(id)) matchedRev += rev;
  }
  return totalRev > 0 && matchedRev / totalRev >= EVENT_STALL_LEGACY_CATALOG_SHARE;
}
const formatNzDateTime = (timestamp?: number | unknown) => {
  const ms = toMillis(timestamp);
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};
const formatHoursOpen = (startTime: number | unknown) => {
  const startMs = toMillis(startTime);
  if (!startMs) return '—';
  const hours = (Date.now() - startMs) / (1000 * 60 * 60);
  return hours > 0 ? hours.toFixed(1) : '0.0';
};
const formatCurrency = (value: number) => `$${Number(value).toFixed(2)}`;
const DASHBOARD_LOADING_EXPECTED_MS = 30000;
const formatWaitTime = (ms: number) => {
  const clamped = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  if (minutes <= 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
};
const OUTSOURCED_STAFF_EXCLUSIONS = new Set(['sooraj soman', 'mukesh kumar', 'safvana']);
const normStaffName = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const ReportsPage: React.FC<ReportsPageProps> = ({
  orders,
  registerSessions,
  activeRegisterSessions = [],
  currentRegisterSession = null,
  inventoryItems,
  onForceCloseSession,
  onRequireManagerPin,
  currentUser,
  tables = [],
  recipes = [],
  menuItems = [],
  staffMembers = [],
  kitchenSLA = null,
  categories = [],
  integrationSettings = null,
  onSaveIntegrationSettings,
  onNavigateToCouponCampaigns,
  detailedSection = 'home',
  onNavigateToReportPage,
}) => {
  const isAdmin = Boolean(currentUser?.isAdmin);
  const isMonthlyDetailedSection = false;
  const canViewPayrollReconciliation = Boolean(
    currentUser?.isAdmin || currentUser?.permissions?.has('view_staff_payroll')
  );
  const canManageCouponCampaigns = Boolean(currentUser?.isAdmin || currentUser?.permissions?.has('manage_settings'));
  const externalStatementAppUrl = integrationSettings?.externalStatementAppUrl?.trim() ?? '';
  const externalPayrollAppUrl = integrationSettings?.externalPayrollAppUrl?.trim() ?? '';
  const staffMap = useMemo(() => {
    const m = new Map<number, string>();
    staffMembers.forEach((s) => m.set(s.id, s.name));
    return m;
  }, [staffMembers]);

  // --- Data Processing ---
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const todaysOrders = orders.filter((o) => o.timestamp >= today.getTime());

  const sameDayLastWeekOrders = useMemo(() => {
    const lastWeekStart = today.getTime() - 7 * 24 * 60 * 60 * 1000;
    const lastWeekEnd = lastWeekStart + 24 * 60 * 60 * 1000 - 1;
    return orders.filter((o) => o.timestamp >= lastWeekStart && o.timestamp <= lastWeekEnd);
  }, [orders, today]);

  // Home dashboard: bounded date-range fetch + live App orders merged in (see effect below).
  const isReportsHomeSection = detailedSection === 'home';
  const [homeWindowOrders, setHomeWindowOrders] = useState<Order[]>([]);
  const [homeWindowLoading, setHomeWindowLoading] = useState(false);
  const [homeWindowError, setHomeWindowError] = useState<string | null>(null);
  const ordersForReports = isReportsHomeSection ? homeWindowOrders : orders;
  const sessionsForReports = useMemo(
    () =>
      mergeRegisterSessionsById([
        registerSessions,
        activeRegisterSessions,
        isRegisterSessionActive(currentRegisterSession) ? [currentRegisterSession!] : [],
      ]),
    [registerSessions, activeRegisterSessions, currentRegisterSession],
  );

  const [showOpenRegisterModal, setShowOpenRegisterModal] = useState(false);
  const [hydratedActiveRegisterSessions, setHydratedActiveRegisterSessions] = useState<RegisterSession[]>([]);
  const refreshActiveRegisterSessions = useCallback(async () => {
    try {
      const active = await registerSessionsService.getActiveSessions({ preferServer: true });
      const hydrated = await Promise.all(
        active.map(async (session) => {
          let merged = normalizeRegisterSession(session);
          const needsHydration =
            registerSessionStartMs(merged) <= 0 ||
            !merged.cashierName?.trim() ||
            (!merged.terminalName?.trim() && !merged.terminalId);
          if (needsHydration) {
            const full = await registerSessionsService.getById(merged.id);
            if (full) merged = mergeRegisterSessionRecords(merged, full);
          }
          if (registerSessionStartMs(merged) <= 0 || !merged.cashierName?.trim()) {
            try {
              const logs = await auditLogsService.getByDateRange(
                Date.now() - 30 * 24 * 60 * 60 * 1000,
                Date.now(),
              );
              const openLog = logs.find(
                (entry) => entry.action === 'register.open' && entry.entityId === merged.id,
              );
              if (openLog) {
                const details = openLog.details ?? {};
                merged = mergeRegisterSessionRecords(merged, {
                  ...merged,
                  startTime: openLog.timestamp,
                  cashierName:
                    (typeof details.cashierName === 'string' ? details.cashierName : undefined) ??
                    openLog.actorName ??
                    merged.cashierName,
                  terminalName:
                    (typeof details.terminalName === 'string' ? details.terminalName : undefined) ??
                    merged.terminalName,
                  terminalId:
                    (typeof details.terminalId === 'string' ? details.terminalId : undefined) ??
                    merged.terminalId,
                  openingFloat:
                    typeof details.openingFloat === 'number'
                      ? details.openingFloat
                      : merged.openingFloat,
                });
              }
            } catch {
              /* audit fallback is best-effort */
            }
          }
          return merged;
        }),
      );
      setHydratedActiveRegisterSessions(hydrated);
    } catch (err) {
      console.error('Failed to refresh active register sessions for reports:', err);
    }
  }, []);

  // Detailed report: selectable date and optional manual sales override (for backfilling missing orders)
  const [reportSummaryDate, setReportSummaryDate] = useState(() => formatInputDate(new Date()));
  const [vpsDailySummary, setVpsDailySummary] = useState<ReportsDailySummary | null>(null);
  const [vpsDailySummaryLoading, setVpsDailySummaryLoading] = useState(false);
  const [manualOverride, setManualOverride] = useState<ManualSalesOverride | null>(null);
  const [manualOverrideLoading, setManualOverrideLoading] = useState(false);
  const [manualSalesModalOpen, setManualSalesModalOpen] = useState(false);
  const [manualSalesForm, setManualSalesForm] = useState({ date: '', totalRevenue: '', totalOrders: '', note: '' });
  const [manualSalesSaving, setManualSalesSaving] = useState(false);
  const [manualSalesError, setManualSalesError] = useState<string | null>(null);
  const [ordersBackupExportLoading, setOrdersBackupExportLoading] = useState(false);
  const [ordersBackupImportLoading, setOrdersBackupImportLoading] = useState(false);
  const [ordersBackupImportError, setOrdersBackupImportError] = useState<string | null>(null);
  const [ordersBackupImportSuccess, setOrdersBackupImportSuccess] = useState<string | null>(null);
  const [ordersBackupImportFile, setOrdersBackupImportFile] = useState<File | null>(null);

  // Admin-only audit log state (dev first; can be enabled for prod later)
  const [auditLogStartDate, setAuditLogStartDate] = useState(() => getDateNDaysAgo(0));
  const [auditLogEndDate, setAuditLogEndDate] = useState(() => formatInputDate(new Date()));
  const [auditLogEntries, setAuditLogEntries] = useState<AuditLogEntry[] | null>(null);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [auditLogError, setAuditLogError] = useState<string | null>(null);
  const [auditOrderFilter, setAuditOrderFilter] = useState('');
  const [auditViewMode, setAuditViewMode] = useState<'timeline' | 'table'>('timeline');

  const filteredAuditEntries = useMemo(() => {
    const entries = auditLogEntries ?? [];
    const q = auditOrderFilter.trim();
    if (!q) return entries;
    return entries.filter((entry) => {
      const orderId = getAuditOrderId(entry);
      if (orderId && orderId.toLowerCase().includes(q.toLowerCase())) return true;
      if (entry.entityId && String(entry.entityId).toLowerCase().includes(q.toLowerCase())) return true;
      if (entry.action.toLowerCase().includes(q.toLowerCase())) return true;
      return false;
    });
  }, [auditLogEntries, auditOrderFilter]);

  const auditOrderTimelineGroups = useMemo(() => {
    const map = new Map<string, AuditLogEntry[]>();
    filteredAuditEntries.forEach((entry) => {
      const orderId = getAuditOrderId(entry);
      if (!orderId) return;
      const list = map.get(orderId) ?? [];
      list.push(entry);
      map.set(orderId, list);
    });
    return Array.from(map.entries())
      .map(([orderId, entries]) => ({
        orderId,
        entries: entries.slice().sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0)),
        lastTs: Math.max(...entries.map((e) => e.timestamp ?? 0)),
      }))
      .sort((a, b) => b.lastTs - a.lastTs);
  }, [filteredAuditEntries]);

  // Daily expenses (Admin/Manager) - GST/tax reconciliation
  const [expenseStartDate, setExpenseStartDate] = useState(() => getDateNDaysAgo(30));
  const [expenseEndDate, setExpenseEndDate] = useState(() => formatInputDate(new Date()));
  const [expenseList, setExpenseList] = useState<DailyExpense[]>([]);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [expenseError, setExpenseError] = useState<string | null>(null);
  const [expenseForm, setExpenseForm] = useState({
    date: formatInputDate(new Date()),
    vendorName: '',
    description: '',
    amount: '',
    category: '',
    receiptNote: '',
  });
  const [expenseReceiptFile, setExpenseReceiptFile] = useState<File | null>(null);
  const [expenseSaving, setExpenseSaving] = useState(false);

  // Helper: use payment timestamp (when sale actually occurred) for date-based reporting
  const getOrderSaleTimestamp = useCallback((order: Order): number => {
    if (order.payments?.length) {
      return Math.max(...order.payments.map((p) => p.timestamp));
    }
    return order.timestamp;
  }, []);

  // Helper: get sale date key (YYYY-MM-DD) in business timezone for grouping by day
  const businessTimeZone = integrationSettings?.businessHours?.timezone || 'Pacific/Auckland';
  const getOrderSaleDateKey = useCallback(
    (order: Order): string => {
      const saleTs = getOrderSaleTimestamp(order);
      return new Date(saleTs).toLocaleDateString('en-CA', { timeZone: businessTimeZone });
    },
    [getOrderSaleTimestamp, businessTimeZone],
  );

  const manualRevenueField = useSoftKeyboardField({
    id: 'manual-sales-revenue',
    type: 'numeric',
    label: 'Total revenue',
    value: manualSalesForm.totalRevenue,
    onChange: (v) => setManualSalesForm((f) => ({ ...f, totalRevenue: v })),
    preferNativeKeyboard: true,
  });
  const manualOrdersField = useSoftKeyboardField({
    id: 'manual-sales-orders',
    type: 'numeric',
    label: 'Total orders',
    value: manualSalesForm.totalOrders,
    onChange: (v) => setManualSalesForm((f) => ({ ...f, totalOrders: v })),
    preferNativeKeyboard: true,
  });
  const manualNoteField = useSoftKeyboardField({
    id: 'manual-sales-note',
    type: 'alphabetic',
    label: 'Note',
    value: manualSalesForm.note,
    onChange: (v) => setManualSalesForm((f) => ({ ...f, note: v })),
    preferNativeKeyboard: true,
  });

  useEffect(() => {
    setManualOverrideLoading(true);
    manualSalesOverridesService.get(reportSummaryDate).then((o) => {
      setManualOverride(o);
      setManualOverrideLoading(false);
    }).catch(() => {
      setManualOverride(null);
      setManualOverrideLoading(false);
    });
  }, [reportSummaryDate]);

  const handleExportOrdersBackup = useCallback(async () => {
    setOrdersBackupExportLoading(true);
    try {
      const { start, end } = getDateRangeHelper(reportSummaryDate, reportSummaryDate);
      const orders = await ordersService.getByDateRange(start, end);
      // Export all non-deleted orders for the day (paid, unpaid, and cancelled/refunded)
      // so backups can be used to fully reconstruct both total and pending revenue,
      // and to reconcile voids/cancellations historically.
      const exportOrders = orders.filter((o) => !o.isDeleted);
      const blob = new Blob([JSON.stringify(exportOrders, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders-backup-${reportSummaryDate}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert('Failed to export orders backup: ' + (err?.message || 'Unknown error'));
    } finally {
      setOrdersBackupExportLoading(false);
    }
  }, [reportSummaryDate]);

  const parseCSVLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') inQuotes = !inQuotes;
      else if (c === ',' && !inQuotes) { out.push(cur.trim()); cur = ''; }
      else cur += c;
    }
    out.push(cur.trim());
    return out;
  };

  const parseOrdersBackupCSV = useCallback((text: string): { date: string; totalRevenue: number; totalOrders: number } | null => {
    const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return null;
    const headers = parseCSVLine(lines[0]).map((h) => h.replace(/^"|"$/g, ''));
    const totalIdx = headers.findIndex((h) => /^total$/i.test(h.trim()));
    const dateIdx = headers.findIndex((h) => /^date$/i.test(h.trim()) && !/date\s*&\s*time/i.test(h));
    if (totalIdx < 0) return null;
    let sum = 0;
    let date = '';
    for (let i = 1; i < lines.length; i++) {
      const vals = parseCSVLine(lines[i]).map((v) => v.replace(/^"|"$/g, ''));
      const totalStr = vals[totalIdx];
      const num = parseFloat((totalStr || '').replace(/[$\s]/g, '').replace(',', ''));
      if (!isNaN(num)) sum += num;
      if (dateIdx >= 0 && dateIdx < vals.length && !date && vals[dateIdx]) date = vals[dateIdx];
    }
    if (date && !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const d = new Date(date);
      date = isNaN(d.getTime()) ? '' : formatInputDate(d);
    }
    return { date: date || reportSummaryDate, totalRevenue: sum, totalOrders: Math.max(0, lines.length - 1) };
  }, [reportSummaryDate]);

  const handleImportOrdersBackup = useCallback(async () => {
    if (!ordersBackupImportFile) {
      setOrdersBackupImportError('Please select a file first.');
      return;
    }
    setOrdersBackupImportLoading(true);
    setOrdersBackupImportError(null);
    setOrdersBackupImportSuccess(null);
    try {
      const text = await ordersBackupImportFile.text();
      const isCsv = ordersBackupImportFile.name.toLowerCase().endsWith('.csv');

      if (isCsv) {
        const parsed = parseOrdersBackupCSV(text);
        if (!parsed || parsed.totalOrders === 0) {
          setOrdersBackupImportError('CSV has no valid orders. Ensure it is from the close register backup (with Total and Date columns).');
          return;
        }
        const override: ManualSalesOverride = {
          date: parsed.date,
          totalRevenue: parsed.totalRevenue,
          totalOrders: parsed.totalOrders,
          note: 'Imported from orders backup CSV',
          updatedAt: Date.now(),
        };
        await manualSalesOverridesService.set(override);
        setManualOverride(override);
        setReportSummaryDate(parsed.date);
        setOrdersBackupImportSuccess(`Imported CSV: ${parsed.totalOrders} order(s), $${parsed.totalRevenue.toFixed(2)} total. Applied as manual sales for ${parsed.date}.`);
        setOrdersBackupImportFile(null);
      } else {
        const parsed = JSON.parse(text);
        const orders = Array.isArray(parsed) ? parsed : [parsed];
        if (orders.length === 0) {
          setOrdersBackupImportError('File contains no orders.');
          return;
        }
        let imported = 0;
        let skipped = 0;
        for (const raw of orders) {
          const id = raw?.id;
          if (!id || typeof id !== 'string') {
            skipped++;
            continue;
          }
          const { id: _, ...orderData } = raw;
          try {
            const items = Array.isArray((orderData as Order).items) ? (orderData as Order).items : [];
            await createWithId<Order>('orders', id, {
              ...(orderData as Omit<Order, 'id'>),
              items: sanitizeOrderItemsForFirestore(items),
            });
            imported++;
          } catch {
            skipped++;
          }
        }
        setOrdersBackupImportSuccess(`Imported ${imported} order(s)${skipped > 0 ? `, skipped ${skipped}` : ''}.`);
        setOrdersBackupImportFile(null);
      }
    } catch (err: any) {
      setOrdersBackupImportError(err?.message || 'Failed to parse or import file. Use orders backup CSV from close register, or JSON from Export orders backup.');
    } finally {
      setOrdersBackupImportLoading(false);
    }
  }, [ordersBackupImportFile, parseOrdersBackupCSV]);

  const canImportOrdersBackup = currentUser?.isAdmin || currentUser?.permissions?.has('manage_settings');

  const { start: summaryDateStart, end: summaryDateEnd } = useMemo(() => {
    return getDateRangeHelper(reportSummaryDate, reportSummaryDate);
  }, [reportSummaryDate]);

  const ordersBySaleDate = useMemo(() => {
    const grouped = new Map<string, Order[]>();
    ordersForReports.forEach((order) => {
      // Seed/demo fixtures (ORD-1..4, ORD-LIVE-*, ORD-HIST-*) must never count in reports.
      if (isSampleOrDemoOrderId(String(order.id))) return;
      const dateKey = getOrderSaleDateKey(order);
      const list = grouped.get(dateKey);
      if (list) list.push(order);
      else grouped.set(dateKey, [order]);
    });
    return grouped;
  }, [ordersForReports, getOrderSaleDateKey]);

  const ordersForSummaryDate = useMemo(() => {
    return ordersBySaleDate.get(reportSummaryDate) ?? [];
  }, [ordersBySaleDate, reportSummaryDate]);

  const loadAuditLog = useCallback(async () => {
    if (!isAdmin) return;
    ensureVpsAuditFeedRegistered();
    setAuditLogLoading(true);
    setAuditLogError(null);
    try {
      const { start, end } = getDateRangeHelper(auditLogStartDate, auditLogEndDate);
      const entries = await auditLogsService.getByDateRange(start, end);
      setAuditLogEntries(entries);
    } catch (error) {
      console.error('Failed to load audit log:', error);
      setAuditLogError('Failed to load audit log. Please try again.');
    } finally {
      setAuditLogLoading(false);
    }
  }, [isAdmin, auditLogStartDate, auditLogEndDate]);

  const loadExpenses = useCallback(async () => {
    if (!isAdmin) return;
    setExpenseLoading(true);
    setExpenseError(null);
    try {
      const list = await dailyExpensesService.getByDateRange(expenseStartDate, expenseEndDate);
      setExpenseList(list);
    } catch (error) {
      console.error('Failed to load expenses:', error);
      setExpenseError('Failed to load expenses. Please try again.');
    } finally {
      setExpenseLoading(false);
    }
  }, [isAdmin, expenseStartDate, expenseEndDate]);

  const effectiveOrdersForSummaryDate = useMemo(() => {
    return ordersForSummaryDate.filter((o) => {
      const hasPayment =
        Array.isArray(o.payments) && o.payments.some((p) => typeof p.amount === 'number' && p.amount > 0);
      const isPaidLike = o.paymentStatus === 'paid' || hasPayment;
      return (
        isPaidLike &&
        o.status !== 'cancelled' &&
        o.status !== 'refunded' &&
        o.status !== 'comped' &&
        !o.isComped
      );
    });
  }, [ordersForSummaryDate]);

  const computedRevenue = useMemo(() => {
    return effectiveOrdersForSummaryDate.reduce((sum, order) => {
      // Use the same net order total used on receipts and invoice reports
      return sum + getOrderTotalFromItems(order);
    }, 0);
  }, [effectiveOrdersForSummaryDate]);

  const useVpsSummaryForHeadline =
    isVpsApiEnabled() && vpsDailySummary != null && manualOverride == null;
  const totalRevenue = manualOverride != null
    ? manualOverride.totalRevenue
    : useVpsSummaryForHeadline
      ? (vpsDailySummary.totalRevenue ?? 0)
      : computedRevenue;
  const totalOrders = manualOverride != null
    ? manualOverride.totalOrders
    : useVpsSummaryForHeadline
      ? (vpsDailySummary.totalOrders ?? 0)
      : effectiveOrdersForSummaryDate.length;
  const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  const itemSales = useMemo(() => {
    const map = new Map<string, { quantity: number; name: string }>();
    effectiveOrdersForSummaryDate.forEach((order) => {
      order.items.forEach((item) => {
        if (item.removed) return;
        const existing = map.get(item.name) || { quantity: 0, name: item.name };
        map.set(item.name, { ...existing, quantity: existing.quantity + item.quantity });
      });
    });
    return map;
  }, [effectiveOrdersForSummaryDate]);

  const topSellingItems = useMemo(
    () => Array.from(itemSales.values()).sort((a, b) => b.quantity - a.quantity).slice(0, 5),
    [itemSales]
  );

  // Simple category performance for the summary date (by gross sales)
  const categoryPerformanceForSummaryDate = useMemo(() => {
    const totals = new Map<string, { name: string; grossSales: number; itemsSold: number }>();
    const categoryIdToName = new Map(categories.map((c) => [String(c.id), c.name]));

    effectiveOrdersForSummaryDate.forEach((order) => {
      order.items.forEach((item) => {
        if (item.removed) return;
        // Skip discount/negative lines
        const price = Number(item.price ?? 0);
        if (price < 0 || String(item.id).includes('combo-discount')) return;

        const rawCatId = item.categoryId;
        const catKey: string = rawCatId != null && rawCatId !== '' ? String(rawCatId) : 'uncategorised';
        const name = catKey !== 'uncategorised' ? (categoryIdToName.get(catKey) ?? 'Uncategorised') : 'Uncategorised';

        if (!totals.has(catKey)) {
          totals.set(catKey, { name, grossSales: 0, itemsSold: 0 });
        }
        const entry = totals.get(catKey)!;
        entry.grossSales += price * item.quantity;
        entry.itemsSold += item.quantity;
      });
    });

    const rows = Array.from(totals.values());
    const totalGross = rows.reduce((sum, r) => sum + r.grossSales, 0);
    return rows
      .map((r) => ({
        ...r,
        share: totalGross > 0 ? (r.grossSales / totalGross) * 100 : 0,
      }))
      .sort((a, b) => b.grossSales - a.grossSales)
      .slice(0, 4);
  }, [categories, effectiveOrdersForSummaryDate]);

  const pendingOrdersForSummaryDate = useMemo(() => {
    return ordersForSummaryDate.filter(isOpenUnpaidOrder);
  }, [ordersForSummaryDate]);

  const totalPendingOrderRevenue = useMemo(() => {
    return pendingOrdersForSummaryDate.reduce((sum, order) => sum + getOrderTotalFromItems(order), 0);
  }, [pendingOrdersForSummaryDate]);

  // Opening float for the summary date (sum of opening floats for sessions opened that day)
  const { openingFloatTotal, openingFloatSessionCount } = useMemo(() => {
    const sessionsForDay = registerSessions.filter(
      (session) => session.startTime >= summaryDateStart && session.startTime <= summaryDateEnd
    );
    const total = sessionsForDay.reduce((sum, session) => sum + (session.openingFloat ?? 0), 0);
    return { openingFloatTotal: total, openingFloatSessionCount: sessionsForDay.length };
  }, [registerSessions, summaryDateStart, summaryDateEnd]);

  useEffect(() => {
    if (!isReportsHomeSection && !showOpenRegisterModal) return;
    void refreshActiveRegisterSessions();
  }, [isReportsHomeSection, showOpenRegisterModal, refreshActiveRegisterSessions]);

  const openRegisterSummaries = useMemo(() => {
    const activeSessions = collectActiveRegisterSessions(
      currentRegisterSession,
      activeRegisterSessions,
      hydratedActiveRegisterSessions,
      sessionsForReports,
    );
    return activeSessions
      .filter((session) => Boolean(session.id))
      .map((session) => {
      const normalized = normalizeRegisterSession(session);
      const startMs = registerSessionStartMs(normalized);
      const cashierName =
        normalized.cashierName?.trim() ||
        staffMembers.find((s) => s.id === normalized.cashierId)?.name ||
        'Unknown cashier';
      const terminal =
        normalized.terminalName?.trim() ||
        normalized.terminalId ||
        (normalized.id ? `Session ${normalized.id.slice(-6)}` : 'Open register');
      return {
        id: normalized.id,
        terminal,
        cashier: cashierName,
        openedAt: startMs,
        openingFloat: normalized.openingFloat,
        lastReminderAt: toMillis(normalized.lastReminderAt) || undefined,
        hoursOpen: formatHoursOpen(startMs),
      };
    });
  }, [
    currentRegisterSession,
    activeRegisterSessions,
    hydratedActiveRegisterSessions,
    sessionsForReports,
    staffMembers,
  ]);
  const openRegisterCount = openRegisterSummaries.length;
  const longestOpenDuration = openRegisterSummaries.reduce((max, session) => {
    const hours = session.hoursOpen === '—' ? 0 : parseFloat(session.hoursOpen);
    return Number.isFinite(hours) && hours > max ? hours : max;
  }, 0);
  const lowStockItems = useMemo(() => {
    return inventoryItems
      .map((item) => {
        const threshold = item.lowStockThreshold ?? 0;
        const bulkRemaining = item.isBulkPurchase && item.bulkPurchases
          ? item.bulkPurchases.reduce((sum, bulk) => sum + (bulk.remainingQuantity || 0), 0)
          : 0;
        const availableStock = item.isBulkPurchase ? bulkRemaining : item.stock;
        return {
          ...item,
          availableStock,
          variance: availableStock - threshold,
          threshold,
        };
      })
      .filter((item) => item.threshold > 0 && item.availableStock <= item.threshold)
      .map((item) => {
        const severity =
          item.availableStock <= 0 ? 'critical' :
          item.availableStock <= item.threshold / 2 ? 'high' :
          'warning';
        return {
          ...item,
          severity,
        };
      })
      .sort((a, b) => a.variance - b.variance)
      .slice(0, 10);
  }, [inventoryItems]);

  // Detailed reports state
  // Default detailed sales / daily sales range to last 2 days to keep lists small
  const [salesStartDate, setSalesStartDate] = useState(getDateNDaysAgo(1));
  const [salesEndDate, setSalesEndDate] = useState(formatInputDate(new Date()));

  const eventStallRangeMs = useMemo(() => getDateRangeHelper(salesStartDate, salesEndDate), [salesStartDate, salesEndDate]);
  const eventProfiles = integrationSettings?.eventStall?.events ?? [];
  const activeEventId = integrationSettings?.eventStall?.activeEventId;
  const [eventReportFilter, setEventReportFilter] = useState<string>('all');
  const [eventCostDraftById, setEventCostDraftById] = useState<Record<string, string>>({});
  const [eventCostSavingById, setEventCostSavingById] = useState<Record<string, boolean>>({});
  const canManageEventCosts = Boolean(currentUser?.isAdmin || currentUser?.permissions?.has('manage_settings'));

  const eventStallMenuItemIds = useMemo(() => collectEventStallMenuItemIds(integrationSettings), [integrationSettings]);

  const eventStallUnitCostByItemId = useMemo(
    () => buildEventStallUnitCostMap(integrationSettings ?? undefined),
    [integrationSettings],
  );

  const eventSnapshotById = useMemo(() => {
    const m = new Map<string, { name: string; costByItemId: Map<number, number>; nameByItemId: Map<number, string> }>();
    for (const evt of eventProfiles) {
      const costByItemId = new Map<number, number>();
      const nameByItemId = new Map<number, string>();
      for (const row of evt.menuSnapshot ?? []) {
        const id = Number(row.id);
        if (!Number.isFinite(id)) continue;
        const nm = String(row.name ?? '').trim();
        if (nm) nameByItemId.set(id, nm);
        if (row.unitCost != null && Number.isFinite(Number(row.unitCost)) && Number(row.unitCost) >= 0) {
          costByItemId.set(id, Math.round(Number(row.unitCost) * 100) / 100);
        }
      }
      m.set(evt.id, { name: evt.name, costByItemId, nameByItemId });
    }
    return m;
  }, [eventProfiles]);

  /**
   * Future: per-event fidelity — stamp orders with eventMenuSnapshotId and resolve unitCost from that
   * snapshot at sale time instead of integrationSettings.eventStall.eventMenuItems (current menu).
   */
  const eventStallSalesReport = useMemo(() => {
    const { start, end } = eventStallRangeMs;
    // Must use ordersForReports (App keeps a global orders listener including on report routes).
    const list = ordersForReports.filter((o) => {
      if (!orderIncludedInEventStallSalesReport(o, { start, end }, eventStallMenuItemIds)) return false;
      if (eventReportFilter === 'all') return true;
      if (eventReportFilter === 'active') return (o.eventId ?? 'legacy-unassigned') === (activeEventId ?? '');
      if (eventReportFilter === 'legacy-unassigned') return !o.eventId;
      return (o.eventId ?? 'legacy-unassigned') === eventReportFilter;
    });
    const eventMenuItems = integrationSettings?.eventStall?.eventMenuItems ?? [];
    const settingsNameByMenuId = new Map<number, string>();
    for (const row of eventMenuItems) {
      const id = Number(row.id);
      const nm = String(row.name ?? '').trim();
      if (Number.isFinite(id) && nm) settingsNameByMenuId.set(id, nm);
    }

    let gross = 0;
    const byCategory = new Map<string, { revenue: number; qty: number }>();
    /** Rename-safe: aggregate by numeric menu line id; legacy fallback key for non-numeric ids */
    const byProduct = new Map<
      string,
      { menuItemId: number | null; fallbackName: string; revenue: number; qty: number; cost: number; source: 'order' | 'snapshot' | 'settings' | 'missing' }
    >();

    for (const o of list) {
      gross += Number(o.totalAmount) || 0;
      for (const line of o.items ?? []) {
        if (line.removed) continue;
        const qty = Number(line.quantity) || 0;
        const lineRev = (Number(line.price) || 0) * qty;
        const numericId = Number(line.id);
        const orderSnapshotCost =
          Number.isFinite(numericId) && o.eventCostSnapshot ? Number(o.eventCostSnapshot[String(numericId)]) : undefined;
        const eventSnapshot = o.eventMenuSnapshotId ? eventSnapshotById.get(o.eventMenuSnapshotId) : undefined;
        const snapshotCost = Number.isFinite(numericId) ? eventSnapshot?.costByItemId.get(numericId) : undefined;
        const settingsCost = Number.isFinite(numericId) ? eventStallUnitCostByItemId.get(numericId) : undefined;
        const unitCostLookup = Number.isFinite(Number(orderSnapshotCost))
          ? Number(orderSnapshotCost)
          : snapshotCost != null
            ? snapshotCost
            : settingsCost;
        const costSource: 'order' | 'snapshot' | 'settings' | 'missing' = Number.isFinite(Number(orderSnapshotCost))
          ? 'order'
          : snapshotCost != null
            ? 'snapshot'
            : settingsCost != null
              ? 'settings'
              : 'missing';
        const lineCost = unitCostLookup != null ? unitCostLookup * qty : 0;
        const cat =
          typeof line.categoryId === 'string'
            ? line.categoryId
            : line.categoryId != null
              ? String(line.categoryId)
              : 'Uncategorized';
        const curCat = byCategory.get(cat) ?? { revenue: 0, qty: 0 };
        curCat.revenue += lineRev;
        curCat.qty += qty;
        byCategory.set(cat, curCat);

        const lineLabel = String(line.name ?? '').trim();
        const pk = Number.isFinite(numericId)
          ? `id:${numericId}`
          : `legacy:${String(line.id)}:${lineLabel}`;
        const curP =
          byProduct.get(pk) ?? {
            menuItemId: Number.isFinite(numericId) ? numericId : null,
            fallbackName: lineLabel || `Item ${String(line.id)}`,
            revenue: 0,
            qty: 0,
            cost: 0,
            source: costSource,
          };
        if (!curP.fallbackName && lineLabel) curP.fallbackName = lineLabel;
        curP.revenue += lineRev;
        curP.qty += qty;
        curP.cost += lineCost;
        if (curP.source === 'missing' && costSource !== 'missing') curP.source = costSource;
        byProduct.set(pk, curP);
      }
    }

    const gst = gross * (15 / 115);
    const net = gross - gst;
    const categoryRows = [...byCategory.entries()]
      .map(([category, v]) => ({ category, ...v }))
      .sort((a, b) => b.revenue - a.revenue);

    const productRows = [...byProduct.values()]
      .map((v) => {
        const menuItemId = v.menuItemId;
        const settingsName =
          menuItemId != null && Number.isFinite(menuItemId) ? settingsNameByMenuId.get(menuItemId) : undefined;
        const name = (settingsName && settingsName.trim()) || v.fallbackName || (menuItemId != null ? `Item ${menuItemId}` : 'Item');
        const unitCost = v.source !== 'missing' && v.qty > 0 ? v.cost / v.qty : null;
        const profit = v.source !== 'missing' ? v.revenue - v.cost : null;
        return {
          menuItemId,
          name,
          qty: v.qty,
          revenue: v.revenue,
          cost: v.cost,
          unitCost,
          profit,
          costSource: v.source,
        };
      })
      .sort((a, b) => b.revenue - a.revenue);

    const productCostTotal = productRows.reduce((s, r) => s + r.cost, 0);
    const productRevenueTotal = productRows.reduce((s, r) => s + r.revenue, 0);
    const productProfitTotal = productRows.reduce((s, r) => s + (r.profit != null ? r.profit : 0), 0);
    const productMarginPercent =
      productRevenueTotal > 0 ? (productProfitTotal / productRevenueTotal) * 100 : null;

    return {
      orderCount: list.length,
      gross,
      gst,
      net,
      categoryRows,
      productRows,
      productCostTotal,
      productRevenueTotal,
      productProfitTotal,
      productMarginPercent,
      list,
    };
  }, [
    ordersForReports,
    eventStallRangeMs,
    eventStallMenuItemIds,
    eventStallUnitCostByItemId,
    integrationSettings,
    eventReportFilter,
    activeEventId,
    eventSnapshotById,
  ]);

  const saveFallbackEventItemCost = useCallback(
    async (menuItemId: number, rawValue: string) => {
      if (!integrationSettings || !onSaveIntegrationSettings) return;
      const parsed = Math.round(Number(rawValue) * 100) / 100;
      if (!Number.isFinite(parsed) || parsed < 0) {
        alert('Please enter a valid non-negative cost.');
        return;
      }
      setEventCostSavingById((prev) => ({ ...prev, [String(menuItemId)]: true }));
      try {
        const flat = integrationSettings.eventStall?.eventMenuItems ?? [];
        const hadInFlat = flat.some((row) => Number(row.id) === menuItemId);
        let nextFlat = flat.map((row) =>
          Number(row.id) === menuItemId ? { ...row, unitCost: parsed } : row
        );
        if (!hadInFlat) {
          const mi = menuItems.find((m) => m.id === menuItemId);
          const catKey = mi?.categoryId != null ? String(mi.categoryId) : '';
          const catName =
            categories.find((c) => String(c.id) === catKey)?.name?.trim() || 'Uncategorized';
          nextFlat = [
            ...nextFlat,
            {
              id: menuItemId,
              name: mi?.name?.trim() || `Item ${menuItemId}`,
              price: Number(mi?.price) || 0,
              categoryName: catName,
              unitCost: parsed,
            },
          ];
        }

        const activeEvtId = integrationSettings.eventStall?.activeEventId;
        const nextEvents = (integrationSettings.eventStall?.events ?? []).map((evt) => {
          const ms = evt.menuSnapshot ?? [];
          const hadInSnap = ms.some((row) => Number(row.id) === menuItemId);
          let nextSnap = ms.map((row) =>
            Number(row.id) === menuItemId ? { ...row, unitCost: parsed } : row
          );
          if (!hadInSnap && evt.id === activeEvtId) {
            const mi = menuItems.find((m) => m.id === menuItemId);
            const catKey = mi?.categoryId != null ? String(mi.categoryId) : '';
            const catName =
              categories.find((c) => String(c.id) === catKey)?.name?.trim() || 'Uncategorized';
            nextSnap = [
              ...nextSnap,
              {
                id: menuItemId,
                name: mi?.name?.trim() || `Item ${menuItemId}`,
                price: Number(mi?.price) || 0,
                categoryName: catName,
                unitCost: parsed,
              },
            ];
          }
          return { ...evt, menuSnapshot: nextSnap };
        });

        const nextSettings: IntegrationSettings = {
          ...integrationSettings,
          eventStall: {
            ...integrationSettings.eventStall,
            eventMenuItems: nextFlat,
            events: nextEvents,
          },
        };
        await onSaveIntegrationSettings(nextSettings);
        setEventCostDraftById((prev) => {
          const next = { ...prev };
          delete next[String(menuItemId)];
          return next;
        });
      } catch (err) {
        console.error('Failed to save event item cost from report', err);
        alert('Could not save cost. Please try again from Settings.');
      } finally {
        setEventCostSavingById((prev) => ({ ...prev, [String(menuItemId)]: false }));
      }
    },
    [integrationSettings, onSaveIntegrationSettings, menuItems, categories]
  );

  /** Same paid stall orders as tables; hours bucketed by payment time (fallback order timestamp) — 11a–10p business TZ */
  const eventStallCategoryByHourChartData = useMemo(() => {
    const { start, end } = eventStallRangeMs;
    const timezone = integrationSettings?.businessHours?.timezone;
    const openingHour = 11;
    const closingHour = 22;
    const bucketCount = closingHour - openingHour + 1;

    const list = ordersForReports.filter((o) => {
      if (!orderIncludedInEventStallSalesReport(o, { start, end }, eventStallMenuItemIds)) return false;
      if (eventReportFilter === 'all') return true;
      if (eventReportFilter === 'active') return (o.eventId ?? 'legacy-unassigned') === (activeEventId ?? '');
      if (eventReportFilter === 'legacy-unassigned') return !o.eventId;
      return (o.eventId ?? 'legacy-unassigned') === eventReportFilter;
    });

    const getSaleTs = (order: Order): number => {
      if (order.payments?.length) return Math.max(...order.payments.map((p) => p.timestamp));
      return order.timestamp;
    };

    const getHourInTimezone = (timestamp: number): number => {
      const d = new Date(timestamp);
      if (timezone) {
        const hr = d.toLocaleString('en-CA', { timeZone: timezone, hour: 'numeric', hour12: false });
        return parseInt(hr, 10);
      }
      return d.getHours();
    };

    const buckets = Array.from({ length: bucketCount }, (_, i) => ({
      hour: openingHour + i,
      byCategory: {} as Record<string, number>,
      total: 0,
    }));

    for (const o of list) {
      const saleTs = getSaleTs(o);
      const hour = getHourInTimezone(saleTs);
      const bucket = buckets.find((h) => h.hour === hour);
      if (!bucket) continue;

      for (const line of o.items ?? []) {
        if (line.removed) continue;
        const qty = Number(line.quantity) || 0;
        const lineRev = (Number(line.price) || 0) * qty;
        const cat =
          typeof line.categoryId === 'string'
            ? line.categoryId
            : line.categoryId != null
              ? String(line.categoryId)
              : 'Uncategorized';
        bucket.byCategory[cat] = (bucket.byCategory[cat] ?? 0) + lineRev;
        bucket.total += lineRev;
      }
    }

    return buckets.map((b) => ({
      label: `${b.hour % 12 || 12}${b.hour < 12 ? 'a' : 'p'}`,
      total: b.total,
      byCategory: b.byCategory,
    }));
  }, [
    ordersForReports,
    eventStallRangeMs,
    eventStallMenuItemIds,
    integrationSettings?.businessHours?.timezone,
    eventReportFilter,
    activeEventId,
  ]);
  const [salesRows, setSalesRows] = useState<DailySalesRow[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState<string | null>(null);

  const [registerStartDate, setRegisterStartDate] = useState(getDateNDaysAgo(6));
  const [registerEndDate, setRegisterEndDate] = useState(formatInputDate(new Date()));
  const [registerLogs, setRegisterLogs] = useState<RegisterLogRow[]>([]);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [selectedTerminal, setSelectedTerminal] = useState<string>('all');
  const [salesInsights, setSalesInsights] = useState<SalesInsights | null>(null);
  const [couponDashboard, setCouponDashboard] = useState<CouponDashboardAnalytics | null>(null);
  const couponUsageByStaffSorted = useMemo(() => {
    if (!couponDashboard?.usageByStaff?.length) return [];
    return [...couponDashboard.usageByStaff].sort((a, b) => b.redemptions - a.redemptions);
  }, [couponDashboard]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [salesByInvoice, setSalesByInvoice] = useState<SalesByInvoiceRow[]>([]);
  const [salesByInvoiceLoading, setSalesByInvoiceLoading] = useState(false);
  const [salesByInvoiceLoadingMore, setSalesByInvoiceLoadingMore] = useState(false);
  const [salesByInvoiceError, setSalesByInvoiceError] = useState<string | null>(null);
  const [salesByInvoiceCursor, setSalesByInvoiceCursor] = useState<{ timestamp: number; id: string } | null>(null);
  /** Fetched `start|end` for the data in `salesByInvoice`; `null` after range change or before a load. */
  const [salesByInvoiceLastLoadedKey, setSalesByInvoiceLastLoadedKey] = useState<string | null>(null);
  const [salesByInvoiceStartDate, setSalesByInvoiceStartDate] = useState('');
  const [salesByInvoiceEndDate, setSalesByInvoiceEndDate] = useState('');
  const salesByInvoiceTotals = useMemo(
    () =>
      salesByInvoice.reduce(
        (acc, row) => {
          acc.total += row.totalAmount;
          acc.cash += row.cashAmount;
          acc.card += row.cardAmount;
          acc.other += row.otherAmount;
          return acc;
        },
        { total: 0, cash: 0, card: 0, other: 0 },
      ),
    [salesByInvoice],
  );
  // Sales by invoice rows for the current summary date (used for reconciliation/debug)
  const salesByInvoiceForSummaryDate = useMemo(
    () => salesByInvoice.filter((r) => r.date === reportSummaryDate),
    [salesByInvoice, reportSummaryDate],
  );
  const salesByInvoiceLoadedForCurrentRange = useMemo(
    () =>
      Boolean(salesByInvoiceStartDate && salesByInvoiceEndDate) &&
      salesByInvoiceLastLoadedKey === `${salesByInvoiceStartDate}|${salesByInvoiceEndDate}`,
    [salesByInvoiceStartDate, salesByInvoiceEndDate, salesByInvoiceLastLoadedKey],
  );

  const invoiceTotalForSummaryDate = useMemo(() => {
    if (!salesByInvoiceLoadedForCurrentRange) return null;
    if (!salesByInvoiceStartDate || !salesByInvoiceEndDate) return null;
    if (reportSummaryDate < salesByInvoiceStartDate || reportSummaryDate > salesByInvoiceEndDate) {
      return null;
    }
    return salesByInvoiceForSummaryDate.reduce((sum, r) => sum + r.totalAmount, 0);
  }, [
    reportSummaryDate,
    salesByInvoiceStartDate,
    salesByInvoiceEndDate,
    salesByInvoiceForSummaryDate,
    salesByInvoiceLoadedForCurrentRange,
  ]);
  // Revenue reconciliation helpers: compare summary revenue vs sales-by-invoice vs overrides.
  const summaryOrderIdsForDate = useMemo(
    () => new Set(effectiveOrdersForSummaryDate.map((o) => o.id)),
    [effectiveOrdersForSummaryDate],
  );
  const invoiceOrderIdsForSummaryDate = useMemo(
    () => new Set(salesByInvoiceForSummaryDate.map((r) => r.orderId)),
    [salesByInvoiceForSummaryDate],
  );
  const extraOrdersInSummaryNotInInvoices = useMemo(
    () => effectiveOrdersForSummaryDate.filter((o) => !invoiceOrderIdsForSummaryDate.has(o.id)),
    [effectiveOrdersForSummaryDate, invoiceOrderIdsForSummaryDate],
  );
  const extraInvoicesNotInSummary = useMemo(
    () => salesByInvoiceForSummaryDate.filter((r) => !summaryOrderIdsForDate.has(r.orderId)),
    [salesByInvoiceForSummaryDate, summaryOrderIdsForDate],
  );
  // Uber/DoorDash subset of Sales by Invoice (same date range) for detailed report
  const salesByInvoiceUberDoordash = useMemo(
    () => salesByInvoice.filter((r) => r.orderType === 'uber-doordash'),
    [salesByInvoice],
  );
  const salesByInvoiceUberDoordashTotals = useMemo(
    () =>
      salesByInvoiceUberDoordash.reduce(
        (acc, row) => {
          acc.total += row.totalAmount;
          acc.cash += row.cashAmount;
          acc.card += row.cardAmount;
          acc.other += row.otherAmount;
          return acc;
        },
        { total: 0, cash: 0, card: 0, other: 0 },
      ),
    [salesByInvoiceUberDoordash],
  );

  // Trends & Forecast: default to current week (Monday → Sunday)
  const [trendsStartDate, setTrendsStartDate] = useState(() => {
    const todayStr = formatInputDate(new Date());
    const weekStartTs = getWeekStartTimestamp(todayStr);
    return formatInputDate(new Date(weekStartTs));
  });
  const [trendsEndDate, setTrendsEndDate] = useState(() => {
    const todayStr = formatInputDate(new Date());
    const weekStartTs = getWeekStartTimestamp(todayStr);
    const weekEndTs = weekStartTs + 6 * 24 * 60 * 60 * 1000;
    return formatInputDate(new Date(weekEndTs));
  });

  const homeOrdersRange = useMemo(() => {
    const rangeStart = reportSummaryDate < trendsStartDate ? reportSummaryDate : trendsStartDate;
    const rangeEnd = reportSummaryDate > trendsEndDate ? reportSummaryDate : trendsEndDate;
    return getDateRangeHelper(rangeStart, rangeEnd);
  }, [reportSummaryDate, trendsStartDate, trendsEndDate]);

  useEffect(() => {
    if (!isMonthlyDetailedSection) return;
    let cancelled = false;
    setMonthlyPayrollLoading(true);
    setMonthlyPayrollError(null);
    payrollPeriodImportsService
      .listRecent(120)
      .then((rows) => {
        if (cancelled) return;
        setMonthlyPayrollImports(rows);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to load payroll imports for monthly salary card:', error);
        setMonthlyPayrollImports([]);
        setMonthlyPayrollError('Could not load payroll imports.');
      })
      .finally(() => {
        if (cancelled) return;
        setMonthlyPayrollLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isMonthlyDetailedSection]);

  useEffect(() => {
    if (!isReportsHomeSection) return;
    let cancelled = false;
    setHomeWindowLoading(true);
    setHomeWindowError(null);
    ordersService
      .getByDateRange(homeOrdersRange.start, homeOrdersRange.end)
      .then((rows) => {
        if (cancelled) return;
        setHomeWindowOrders(rows);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to load report home window orders:', error);
        setHomeWindowOrders([]);
        setHomeWindowError('Failed to load selected date range.');
      })
      .finally(() => {
        if (cancelled) return;
        setHomeWindowLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isReportsHomeSection, homeOrdersRange.start, homeOrdersRange.end]);

  // KDS/POS use the live orders listener; homeWindowOrders is a one-shot fetch. Merge live
  // orders into the window so Pending Order Revenue and charts update when a ticket is sent.
  useEffect(() => {
    if (!isReportsHomeSection) return;
    const { start, end } = homeOrdersRange;
    setHomeWindowOrders((prev) => {
      const map = new Map<string, Order>();
      for (const o of prev) map.set(o.id, o);
      for (const o of orders) {
        if (o.timestamp >= start && o.timestamp <= end) {
          map.set(o.id, o);
        }
      }
      return Array.from(map.values());
    });
  }, [isReportsHomeSection, orders, homeOrdersRange.start, homeOrdersRange.end]);

  const [editRegisterLog, setEditRegisterLog] = useState<RegisterLogRow | null>(null);
  const [editRegisterOpeningFloat, setEditRegisterOpeningFloat] = useState('');
  const [editRegisterClosingFloat, setEditRegisterClosingFloat] = useState('');
  const [editRegisterCashSales, setEditRegisterCashSales] = useState('');
  const [editRegisterCardSales, setEditRegisterCardSales] = useState('');
  const [editRegisterSaving, setEditRegisterSaving] = useState(false);
  const [editRegisterError, setEditRegisterError] = useState<string | null>(null);

  const [monthlyReportRows, setMonthlyReportRows] = useState<MonthlySalesRow[]>([]);
  /** Which `YYYY-MM` `monthlyReportRows` was built for; null after month change or before first load. */
  const [monthlySalesDataMonth, setMonthlySalesDataMonth] = useState<string | null>(null);
  const [monthlyReportMonth, setMonthlyReportMonth] = useState(() => formatInputDate(new Date()).slice(0, 7));
  const [monthlyReportLoading, setMonthlyReportLoading] = useState(false);
  const monthlyLoadRequestIdRef = useRef(0);
  const detailedReportsDataLoadedRef = useRef({ insights: false, registerLogs: false });
  const insightsLoadInFlightRef = useRef(false);
  const [monthlyReportError, setMonthlyReportError] = useState<string | null>(null);
  const [monthlyCompletionFilter, setMonthlyCompletionFilter] = useState<'all' | 'complete' | 'incomplete'>('all');
  const [monthlyExpenseLinesByDate, setMonthlyExpenseLinesByDate] = useState<Record<string, MonthlyReportExpenseLine[]>>({});
  const [gstExpenseModalDate, setGstExpenseModalDate] = useState<string | null>(null);
  const [monthlySmartPayByDate, setMonthlySmartPayByDate] = useState<Record<string, number>>({});
  const [monthlyImportedTxnIds, setMonthlyImportedTxnIds] = useState<Record<string, true>>({});
  const [monthlyImportMessage, setMonthlyImportMessage] = useState<string | null>(null);
  const [monthlyColumnVisibility, setMonthlyColumnVisibility] = useState<Record<MonthlyColumnVisibilityKey, boolean>>(DEFAULT_MONTHLY_COLUMN_VISIBILITY);
  const [isMonthlyColumnVisibilityOpen, setIsMonthlyColumnVisibilityOpen] = useState(false);
  const [monthlyStorageHydrated, setMonthlyStorageHydrated] = useState(false);
  const [monthlySyncStatus, setMonthlySyncStatus] = useState<'synced' | 'syncing' | 'failed'>('synced');
  const [monthlySyncStatusMessage, setMonthlySyncStatusMessage] = useState<string | null>(null);
  const [monthlySyncPermissionBlocked, setMonthlySyncPermissionBlocked] = useState(false);
  const [monthlyPayrollImports, setMonthlyPayrollImports] = useState<PayrollPeriodImport[]>([]);
  const [monthlyPayrollLoading, setMonthlyPayrollLoading] = useState(false);
  const [monthlyPayrollError, setMonthlyPayrollError] = useState<string | null>(null);
  /** Payroll salary shown in card: work-day accrual vs full run in pay-end month (cash-style). */
  const [monthlySalaryPayrollBasis, setMonthlySalaryPayrollBasis] = useState<'accrual' | 'cash'>('accrual');
  /** Sum of Daily Expenses with category Labour/Wages for `monthlyReportMonth` (separate from GST grid). */
  const [monthlyDailyLabourWagesTotal, setMonthlyDailyLabourWagesTotal] = useState<number | null>(null);
  const [monthlyDailyLabourWagesLoading, setMonthlyDailyLabourWagesLoading] = useState(false);
  const [monthlyDateFromFilter, setMonthlyDateFromFilter] = useState('');
  const [monthlyDateToFilter, setMonthlyDateToFilter] = useState('');
  const [monthlyTerminalIdsFilter, setMonthlyTerminalIdsFilter] = useState('');
  const [monthlyDiscountFilter, setMonthlyDiscountFilter] = useState<'all' | 'with-discount' | 'without-discount'>('all');
  const [monthlyTablePage, setMonthlyTablePage] = useState(1);
  const [monthlyTablePageSize, setMonthlyTablePageSize] = useState<10 | 20 | 50 | 100>(10);
  const [flashPnlTimesheetSummary, setFlashPnlTimesheetSummary] = useState<TimesheetLaborSummary | null>(null);
  const [foodCostReport, setFoodCostReport] = useState<TheoreticalVsActualFoodCostReport | null>(null);
  const [foodCostStartDate, setFoodCostStartDate] = useState(getDateNDaysAgo(6));
  const [foodCostEndDate, setFoodCostEndDate] = useState(formatInputDate(new Date()));
  const [foodCostLoading, setFoodCostLoading] = useState(false);

  useEffect(() => {
    if (!isMonthlyDetailedSection || !isAdmin) {
      setMonthlyDailyLabourWagesTotal(null);
      setMonthlyDailyLabourWagesLoading(false);
      return;
    }
    let cancelled = false;
    const from = `${monthlyReportMonth}-01`;
    const to = lastCalendarDayOfMonth(monthlyReportMonth);
    setMonthlyDailyLabourWagesLoading(true);
    dailyExpensesService
      .getByDateRange(from, to)
      .then((list) => {
        if (cancelled) return;
        let t = 0;
        list.forEach((e) => {
          if ((e.category ?? '').trim() !== DAILY_EXPENSE_STAFF_WAGES_CATEGORY) return;
          t += Number(e.amount) || 0;
        });
        setMonthlyDailyLabourWagesTotal(Math.max(0, t));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load daily Labour/Wages totals for monthly salary card:', err);
        setMonthlyDailyLabourWagesTotal(null);
      })
      .finally(() => {
        if (!cancelled) setMonthlyDailyLabourWagesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isMonthlyDetailedSection, isAdmin, monthlyReportMonth]);

  const [activeReportTab, setActiveReportTab] = useState<'detailed' | 'executive' | 'audit' | 'expenses'>('detailed');
  const dashboardBusy = homeWindowLoading || manualOverrideLoading || ordersBackupExportLoading || ordersBackupImportLoading;
  // While the summary-date data sources are still loading, the KPI cards would otherwise
  // flash misleading partial numbers (live POS orders only — e.g. $0.00 revenue but a
  // pending-revenue figure from open tickets). Show placeholders until everything settles.
  const summaryCardsLoading = homeWindowLoading || vpsDailySummaryLoading || manualOverrideLoading;
  const isExecutivePanelActive = detailedSection === 'home' && activeReportTab === 'executive';
  const [dashboardBusyStartedAt, setDashboardBusyStartedAt] = useState<number | null>(null);
  const [dashboardProgressNow, setDashboardProgressNow] = useState(() => Date.now());
  const [dashboardProgressPercent, setDashboardProgressPercent] = useState(0);

  useEffect(() => {
    if (detailedSection !== 'home' && activeReportTab !== 'detailed') {
      setActiveReportTab('detailed');
    }
  }, [detailedSection, activeReportTab]);

  useLayoutEffect(() => {
    if (isReportsHomeSection && isVpsApiEnabled()) {
      ensureVpsReportsFeedRegistered();
    }
  }, [isReportsHomeSection]);

  useEffect(() => {
    if (!isReportsHomeSection || activeReportTab !== 'detailed' || !isVpsApiEnabled()) {
      setVpsDailySummary(null);
      return;
    }
    let cancelled = false;
    setVpsDailySummaryLoading(true);
    reportsDailySummaryService
      .getByDateRange(reportSummaryDate, reportSummaryDate)
      .then((rows) => {
        if (cancelled) return;
        setVpsDailySummary(rows[0] ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('reports_daily VPS summary unavailable; using order aggregation.', err);
        setVpsDailySummary(null);
      })
      .finally(() => {
        if (!cancelled) setVpsDailySummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isReportsHomeSection, activeReportTab, reportSummaryDate]);

  useEffect(() => {
    if (!dashboardBusy) {
      setDashboardBusyStartedAt(null);
      setDashboardProgressPercent(0);
      return;
    }
    setDashboardBusyStartedAt((prev) => {
      if (prev !== null) return prev;
      setDashboardProgressPercent(8);
      return Date.now();
    });
  }, [dashboardBusy]);

  useEffect(() => {
    if (!dashboardBusy) return;
    const timer = window.setInterval(() => setDashboardProgressNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [dashboardBusy]);

  useEffect(() => {
    if (!dashboardBusy || !dashboardBusyStartedAt) return;
    const elapsed = dashboardProgressNow - dashboardBusyStartedAt;
    const projected = Math.min(95, Math.max(8, (elapsed / DASHBOARD_LOADING_EXPECTED_MS) * 100));
    setDashboardProgressPercent((prev) => {
      if (projected < prev) return prev;
      return Math.min(projected, prev + 4);
    });
  }, [dashboardBusy, dashboardBusyStartedAt, dashboardProgressNow]);

  const dashboardElapsedMs = dashboardBusy && dashboardBusyStartedAt ? Math.max(0, dashboardProgressNow - dashboardBusyStartedAt) : 0;
  const dashboardRemainingMs = Math.max(0, DASHBOARD_LOADING_EXPECTED_MS - dashboardElapsedMs);
  const dashboardLoadDelayed = dashboardBusy && dashboardElapsedMs > DASHBOARD_LOADING_EXPECTED_MS;

  const executiveMetrics: ExecutiveDashboardMetrics | null = useMemo(() => {
    if (!isAdmin || !isExecutivePanelActive) return null;
    return getExecutiveDashboardMetrics(
      ordersForReports,
      tables,
      sessionsForReports,
      menuItems,
      sameDayLastWeekOrders,
      staffMap,
      kitchenSLA
    );
  }, [isAdmin, isExecutivePanelActive, ordersForReports, tables, sessionsForReports, menuItems, sameDayLastWeekOrders, staffMap, kitchenSLA]);

  const weekEnding = useMemo(() => getWeekEndingDate(), []);
  const flashPnlReport: FlashPnlReport | null = useMemo(() => {
    if (!isAdmin || !isExecutivePanelActive) return null;
    return getFlashPnlReport(
      weekEnding,
      ordersForReports,
      recipes,
      inventoryItems,
      categories,
      integrationSettings?.flashPnl,
      flashPnlTimesheetSummary
    );
  }, [isAdmin, isExecutivePanelActive, weekEnding, ordersForReports, recipes, inventoryItems, categories, integrationSettings?.flashPnl, flashPnlTimesheetSummary]);

  // Sales by hour for the detailed report: uses the summary date and paid orders only
  // Uses payment timestamp (when sale occurred), not order placement timestamp
  const detailedSalesByHourChartData = useMemo<SalesByHourPoint[]>(() => {
    const timezone = integrationSettings?.businessHours?.timezone;

    const getSaleTimestamp = (order: Order): number => {
      if (order.payments?.length) {
        return Math.max(...order.payments.map((p) => p.timestamp));
      }
      return order.timestamp;
    };

    const getHourInTimezone = (timestamp: number): number => {
      const d = new Date(timestamp);
      if (timezone) {
        const hr = d.toLocaleString('en-CA', { timeZone: timezone, hour: 'numeric', hour12: false });
        return parseInt(hr, 10);
      }
      return d.getHours();
    };

    const paidInRange = ordersForSummaryDate.filter((o) => {
      if (o.paymentStatus !== 'paid' || o.status === 'cancelled' || o.status === 'refunded') return false;
      return true;
    });

    // Only show operating window: 11am (11) to 10pm (22)
    const openingHour = 11;
    const closingHour = 22;
    const bucketCount = closingHour - openingHour + 1;

    const buckets = Array.from({ length: bucketCount }, (_, i) => ({
      hour: openingHour + i,
      total: 0,
      dineInOrders: 0,
      takeawayOrders: 0,
      deliveryOrders: 0,
      buffetOrders: 0,
    }));

    paidInRange.forEach((order) => {
      const saleTs = getSaleTimestamp(order);
      const hour = getHourInTimezone(saleTs);
      const amount = getOrderTotalFromItems(order);
      const bucket = buckets.find((h) => h.hour === hour);
      if (!bucket) return;

      bucket.total += amount;
      switch (order.orderType) {
        case 'dine-in':
          bucket.dineInOrders += 1;
          break;
        case 'takeaway':
          bucket.takeawayOrders += 1;
          break;
        case 'delivery':
          bucket.deliveryOrders += 1;
          break;
        case 'buffet':
          bucket.buffetOrders += 1;
          break;
        default:
          break;
      }
    });

    return buckets.map((h) => ({
      label: `${h.hour % 12 || 12}${h.hour < 12 ? 'a' : 'p'}`,
      total: h.total,
      dineInOrders: h.dineInOrders,
      takeawayOrders: h.takeawayOrders,
      deliveryOrders: h.deliveryOrders,
      buffetOrders: h.buffetOrders,
    }));
  }, [ordersForSummaryDate, integrationSettings?.businessHours?.timezone]);

  // Real-time data processing functions
  const processSalesData = useCallback(async () => {
    setSalesLoading(true);
    setSalesError(null);
    try {
      const data = await getDailySales(salesStartDate, salesEndDate);
      setSalesRows(data);
    } catch (error) {
      console.error('Failed to process daily sales:', error);
      setSalesError('Failed to load daily sales report.');
    } finally {
      setSalesLoading(false);
    }
  }, [salesStartDate, salesEndDate]);

  const processRegisterLogs = useCallback(async () => {
    setRegisterLoading(true);
    setRegisterError(null);
    try {
      // Include active sessions even if they're outside the date range
      const activeSessions = registerSessions.filter(s => s.isActive);
      const data = await getRegisterLogs(registerStartDate, registerEndDate, activeSessions);
      setRegisterLogs(data);
    } catch (error) {
      console.error('Failed to process register logs:', error);
      setRegisterError('Failed to load register logs.');
    } finally {
      setRegisterLoading(false);
    }
  }, [registerStartDate, registerEndDate, registerSessions]);

  const processInsights = useCallback(async () => {
    if (insightsLoadInFlightRef.current) return;
    insightsLoadInFlightRef.current = true;
    setInsightsLoading(true);
    setInsightsError(null);
    try {
      const [data, couponData] = await Promise.all([
        getSalesInsights(salesStartDate, salesEndDate),
        getCouponDashboardAnalytics(salesStartDate, salesEndDate),
      ]);
      setSalesInsights(data);
      setCouponDashboard(couponData);
    } catch (error) {
      console.error('Failed to process sales insights:', error);
      setSalesInsights(null);
      setCouponDashboard(null);
      setInsightsError('Failed to load sales insights.');
    } finally {
      insightsLoadInFlightRef.current = false;
      setInsightsLoading(false);
    }
  }, [salesStartDate, salesEndDate]);

  const ensureInsightsAndCouponLoaded = useCallback(() => {
    if (detailedReportsDataLoadedRef.current.insights) return;
    detailedReportsDataLoadedRef.current.insights = true;
    void processInsights();
  }, [processInsights]);

  const ensureRegisterLogsLoaded = useCallback(() => {
    if (detailedReportsDataLoadedRef.current.registerLogs) return;
    detailedReportsDataLoadedRef.current.registerLogs = true;
    void processRegisterLogs();
  }, [processRegisterLogs]);

  const processSalesByInvoice = useCallback(async () => {
    if (!salesByInvoiceStartDate || !salesByInvoiceEndDate) {
      setSalesByInvoiceError('Select a start date and an end date.');
      return;
    }
    if (salesByInvoiceStartDate > salesByInvoiceEndDate) {
      setSalesByInvoiceError('Start date must be on or before the end date.');
      return;
    }
    setSalesByInvoiceError(null);
    setSalesByInvoiceLoading(true);
    try {
      const page = await getSalesByInvoicePage(salesByInvoiceStartDate, salesByInvoiceEndDate, { pageSize: 50 });
      setSalesByInvoice(page.rows);
      setSalesByInvoiceCursor(page.nextCursor);
      setSalesByInvoiceLastLoadedKey(`${salesByInvoiceStartDate}|${salesByInvoiceEndDate}`);
    } catch (error) {
      console.error('Failed to load sales by invoice:', error);
      setSalesByInvoice([]);
      setSalesByInvoiceCursor(null);
      setSalesByInvoiceLastLoadedKey(null);
      setSalesByInvoiceError('Failed to load sales by invoice.');
    } finally {
      setSalesByInvoiceLoading(false);
    }
  }, [salesByInvoiceStartDate, salesByInvoiceEndDate]);

  const loadMoreSalesByInvoice = useCallback(async () => {
    if (!salesByInvoiceCursor || !salesByInvoiceStartDate || !salesByInvoiceEndDate || salesByInvoiceLoadingMore) return;
    setSalesByInvoiceLoadingMore(true);
    setSalesByInvoiceError(null);
    try {
      const page = await getSalesByInvoicePage(salesByInvoiceStartDate, salesByInvoiceEndDate, {
        pageSize: 50,
        cursorTimestamp: salesByInvoiceCursor.timestamp,
        cursorId: salesByInvoiceCursor.id,
      });
      setSalesByInvoice((prev) => [...prev, ...page.rows]);
      setSalesByInvoiceCursor(page.nextCursor);
    } catch (error) {
      console.error('Failed to load more sales by invoice:', error);
      setSalesByInvoiceError('Failed to load more invoices.');
    } finally {
      setSalesByInvoiceLoadingMore(false);
    }
  }, [salesByInvoiceCursor, salesByInvoiceStartDate, salesByInvoiceEndDate, salesByInvoiceLoadingMore]);

  const openEditRegisterLog = useCallback((log: RegisterLogRow) => {
    setEditRegisterLog(log);
    setEditRegisterOpeningFloat(String(log.openingFloat ?? ''));
    setEditRegisterClosingFloat(log.closingFloat !== undefined ? String(log.closingFloat) : '');
    setEditRegisterCashSales(String(log.cashSales ?? ''));
    setEditRegisterCardSales(String(log.cardSales ?? ''));
    setEditRegisterError(null);
  }, []);

  const closeEditRegisterLog = useCallback(() => {
    setEditRegisterLog(null);
    setEditRegisterError(null);
  }, []);

  const saveEditRegisterLog = useCallback(async () => {
    if (!editRegisterLog) return;
    const openingFloat = Number(editRegisterOpeningFloat);
    const closingFloat = Number(editRegisterClosingFloat);
    const cashSales = Number(editRegisterCashSales);
    const cardSales = Number(editRegisterCardSales);
    if (Number.isNaN(openingFloat) || openingFloat < 0) {
      setEditRegisterError('Enter a valid opening float.');
      return;
    }
    if (Number.isNaN(closingFloat) || closingFloat < 0) {
      setEditRegisterError('Enter a valid closing float.');
      return;
    }
    if (Number.isNaN(cashSales) || cashSales < 0) {
      setEditRegisterError('Enter valid cash sales.');
      return;
    }
    if (Number.isNaN(cardSales) || cardSales < 0) {
      setEditRegisterError('Enter valid card sales.');
      return;
    }
    const variance = closingFloat - openingFloat - cashSales;
    setEditRegisterSaving(true);
    setEditRegisterError(null);
    try {
      await registerSessionsService.update(editRegisterLog.id, {
        openingFloat,
        closingFloat,
        cashSales,
        cardSales,
        variance,
      });
      closeEditRegisterLog();
      await processRegisterLogs();
    } catch (err: any) {
      setEditRegisterError(err?.message || 'Failed to update register log.');
    } finally {
      setEditRegisterSaving(false);
    }
  }, [editRegisterLog, editRegisterOpeningFloat, editRegisterClosingFloat, editRegisterCashSales, editRegisterCardSales, closeEditRegisterLog, processRegisterLogs]);

  const loadMonthlyReport = useCallback(async () => {
    const myRequest = ++monthlyLoadRequestIdRef.current;
    const month = monthlyReportMonth;
    setMonthlyReportLoading(true);
    setMonthlyReportError(null);
    try {
      const rows = await getMonthlySalesBreakdown(month);
      if (myRequest !== monthlyLoadRequestIdRef.current) return;
      setMonthlyReportRows(rows);
      setMonthlySalesDataMonth(month);
    } catch (error) {
      if (myRequest !== monthlyLoadRequestIdRef.current) return;
      console.error('Failed to load monthly report:', error);
      setMonthlyReportRows([]);
      setMonthlySalesDataMonth(null);
      setMonthlyReportError('Failed to load monthly report.');
    } finally {
      if (myRequest === monthlyLoadRequestIdRef.current) {
        setMonthlyReportLoading(false);
      }
    }
  }, [monthlyReportMonth, currentUser?.id, currentUser?.name]);

  const importMonthlyAnzStatement = useCallback(
    async (file: File) => {
      try {
        const XLSX = await import('xlsx');
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(firstSheet, {
          defval: '',
          raw: true,
          cellDates: true,
        } as Parameters<typeof XLSX.utils.sheet_to_json>[1]);
        if (rows.length === 0) {
          setMonthlyImportMessage('No rows found in uploaded statement.');
          return;
        }

        const rulesByPatternKey = new Map(
          (await bankExpenseCategorizationRulesService.listAll()).map((r) => [r.patternKey, r])
        );
        let autoRuleCount = 0;

        const importedSmartPayByDate: Record<string, number> = {};
        const mergedExpenseLinesByDate: Record<string, MonthlyReportExpenseLine[]> = {
          ...monthlyExpenseLinesByDate,
        };
        const newTxnIds: Record<string, true> = {};
        const transactionsToCreate: Array<{
          id: string;
          month: string;
          date: string;
          amount: number;
          code?: string;
          details?: string;
          reference?: string;
          type?: string;
          importedAt: number;
          importedBy?: number;
          importedByName?: string;
        }> = [];
        let importedCount = 0;
        let duplicateCount = 0;
        const anzBaseKeyRowIndex = new Map<string, number>();

        for (const row of rows) {
          const date = parseAnzStatementDate(
            getStatementField(row, 'Transaction Date', 'Date', 'Trans Date', 'Value Date', 'Transaction date'),
            XLSX
          );
          const amount = parseAnzRowSignedAmount(row);
          if (!date || amount == null) continue;

          const code = getStatementStringField(row, 'Code', 'OP code', 'Tran code');
          const details = getStatementStringField(
            row,
            'Details',
            'Particulars',
            'Description',
            'Narration',
            'Payee',
            'Transaction Details',
            'Transaction detail'
          );
          const reference = getStatementStringField(
            row,
            'Reference',
            'Code reference',
            'Your reference',
            'Our reference',
            'Ref'
          );
          const type = getStatementStringField(row, 'Type', 'Transaction Type', 'Trans type');

          const baseKey = buildAnzImportBaseKey(date, amount, row);
          const n = anzBaseKeyRowIndex.get(baseKey) ?? 0;
          anzBaseKeyRowIndex.set(baseKey, n + 1);
          const uniqueTxnId = n === 0 ? baseKey : `${baseKey}@@dup@@${n}`;

          if (monthlyImportedTxnIds[uniqueTxnId] || newTxnIds[uniqueTxnId]) {
            duplicateCount += 1;
            continue;
          }

          newTxnIds[uniqueTxnId] = true;
          importedCount += 1;
          transactionsToCreate.push({
            id: uniqueTxnId,
            month: monthlyReportMonth,
            date,
            amount,
            code,
            details,
            reference,
            type,
            importedAt: Date.now(),
            importedBy: currentUser?.id,
            importedByName: currentUser?.name,
          });

          // Expenses: only debit (negative) bank transactions — one line per transaction for GST control.
          if (amount < 0) {
            const { line, appliedRule } = buildAnzDebitExpenseLine(
              uniqueTxnId,
              Math.abs(amount),
              details,
              reference,
              code,
              rulesByPatternKey
            );
            if (appliedRule) autoRuleCount += 1;
            mergedExpenseLinesByDate[date] = [...(mergedExpenseLinesByDate[date] ?? []), line];
          }

          // Smart Pay column: settlement transactions by Code.
          if (code.toLowerCase() === 'smartpaysett') {
            importedSmartPayByDate[date] = (importedSmartPayByDate[date] ?? 0) + amount;
          }
        }

        const mergedSmartPayByDate: Record<string, number> = { ...monthlySmartPayByDate };
        Object.entries(importedSmartPayByDate).forEach(([date, value]) => {
          mergedSmartPayByDate[date] = (mergedSmartPayByDate[date] ?? 0) + value;
        });

        if (!monthlySyncPermissionBlocked) {
          setMonthlySyncStatus('syncing');
        }

        if (transactionsToCreate.length > 0) {
          await monthlyReportImportTransactionsService.createMany(
            transactionsToCreate.map(({ id: _id, ...tx }) => tx),
            transactionsToCreate.map((tx) => tx.id)
          );
        }

        const changedDates = new Set<string>([...Object.keys(importedSmartPayByDate)]);
        Object.entries(mergedExpenseLinesByDate).forEach(([date, lines]) => {
          if (lines.some((l) => newTxnIds[l.id])) changedDates.add(date);
        });
        if (changedDates.size > 0) {
          await monthlyReportDayEntriesService.upsertMany(
            Array.from(changedDates).map((date) => {
              const lines = mergedExpenseLinesByDate[date] ?? [];
              return {
                month: monthlyReportMonth,
                date,
                expenseTotal: sumMonthlyExpenseLines(lines),
                expenseLines: lines,
                expenseGstClaimable: false,
                smartPayTotal: mergedSmartPayByDate[date] ?? 0,
                updatedAt: Date.now(),
                updatedBy: currentUser?.id,
                updatedByName: currentUser?.name,
              };
            })
          );
        }

        if (Object.keys(newTxnIds).length > 0) {
          setMonthlyImportedTxnIds((prev) => ({ ...prev, ...newTxnIds }));
        }

        setMonthlyExpenseLinesByDate(mergedExpenseLinesByDate);
        if (Object.keys(importedSmartPayByDate).length > 0) setMonthlySmartPayByDate(mergedSmartPayByDate);
        if (!monthlySyncPermissionBlocked) {
          setMonthlySyncStatus('synced');
          setMonthlySyncStatusMessage(null);
        }

        setMonthlyImportMessage(
          `Import complete: ${importedCount} new transactions, ${duplicateCount} skipped as duplicates.${
            autoRuleCount > 0 ? ` ${autoRuleCount} line(s) pre-categorised from saved bank-text rules (review in GST / lines).` : ''
          }`
        );
      } catch (error) {
        console.error('Failed to import ANZ statement:', error);
        if (isFirestorePermissionError(error)) {
          setMonthlySyncPermissionBlocked(true);
          setMonthlySyncStatus('failed');
          setMonthlySyncStatusMessage('Cloud sync blocked: insufficient Firestore permissions. Using local data only.');
          setMonthlyImportMessage('Import parsed, but cloud save failed due to Firestore permissions.');
        } else {
          setMonthlySyncStatus('failed');
          setMonthlySyncStatusMessage('Cloud sync failed. Changes kept locally.');
          setMonthlyImportMessage('Failed to import statement. Please check the ANZ export format.');
        }
      }
    },
    [
      monthlyImportedTxnIds,
      monthlyReportMonth,
      monthlyExpenseLinesByDate,
      monthlySmartPayByDate,
      currentUser?.id,
      currentUser?.name,
      monthlySyncPermissionBlocked,
    ]
  );

  useEffect(() => {
    if (!isMonthlyDetailedSection) return;
    setMonthlyReportRows([]);
    setMonthlySalesDataMonth(null);
    setMonthlyReportError(null);
  }, [monthlyReportMonth, isMonthlyDetailedSection]);

  useEffect(() => {
    if (!isMonthlyDetailedSection) return;
    if (monthlyReportLoading) return;
    if (monthlySalesDataMonth === monthlyReportMonth) return;
    void loadMonthlyReport();
  }, [
    isMonthlyDetailedSection,
    monthlyReportMonth,
    monthlySalesDataMonth,
    monthlyReportLoading,
    loadMonthlyReport,
  ]);

  useEffect(() => {
    if (!isMonthlyDetailedSection) return;
    setMonthlyStorageHydrated(false);
    setMonthlySyncPermissionBlocked(false);
    setMonthlySyncStatus('synced');
    setMonthlySyncStatusMessage(null);
    let cancelled = false;
    (async () => {
      let parsedLocal: MonthlyPersistedImportState | null = null;
      try {
        const raw = localStorage.getItem(getMonthlyStorageKey(monthlyReportMonth));
        parsedLocal = raw ? (JSON.parse(raw) as MonthlyPersistedImportState) : null;
      } catch (error) {
        console.warn('Failed to parse local monthly state:', error);
      }

      try {
        setMonthlySyncStatus('syncing');
        const [remoteEntries, remoteTransactions] = await Promise.all([
          monthlyReportDayEntriesService.getByMonth(monthlyReportMonth),
          monthlyReportImportTransactionsService.getByMonth(monthlyReportMonth),
        ]);
        if (cancelled) return;

        const remoteExpenseLinesByDate: Record<string, MonthlyReportExpenseLine[]> = {};
        const remoteSmartPayByDate: Record<string, number> = {};
        remoteEntries.forEach((entry) => {
          remoteExpenseLinesByDate[entry.date] = mergeExpenseLinesFromRemoteEntry(entry);
          remoteSmartPayByDate[entry.date] = Number(entry.smartPayTotal || 0) || 0;
        });
        const remoteTxnIds: Record<string, true> = {};
        remoteTransactions.forEach((tx) => {
          remoteTxnIds[tx.id] = true;
        });

        // One-time migration path: if remote month is empty but local month has values, push local to Firestore.
        const hasRemoteState = remoteEntries.length > 0 || remoteTransactions.length > 0;
        const localExpenseLines =
          parsedLocal?.expenseLinesByDate && Object.keys(parsedLocal.expenseLinesByDate).length > 0
            ? parsedLocal.expenseLinesByDate
            : migrateLegacyMonthlyExpenseMaps(parsedLocal?.expensesByDate, parsedLocal?.expenseGstClaimableByDate);
        const localSmartPay = parsedLocal?.smartPayByDate ?? {};
        const localTxnIds = parsedLocal?.importedTxnIds ?? {};
        const hasLocalImportState =
          Object.keys(localExpenseLines).length > 0 ||
          Object.keys(localSmartPay).length > 0 ||
          Object.keys(localTxnIds).length > 0;

        if (!hasRemoteState && hasLocalImportState) {
          const migratedEntriesDates = new Set<string>([
            ...Object.keys(localExpenseLines),
            ...Object.keys(localSmartPay),
          ]);
          if (migratedEntriesDates.size > 0) {
            await monthlyReportDayEntriesService.upsertMany(
              Array.from(migratedEntriesDates).map((date) => {
                const lines = localExpenseLines[date] ?? [];
                return {
                  month: monthlyReportMonth,
                  date,
                  expenseTotal: sumMonthlyExpenseLines(lines),
                  expenseLines: lines,
                  expenseGstClaimable: false,
                  smartPayTotal: Number(localSmartPay[date] || 0) || 0,
                  updatedAt: Date.now(),
                  updatedBy: currentUser?.id,
                  updatedByName: currentUser?.name,
                };
              })
            );
          }
          if (Object.keys(localTxnIds).length > 0) {
            const now = Date.now();
            const ids = Object.keys(localTxnIds);
            await monthlyReportImportTransactionsService.createMany(
              ids.map((id) => {
                const core = stripAnzImportDupSuffix(id);
                const p = core.split('|');
                return {
                  month: monthlyReportMonth,
                  date: p[0] || `${monthlyReportMonth}-01`,
                  amount: Number(p[1] || 0) || 0,
                  code: p[2] || '',
                  details: p[3] || '',
                  reference: p[4] || '',
                  type: p[5] || '',
                  importedAt: now,
                  importedBy: currentUser?.id,
                  importedByName: currentUser?.name,
                };
              }),
              ids
            );
          }
        }

        const useExpenseLines = hasRemoteState ? remoteExpenseLinesByDate : localExpenseLines;
        const useSmartPay = hasRemoteState ? remoteSmartPayByDate : localSmartPay;
        const useTxnIds = hasRemoteState ? remoteTxnIds : localTxnIds;

        setMonthlyExpenseLinesByDate(useExpenseLines);
        setMonthlySmartPayByDate(useSmartPay);
        setMonthlyImportedTxnIds(useTxnIds);
        setMonthlyCompletionFilter(parsedLocal?.completionFilter ?? 'all');
        setMonthlyDateFromFilter(parsedLocal?.dateFromFilter ?? '');
        setMonthlyDateToFilter(parsedLocal?.dateToFilter ?? '');
        setMonthlyTerminalIdsFilter(parsedLocal?.terminalIdsFilter ?? '');
        setMonthlyDiscountFilter(parsedLocal?.discountFilter ?? 'all');
        setMonthlyColumnVisibility({
          ...DEFAULT_MONTHLY_COLUMN_VISIBILITY,
          ...(parsedLocal?.columnVisibility ?? {}),
        });
        setIsMonthlyColumnVisibilityOpen(parsedLocal?.isColumnVisibilityOpen ?? false);
        setMonthlyStorageHydrated(true);
        setMonthlySyncStatus('synced');
        setMonthlySyncStatusMessage(null);
      } catch (error) {
        console.warn('Failed to restore shared monthly state:', error);
        if (isFirestorePermissionError(error)) {
          setMonthlySyncPermissionBlocked(true);
          setMonthlySyncStatus('failed');
          setMonthlySyncStatusMessage('Cloud sync blocked: insufficient Firestore permissions. Using local data only.');
        } else {
          setMonthlySyncStatus('failed');
          setMonthlySyncStatusMessage('Cloud sync unavailable. Using local data only.');
        }
        setMonthlyExpenseLinesByDate(
          parsedLocal?.expenseLinesByDate && Object.keys(parsedLocal.expenseLinesByDate).length > 0
            ? parsedLocal.expenseLinesByDate
            : migrateLegacyMonthlyExpenseMaps(parsedLocal?.expensesByDate, parsedLocal?.expenseGstClaimableByDate)
        );
        setMonthlySmartPayByDate(parsedLocal?.smartPayByDate ?? {});
        setMonthlyImportedTxnIds(parsedLocal?.importedTxnIds ?? {});
        setMonthlyCompletionFilter(parsedLocal?.completionFilter ?? 'all');
        setMonthlyDateFromFilter(parsedLocal?.dateFromFilter ?? '');
        setMonthlyDateToFilter(parsedLocal?.dateToFilter ?? '');
        setMonthlyTerminalIdsFilter(parsedLocal?.terminalIdsFilter ?? '');
        setMonthlyDiscountFilter(parsedLocal?.discountFilter ?? 'all');
        setMonthlyColumnVisibility({
          ...DEFAULT_MONTHLY_COLUMN_VISIBILITY,
          ...(parsedLocal?.columnVisibility ?? {}),
        });
        setIsMonthlyColumnVisibilityOpen(parsedLocal?.isColumnVisibilityOpen ?? false);
        setMonthlyStorageHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [monthlyReportMonth, isMonthlyDetailedSection]);

  useEffect(() => {
    if (!isMonthlyDetailedSection) return;
    if (!monthlyStorageHydrated) return;
    const payload: MonthlyPersistedImportState = {
      expenseLinesByDate: monthlyExpenseLinesByDate,
      smartPayByDate: monthlySmartPayByDate,
      importedTxnIds: monthlyImportedTxnIds,
      completionFilter: monthlyCompletionFilter,
      dateFromFilter: monthlyDateFromFilter,
      dateToFilter: monthlyDateToFilter,
      terminalIdsFilter: monthlyTerminalIdsFilter,
      discountFilter: monthlyDiscountFilter,
      columnVisibility: monthlyColumnVisibility,
      isColumnVisibilityOpen: isMonthlyColumnVisibilityOpen,
    };
    try {
      localStorage.setItem(getMonthlyStorageKey(monthlyReportMonth), JSON.stringify(payload));
    } catch (error) {
      console.warn('Failed to persist monthly import state:', error);
    }
  }, [
    isMonthlyDetailedSection,
    monthlyReportMonth,
    monthlyExpenseLinesByDate,
    monthlySmartPayByDate,
    monthlyImportedTxnIds,
    monthlyCompletionFilter,
    monthlyDateFromFilter,
    monthlyDateToFilter,
    monthlyTerminalIdsFilter,
    monthlyDiscountFilter,
    monthlyColumnVisibility,
    isMonthlyColumnVisibilityOpen,
    monthlyStorageHydrated,
  ]);

  useEffect(() => {
    if (!isMonthlyDetailedSection) return;
    if (!monthlyStorageHydrated || monthlySyncPermissionBlocked) return;
    const syncTimer = setTimeout(() => {
      const dates = new Set<string>([
        ...Object.keys(monthlyExpenseLinesByDate),
        ...Object.keys(monthlySmartPayByDate),
      ]);
      if (dates.size === 0) return;
      setMonthlySyncStatus('syncing');
      void monthlyReportDayEntriesService.upsertMany(
        Array.from(dates).map((date) => {
          const lines = monthlyExpenseLinesByDate[date] ?? [];
          return {
            month: monthlyReportMonth,
            date,
            expenseTotal: sumMonthlyExpenseLines(lines),
            expenseLines: lines,
            expenseGstClaimable: false,
            smartPayTotal: Number(monthlySmartPayByDate[date] || 0) || 0,
            updatedAt: Date.now(),
            updatedBy: currentUser?.id,
            updatedByName: currentUser?.name,
          };
        })
      ).catch((error) => {
        console.error('Failed to sync monthly report entries:', error);
        if (isFirestorePermissionError(error)) {
          setMonthlySyncPermissionBlocked(true);
          setMonthlySyncStatusMessage('Cloud sync blocked: insufficient Firestore permissions. Using local data only.');
        } else {
          setMonthlySyncStatusMessage('Cloud sync failed. Changes kept locally.');
        }
        setMonthlySyncStatus('failed');
      }).then(() => {
        setMonthlySyncStatus((prev) => (prev === 'syncing' ? 'synced' : prev));
      });
    }, 700);
    return () => clearTimeout(syncTimer);
  }, [
    isMonthlyDetailedSection,
    monthlyStorageHydrated,
    monthlySyncPermissionBlocked,
    monthlyReportMonth,
    monthlyExpenseLinesByDate,
    monthlySmartPayByDate,
    currentUser?.id,
    currentUser?.name,
  ]);

  const filteredMonthlyReportRows = useMemo(() => {
    const hasExpense = (date: string) => sumMonthlyExpenseLines(monthlyExpenseLinesByDate[date]) > 0;
    const completionFiltered =
      monthlyCompletionFilter === 'complete'
        ? monthlyReportRows.filter((row) => hasExpense(row.date))
        : monthlyCompletionFilter === 'incomplete'
          ? monthlyReportRows.filter((row) => !hasExpense(row.date))
          : monthlyReportRows;

    return completionFiltered.filter((row) => {
      const matchesDateFrom = !monthlyDateFromFilter || row.date >= monthlyDateFromFilter;
      if (!matchesDateFrom) return false;

      const matchesDateTo = !monthlyDateToFilter || row.date <= monthlyDateToFilter;
      if (!matchesDateTo) return false;

      const terminalFilter = monthlyTerminalIdsFilter.trim().toLowerCase();
      const matchesTerminalIds =
        terminalFilter === '' || (row.cardTerminalIds || '').toLowerCase().includes(terminalFilter);
      if (!matchesTerminalIds) return false;

      if (monthlyDiscountFilter === 'with-discount') return row.discount > 0;
      if (monthlyDiscountFilter === 'without-discount') return row.discount <= 0;
      return true;
    });
  }, [
    monthlyReportRows,
    monthlyExpenseLinesByDate,
    monthlyCompletionFilter,
    monthlyDateFromFilter,
    monthlyDateToFilter,
    monthlyTerminalIdsFilter,
    monthlyDiscountFilter,
  ]);

  const monthlyTablePageCount = useMemo(() => {
    const n = filteredMonthlyReportRows.length;
    if (n === 0) return 1;
    return Math.max(1, Math.ceil(n / monthlyTablePageSize));
  }, [filteredMonthlyReportRows.length, monthlyTablePageSize]);

  const effectiveMonthlyTablePage = useMemo(
    () => Math.min(Math.max(1, monthlyTablePage), monthlyTablePageCount),
    [monthlyTablePage, monthlyTablePageCount]
  );

  const paginatedMonthlyReportRows = useMemo(() => {
    const start = (effectiveMonthlyTablePage - 1) * monthlyTablePageSize;
    return filteredMonthlyReportRows.slice(start, start + monthlyTablePageSize);
  }, [filteredMonthlyReportRows, effectiveMonthlyTablePage, monthlyTablePageSize]);

  const monthlyTableRangeLabel = useMemo(() => {
    const n = filteredMonthlyReportRows.length;
    if (n === 0) return '0 days';
    const from = (effectiveMonthlyTablePage - 1) * monthlyTablePageSize + 1;
    const to = Math.min(n, effectiveMonthlyTablePage * monthlyTablePageSize);
    return `Showing ${from}–${to} of ${n} day rows`;
  }, [filteredMonthlyReportRows.length, effectiveMonthlyTablePage, monthlyTablePageSize]);

  useEffect(() => {
    if (!isMonthlyDetailedSection) return;
    if (monthlyTablePage !== effectiveMonthlyTablePage) {
      setMonthlyTablePage(effectiveMonthlyTablePage);
    }
  }, [monthlyTablePage, effectiveMonthlyTablePage, isMonthlyDetailedSection]);

  useEffect(() => {
    if (!isMonthlyDetailedSection) return;
    setMonthlyTablePage(1);
  }, [
    isMonthlyDetailedSection,
    monthlyReportMonth,
    monthlyCompletionFilter,
    monthlyDateFromFilter,
    monthlyDateToFilter,
    monthlyTerminalIdsFilter,
    monthlyDiscountFilter,
    monthlyTablePageSize,
  ]);

  /** Precompute per-date expense totals so the monthly table does not call reducers on every render. */
  const monthlyExpenseCellByDate = useMemo(() => {
    const out: Record<
      string,
      {
        operatingTotal: number;
        wageAmount: number;
        count: number;
        gstAllocationComplete: boolean;
      }
    > = {};
    for (const [d, lines] of Object.entries(monthlyExpenseLinesByDate)) {
      out[d] = {
        operatingTotal: sumMonthlyExpenseLinesExcludingSalaryLike(lines),
        wageAmount: sumSalaryLikeExpenseLines(lines),
        count: lines?.length ?? 0,
        gstAllocationComplete: expenseLinesGstAllocationComplete(lines, {
          date: d,
          importedTxnIds: monthlyImportedTxnIds,
        }),
      };
    }
    return out;
  }, [monthlyExpenseLinesByDate, monthlyImportedTxnIds]);

  const monthlyReportTotals = useMemo(() => {
    return filteredMonthlyReportRows.reduce(
      (acc, row) => {
        const expense = sumMonthlyExpenseLinesExcludingSalaryLike(monthlyExpenseLinesByDate[row.date]);
        acc.totalOrders += row.totalOrders;
        acc.dineInOrders += row.dineInOrders;
        acc.dineInAmount += row.dineInAmount;
        acc.takeawayOrders += row.takeawayOrders;
        acc.takeawayAmount += row.takeawayAmount;
        acc.deliveryOrders += row.deliveryOrders;
        acc.deliveryAmount += row.deliveryAmount;
        acc.uberDoordashOrders += row.uberDoordashOrders;
        acc.uberDoordashAmount += row.uberDoordashAmount;
        acc.totalSales += row.totalSales;
        acc.cashSales += row.cashSales;
        acc.cardSales += row.cardSales;
        acc.bankTransferSales += row.bankTransferSales;
        acc.discount += row.discount;
        acc.gstCollected += row.gstCollected;
        acc.smartPay += monthlySmartPayByDate[row.date] ?? 0;
        acc.expenses += expense;
        return acc;
      },
      {
        totalOrders: 0,
        dineInOrders: 0,
        dineInAmount: 0,
        takeawayOrders: 0,
        takeawayAmount: 0,
        deliveryOrders: 0,
        deliveryAmount: 0,
        uberDoordashOrders: 0,
        uberDoordashAmount: 0,
        totalSales: 0,
        cashSales: 0,
        cardSales: 0,
        bankTransferSales: 0,
        discount: 0,
        gstCollected: 0,
        smartPay: 0,
        expenses: 0,
      }
    );
  }, [filteredMonthlyReportRows, monthlyExpenseLinesByDate, monthlySmartPayByDate]);

  const hasActiveMonthlyColumnFilters = useMemo(
    () =>
      Boolean(
        monthlyDateFromFilter ||
          monthlyDateToFilter ||
          monthlyTerminalIdsFilter.trim() ||
          monthlyDiscountFilter !== 'all'
      ),
    [monthlyDateFromFilter, monthlyDateToFilter, monthlyTerminalIdsFilter, monthlyDiscountFilter]
  );

  const monthlyAppliedFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (monthlyDateFromFilter || monthlyDateToFilter) {
      parts.push(`Date: ${monthlyDateFromFilter || 'Any'} to ${monthlyDateToFilter || 'Any'}`);
    }
    if (monthlyTerminalIdsFilter.trim()) {
      parts.push(`Terminal IDs: ${monthlyTerminalIdsFilter.trim()}`);
    }
    if (monthlyDiscountFilter !== 'all') {
      parts.push(monthlyDiscountFilter === 'with-discount' ? 'Discount: Applied' : 'Discount: Not Applied');
    }
    return parts.length > 0 ? parts.join(' | ') : 'None';
  }, [monthlyDateFromFilter, monthlyDateToFilter, monthlyTerminalIdsFilter, monthlyDiscountFilter]);

  const monthlySalesDataReady = monthlySalesDataMonth === monthlyReportMonth;

  /** Expense / Smart Pay from imports when order-based sales rows are not loaded yet. */
  const monthlyExpensesFromLinesOnly = useMemo(() => {
    let t = 0;
    for (const [d, lines] of Object.entries(monthlyExpenseLinesByDate)) {
      if (d.slice(0, 7) !== monthlyReportMonth) continue;
      t += sumMonthlyExpenseLinesExcludingSalaryLike(lines);
    }
    return t;
  }, [monthlyExpenseLinesByDate, monthlyReportMonth]);

  const monthlySmartPayFromMapOnly = useMemo(() => {
    let t = 0;
    for (const [d, v] of Object.entries(monthlySmartPayByDate)) {
      if (d.slice(0, 7) !== monthlyReportMonth) continue;
      t += Number(v) || 0;
    }
    return t;
  }, [monthlySmartPayByDate, monthlyReportMonth]);

  const monthlyGstClaimableFromLinesInMonth = useMemo(() => {
    let t = 0;
    for (const [d, lines] of Object.entries(monthlyExpenseLinesByDate)) {
      if (d.slice(0, 7) !== monthlyReportMonth) continue;
      t += gstClaimableTotalFromLines(gstEligibleExpenseLines(lines));
    }
    return t;
  }, [monthlyExpenseLinesByDate, monthlyReportMonth]);

  const monthlySalaryAccrual = useMemo(() => {
    const monthStart = `${monthlyReportMonth}-01`;
    const monthDate = new Date(`${monthlyReportMonth}-01T00:00:00`);
    const monthEnd = formatInputDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0));

    const overlapDays = (start: string, end: string): number => {
      const a = new Date(`${start}T00:00:00`).getTime();
      const b = new Date(`${end}T23:59:59`).getTime();
      const m0 = new Date(`${monthStart}T00:00:00`).getTime();
      const m1 = new Date(`${monthEnd}T23:59:59`).getTime();
      const s = Math.max(a, m0);
      const e = Math.min(b, m1);
      if (e < s) return 0;
      return Math.floor((e - s) / (24 * 60 * 60 * 1000)) + 1;
    };

    let total = 0;
    let dayBasedImports = 0;
    let fallbackImports = 0;
    let contributingImports = 0;

    monthlyPayrollImports.forEach((imp) => {
      const periodDays = overlapDays(imp.payPeriodStart, imp.payPeriodEnd);
      if (periodDays <= 0) return;
      contributingImports += 1;

      let importAllocated = 0;
      let usedDayBreakdown = false;
      const lines = imp.lines ?? [];
      lines.forEach((line) => {
        if (OUTSOURCED_STAFF_EXCLUSIONS.has(normStaffName(line.employeeName))) return;
        const gross = Math.max(0, Number(line.gross || 0));
        const rate = Number(line.rate || 0);
        const dayRows = (line.dayBreakdown ?? []).filter((d) => typeof d.date === 'string' && d.date.length >= 10);
        if (dayRows.length > 0) {
          const totalLineHours = dayRows.reduce((sum, d) => sum + Math.max(0, Number(d.hours || 0)), 0);
          const monthHours = dayRows
            .filter((d) => d.date.slice(0, 7) === monthlyReportMonth)
            .reduce((sum, d) => sum + Math.max(0, Number(d.hours || 0)), 0);
          if (monthHours > 0) {
            usedDayBreakdown = true;
            if (gross > 0 && totalLineHours > 0) {
              importAllocated += gross * (monthHours / totalLineHours);
            } else if (rate > 0) {
              importAllocated += monthHours * rate;
            }
          }
        }
      });

      if (importAllocated > 0) {
        total += importAllocated;
        dayBasedImports += 1;
      } else {
        fallbackImports += 1;
        const periodLength =
          Math.floor(
            (new Date(`${imp.payPeriodEnd}T23:59:59`).getTime() - new Date(`${imp.payPeriodStart}T00:00:00`).getTime()) /
              (24 * 60 * 60 * 1000),
          ) + 1;
        const reportable = reportableGrossPayFromImport(imp);
        if (reportable > 0 && periodLength > 0) {
          total += reportable * (periodDays / periodLength);
        }
      }
    });

    return {
      total: Math.max(0, total),
      dayBasedImports,
      fallbackImports,
      contributingImports,
    };
  }, [monthlyPayrollImports, monthlyReportMonth]);

  /** Wage / payroll lines in monthly report expenses (bank / manual categorisation), incl. GST in line amounts. */
  const monthlySalaryCategorized = useMemo(() => {
    let t = 0;
    for (const [d, lines] of Object.entries(monthlyExpenseLinesByDate)) {
      if (d.slice(0, 7) !== monthlyReportMonth) continue;
      t += sumSalaryLikeExpenseLines(lines);
    }
    return Math.max(0, t);
  }, [monthlyExpenseLinesByDate, monthlyReportMonth]);

  /**
   * Cash-style: attribute each import’s full reportable gross to the month of pay period end
   * (common proxy for pay run / pay date; no work-day split).
   */
  const monthlySalaryPayrollCash = useMemo(() => {
    let total = 0;
    let importCount = 0;
    monthlyPayrollImports.forEach((imp) => {
      if (imp.payPeriodEnd.slice(0, 7) !== monthlyReportMonth) return;
      total += reportableGrossPayFromImport(imp);
      importCount += 1;
    });
    return { total: Math.max(0, total), importCount };
  }, [monthlyPayrollImports, monthlyReportMonth]);

  const monthlySalaryVariance = useMemo(() => {
    if (monthlySalaryPayrollBasis === 'accrual') {
      return monthlySalaryCategorized - monthlySalaryAccrual.total;
    }
    return monthlySalaryCategorized - monthlySalaryPayrollCash.total;
  }, [
    monthlySalaryPayrollBasis,
    monthlySalaryCategorized,
    monthlySalaryAccrual.total,
    monthlySalaryPayrollCash.total,
  ]);

  const monthlyDashboardStats = useMemo(() => {
    const completedDays = filteredMonthlyReportRows.filter((row) => {
      return sumMonthlyExpenseLines(monthlyExpenseLinesByDate[row.date]) > 0;
    }).length;
    const daysWithExpenses = completedDays;
    const gstAllocationCompleteDays = filteredMonthlyReportRows.filter((row) =>
      expenseLinesGstAllocationComplete(monthlyExpenseLinesByDate[row.date], {
        date: row.date,
        importedTxnIds: monthlyImportedTxnIds,
      })
    ).length;
    const dayCount = filteredMonthlyReportRows.length;
    const avgSales = dayCount > 0 ? monthlyReportTotals.totalSales / dayCount : 0;
    const gstClaimableTotal = filteredMonthlyReportRows.reduce((sum, row) => {
      return sum + gstClaimableTotalFromLines(gstEligibleExpenseLines(monthlyExpenseLinesByDate[row.date]));
    }, 0);
    const gstCollectedTotal = monthlyReportTotals.gstCollected;
    return {
      dayCount,
      completedDays,
      daysWithExpenses,
      gstAllocationCompleteDays,
      incompleteDays: Math.max(0, dayCount - completedDays),
      avgSales,
      netAfterExpenses: monthlyReportTotals.totalSales - monthlyReportTotals.expenses,
      gstCollectedTotal,
      gstClaimableTotal,
      gstDifference: gstCollectedTotal - gstClaimableTotal,
      smartPayTotal: monthlyReportTotals.smartPay,
    };
  }, [filteredMonthlyReportRows, monthlyExpenseLinesByDate, monthlyReportTotals, monthlyImportedTxnIds]);

  const monthlyExportRows = useMemo(() => {
    const context = {
      reportMonth: monthlyReportMonth,
      completionFilter: monthlyCompletionFilter,
      columnFilters: monthlyAppliedFilterSummary,
    };
    const rows = filteredMonthlyReportRows.map((row) => {
      const lines = monthlyExpenseLinesByDate[row.date] ?? [];
      const expense = sumMonthlyExpenseLinesExcludingSalaryLike(lines);
      const gstClaimableAmount = gstClaimableTotalFromLines(gstEligibleExpenseLines(lines));
      const claimableLineCount = lines.filter((l) => l.gstClaimable && !l.zeroGst).length;
      return {
        rowType: 'Day',
        date: row.date,
        totalOrders: row.totalOrders,
        dineInOrders: row.dineInOrders,
        dineInAmount: row.dineInAmount,
        takeawayOrders: row.takeawayOrders,
        takeawayAmount: row.takeawayAmount,
        deliveryOrders: row.deliveryOrders,
        deliveryAmount: row.deliveryAmount,
        uberDoordashOrders: row.uberDoordashOrders,
        uberDoordashAmount: row.uberDoordashAmount,
        totalSales: row.totalSales,
        cashSales: row.cashSales,
        cardSales: row.cardSales,
        bankTransferSales: row.bankTransferSales,
        discount: row.discount,
        gstCollected: row.gstCollected,
        smartPay: monthlySmartPayByDate[row.date] ?? 0,
        cardTerminalIds: row.cardTerminalIds || '',
        expenses: expense,
        expenseGstClaimable: claimableLineCount > 0 ? `${claimableLineCount} line(s)` : 'None',
        expenseGstClaimableAmount: gstClaimableAmount,
        expenseLinesDetail: JSON.stringify(
          lines.map((l) => ({
            amount: l.amount,
            gstClaimable: l.gstClaimable,
            zeroGst: l.zeroGst === true,
            label: l.label ?? '',
            source: l.source ?? '',
            isWageOrPayrollExpense: l.isWageOrPayrollExpense === true,
            isWageGstClaimable: l.isWageGstClaimable === true,
          }))
        ),
        reportMonth: context.reportMonth,
        completionFilter: context.completionFilter,
        columnFilters: context.columnFilters,
      };
    });
    rows.push({
      rowType: 'TOTALS',
      date: 'TOTALS (filtered)',
      totalOrders: monthlyReportTotals.totalOrders,
      dineInOrders: monthlyReportTotals.dineInOrders,
      dineInAmount: monthlyReportTotals.dineInAmount,
      takeawayOrders: monthlyReportTotals.takeawayOrders,
      takeawayAmount: monthlyReportTotals.takeawayAmount,
      deliveryOrders: monthlyReportTotals.deliveryOrders,
      deliveryAmount: monthlyReportTotals.deliveryAmount,
      uberDoordashOrders: monthlyReportTotals.uberDoordashOrders,
      uberDoordashAmount: monthlyReportTotals.uberDoordashAmount,
      totalSales: monthlyReportTotals.totalSales,
      cashSales: monthlyReportTotals.cashSales,
      cardSales: monthlyReportTotals.cardSales,
      bankTransferSales: monthlyReportTotals.bankTransferSales,
      discount: monthlyReportTotals.discount,
      gstCollected: monthlyReportTotals.gstCollected,
      smartPay: monthlyReportTotals.smartPay,
      cardTerminalIds: '',
      expenses: monthlyReportTotals.expenses,
      expenseGstClaimable: '—',
      expenseGstClaimableAmount: monthlyDashboardStats.gstClaimableTotal,
      expenseLinesDetail: '',
      reportMonth: context.reportMonth,
      completionFilter: context.completionFilter,
      columnFilters: context.columnFilters,
    });
    return rows;
  }, [
    filteredMonthlyReportRows,
    monthlyExpenseLinesByDate,
    monthlyReportTotals,
    monthlyDashboardStats.gstClaimableTotal,
    monthlyReportMonth,
    monthlyCompletionFilter,
    monthlyAppliedFilterSummary,
    monthlySmartPayByDate,
  ]);

  const monthlyExportColumns: ExportColumn<(typeof monthlyExportRows)[number]>[] = [
    { key: 'rowType', label: 'Row Type' },
    { key: 'date', label: 'Date' },
    { key: 'totalOrders', label: 'Total Orders' },
    { key: 'dineInOrders', label: 'Dine-in #' },
    { key: 'dineInAmount', label: 'Dine-in $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'takeawayOrders', label: 'Takeaway #' },
    { key: 'takeawayAmount', label: 'Takeaway $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'deliveryOrders', label: 'Delivery #' },
    { key: 'deliveryAmount', label: 'Delivery $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'uberDoordashOrders', label: 'Uber/DD #' },
    { key: 'uberDoordashAmount', label: 'Uber/DD $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'totalSales', label: 'Total Sales $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'cashSales', label: 'Cash $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'cardSales', label: 'Card $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'bankTransferSales', label: 'Bank Transfer $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'discount', label: 'Discount $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'gstCollected', label: 'GST $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'smartPay', label: 'Smart Pay $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'cardTerminalIds', label: 'Card Terminal IDs' },
    { key: 'expenses', label: 'Total Expenses $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'expenseGstClaimable', label: 'GST claimable lines' },
    { key: 'expenseGstClaimableAmount', label: 'GST Claimable $', formatter: (value) => Number(value ?? 0).toFixed(2) },
    { key: 'expenseLinesDetail', label: 'Expense lines (JSON)' },
    { key: 'reportMonth', label: 'Report Month' },
    { key: 'completionFilter', label: 'Completion Filter' },
    { key: 'columnFilters', label: 'Applied Column Filters' },
  ];

  const visibleMonthlyColumnCount = useMemo(
    () => Object.values(monthlyColumnVisibility).filter(Boolean).length,
    [monthlyColumnVisibility]
  );

  const openGstExpenseModal = useCallback((date: string) => {
    setGstExpenseModalDate(date);
  }, []);

  const closeGstExpenseModal = useCallback(() => {
    setGstExpenseModalDate(null);
  }, []);

  const saveGstExpenseModal = useCallback((date: string, lines: MonthlyReportExpenseLine[]) => {
    setMonthlyExpenseLinesByDate((prev) => {
      const next = { ...prev };
      if (lines.length === 0) {
        delete next[date];
      } else {
        next[date] = lines;
      }
      return next;
    });
    setGstExpenseModalDate(null);
  }, []);

  useEffect(() => {
    if (!isAdmin || !isExecutivePanelActive) return;
    let cancelled = false;
    (async () => {
      const weekStart = getWeekStartTimestamp(weekEnding);
      const weekEnd = new Date(weekEnding + 'T23:59:59').getTime();
      try {
        const roster = await rosterService.getRosterByWeek(weekStart);
        const manualEntries = await timesheetService.getByDateRange(weekStart, weekEnd);
        const rosterShifts: RosterShiftForReport[] = (roster?.shifts ?? []).map((s) => ({
          staffId: s.staffId,
          start: s.start,
          end: s.end,
          actualHours: s.actualHours,
          date: s.date,
          mealBreakStart: s.mealBreakStart,
          unpaidMealMinutes: s.unpaidMealMinutes,
        }));
        const manualForReport: TimesheetEntryForReport[] = manualEntries.map((e) => ({ staffId: e.staffId, startTime: e.startTime, endTime: e.endTime, actualHours: e.actualHours, date: e.date }));
        if (!cancelled) {
          const summary = getTimesheetLaborSummary(rosterShifts, manualForReport, staffMap);
          setFlashPnlTimesheetSummary(summary);
        }
      } catch (e) {
        if (!cancelled) setFlashPnlTimesheetSummary(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, isExecutivePanelActive, weekEnding, staffMap]);

  const loadFoodCostReport = useCallback(() => {
    setFoodCostReport(
      getTheoreticalVsActualFoodCostReport(foodCostStartDate, foodCostEndDate, ordersForReports, recipes, inventoryItems, menuItems)
    );
  }, [foodCostStartDate, foodCostEndDate, ordersForReports, recipes, inventoryItems, menuItems]);

  // Helper function to get date range
  const getDateRange = useCallback((startDate: string, endDate: string) => {
    return getDateRangeHelper(startDate, endDate);
  }, []);

  // --- Trends & Forecast (date range; default = current week Monday → Sunday) ---
  const trendsDateKeys = useMemo(() => {
    const keys: string[] = [];
    if (!trendsStartDate || !trendsEndDate) return keys;
    const tz = businessTimeZone;
    const start = new Date(trendsStartDate + 'T00:00:00');
    const end = new Date(trendsEndDate + 'T00:00:00');
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      keys.push(d.toLocaleDateString('en-CA', { timeZone: tz }));
    }
    return keys;
  }, [trendsStartDate, trendsEndDate, businessTimeZone]);

  const paidTrendDailyAggregates = useMemo(() => {
    const daily = new Map<string, { revenue: number; orderCount: number; hourCounts: Record<number, number> }>();
    ordersForReports.forEach((o) => {
      if (
        o.paymentStatus !== 'paid' ||
        o.status === 'cancelled' ||
        o.status === 'refunded' ||
        (o as Order & { isDeleted?: boolean }).isDeleted
      ) {
        return;
      }
      const dateKey = getOrderSaleDateKey(o);
      const existing = daily.get(dateKey) ?? { revenue: 0, orderCount: 0, hourCounts: {} };
      existing.revenue += getOrderTotalFromItems(o);
      existing.orderCount += 1;
      const ts = getOrderSaleTimestamp(o);
      const hour = new Date(ts).toLocaleString('en-NZ', { timeZone: businessTimeZone, hour: 'numeric', hour12: false });
      const h = parseInt(hour, 10);
      if (!Number.isNaN(h)) {
        existing.hourCounts[h] = (existing.hourCounts[h] || 0) + 1;
      }
      daily.set(dateKey, existing);
    });
    return daily;
  }, [ordersForReports, getOrderSaleDateKey, getOrderSaleTimestamp, businessTimeZone]);

  const lastSevenDaysData = useMemo(() => {
    return trendsDateKeys.map((dateKey) => {
      const aggregate = paidTrendDailyAggregates.get(dateKey);
      const hourCounts = aggregate?.hourCounts ?? {};
      const peakHour =
        Object.keys(hourCounts).length > 0
          ? Number(Object.entries(hourCounts).sort((a, b) => b[1] - a[1])[0][0])
          : null;
      return {
        dateKey,
        revenue: aggregate?.revenue ?? 0,
        orderCount: aggregate?.orderCount ?? 0,
        peakHour,
        hourCounts,
      };
    });
  }, [trendsDateKeys, paidTrendDailyAggregates]);

  const trendsAndForecast = useMemo(() => {
    const totalRevenue = lastSevenDaysData.reduce((s, d) => s + d.revenue, 0);
    const totalOrders = lastSevenDaysData.reduce((s, d) => s + d.orderCount, 0);
    const daysWithData = lastSevenDaysData.filter((d) => d.orderCount > 0).length;
    const avgDailyRevenue = daysWithData > 0 ? totalRevenue / daysWithData : 0;
    const avgOrdersPerDay = daysWithData > 0 ? totalOrders / daysWithData : 0;
    const busiest = lastSevenDaysData.reduce(
      (best, d) => (d.revenue > (best?.revenue ?? 0) ? d : best),
      lastSevenDaysData[0] ?? null,
    );
    const thisWeekRevenueForecast = avgDailyRevenue * 7;
    const thisWeekOrdersForecast = Math.round(avgOrdersPerDay * 7);
    const allHours: Record<number, number> = {};
    lastSevenDaysData.forEach((d) => {
      Object.entries(d.hourCounts).forEach(([hour, count]) => {
        const parsedHour = Number(hour);
        if (!Number.isNaN(parsedHour)) {
          allHours[parsedHour] = (allHours[parsedHour] || 0) + count;
        }
      });
    });
    const peakHourEntry =
      Object.keys(allHours).length > 0
        ? Object.entries(allHours).sort((a, b) => b[1] - a[1])[0]
        : null;
    const peakHour = peakHourEntry ? parseInt(peakHourEntry[0], 10) : null;
    const peakHourOrders = peakHourEntry ? peakHourEntry[1] : 0;
    const avgOrdersAtPeak = peakHour != null && lastSevenDaysData.length > 0 ? peakHourOrders / lastSevenDaysData.length : 0;
    return {
      totalRevenueLastWeek: totalRevenue,
      totalOrdersLastWeek: totalOrders,
      avgDailyRevenue,
      avgOrdersPerDay,
      daysWithData,
      busiest: busiest ?? null,
      thisWeekRevenueForecast,
      thisWeekOrdersForecast,
      peakHour,
      peakHourOrders,
      avgOrdersAtPeak,
    };
  }, [lastSevenDaysData]);

  const [closingSession, setClosingSession] = useState<RegisterSession | null>(null);
  const [closingFloat, setClosingFloat] = useState('');
  const [closingCash, setClosingCash] = useState('');
  const [closingCard, setClosingCard] = useState('');
  const [closingError, setClosingError] = useState<string | null>(null);
  const [closingLoading, setClosingLoading] = useState(false);
  const cashDenominationsTemplate = useMemo(() => [100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1], []);
  const [closingCashDenoms, setClosingCashDenoms] = useState<{ denom: number; count: string }[]>(
    cashDenominationsTemplate.map((d) => ({ denom: d, count: '' }))
  );
  const closingCashDenomTotal = useMemo(
    () =>
      closingCashDenoms.reduce((sum, d) => {
        const c = Number(d.count);
        if (!Number.isFinite(c) || c < 0) return sum;
        return sum + d.denom * c;
      }, 0),
    [closingCashDenoms],
  );
  const [showSalesByHourModal, setShowSalesByHourModal] = useState(false);
  const [showEventStallHourModal, setShowEventStallHourModal] = useState(false);
  const [showPendingOrdersModal, setShowPendingOrdersModal] = useState(false);
  const [cancellingPendingOrderId, setCancellingPendingOrderId] = useState<string | null>(null);
  const [pendingOrdersActionError, setPendingOrdersActionError] = useState<string | null>(null);

  // Admin cleanup for stale unpaid orders (abandoned tickets) that inflate Pending Order Revenue.
  const handleCancelPendingOrder = useCallback(async (order: Order) => {
    if (!isAdmin) return;
    if (!window.confirm(`Cancel unpaid order ${order.id} (${formatCurrency(getOrderTotalFromItems(order))})? It will be excluded from reports but kept for audit history.`)) {
      return;
    }
    setCancellingPendingOrderId(order.id);
    setPendingOrdersActionError(null);
    const cancelledAt = Date.now();
    try {
      const updates: Partial<Order> = { status: 'cancelled' };
      (updates as any).cancelledAt = cancelledAt;
      (updates as any).cancellationReason = 'Cleared from Reports dashboard (stale unpaid order)';
      await ordersService.update(order.id, updates);
      // Patch the one-shot home window copy immediately; the live listener reconciles shortly after.
      setHomeWindowOrders((prev) =>
        prev.map((o) => (o.id === order.id ? ({ ...o, ...updates } as Order) : o))
      );
      void auditLogsService.add({
        timestamp: cancelledAt,
        actorId: currentUser?.id,
        actorName: currentUser?.name,
        actorRole: currentUser?.isAdmin ? 'admin' : undefined,
        action: 'order.cancel_stale_unpaid',
        entityType: 'order',
        entityId: order.id,
        details: { total: getOrderTotalFromItems(order), orderType: order.orderType, orderTimestamp: order.timestamp },
      }).catch((err) => console.warn('Failed to write audit log for pending order cancel:', err));
    } catch (error) {
      console.error('Failed to cancel pending order:', error);
      setPendingOrdersActionError(`Failed to cancel order ${order.id}. Please try again.`);
    } finally {
      setCancellingPendingOrderId(null);
    }
  }, [isAdmin, currentUser]);

  const resetClosingForm = () => {
    setClosingSession(null);
    setClosingFloat('');
    setClosingCash('');
    setClosingCard('');
    setClosingError(null);
    setClosingLoading(false);
    setClosingCashDenoms(cashDenominationsTemplate.map((d) => ({ denom: d, count: '' })));
  };

  const openClosingModal = (session: RegisterSession) => {
    setClosingSession(session);
    setClosingFloat(String(session.openingFloat ?? ''));
    setClosingCash('');
    setClosingCard('');
    setClosingError(null);
    // Pre-fill denominations from session if present
    if (session.cashDenominations && session.cashDenominations.length) {
      const map = new Map(session.cashDenominations.map((d) => [d.denom, String(d.count)] as const));
      setClosingCashDenoms(cashDenominationsTemplate.map((d) => ({ denom: d, count: map.get(d) ?? '' })));
    } else {
      setClosingCashDenoms(cashDenominationsTemplate.map((d) => ({ denom: d, count: '' })));
    }
  };

  const handleCloseSession = useCallback(() => {
    if (!closingSession) return;
    const closingFloatNum = Number(closingFloat);
    const cashSalesNum = Number(closingCash);
    const cardSalesNum = Number(closingCard);

    if (Number.isNaN(closingFloatNum)) {
      setClosingError('Enter a valid closing float.');
      return;
    }

    if (Number.isNaN(cashSalesNum) || Number.isNaN(cardSalesNum)) {
      setClosingError('Provide cash and card sales.');
      return;
    }

    // Build cash denominations payload
    const denomsPayload = closingCashDenoms
      .map((d) => ({ denom: d.denom, count: Number(d.count) }))
      .filter((d) => !Number.isNaN(d.count) && d.count > 0);

    setClosingLoading(true);
    setClosingError(null);

    onRequireManagerPin('close', async () => {
      const variance = closingFloatNum - ((closingSession.openingFloat || 0) + cashSalesNum);
      try {
        await onForceCloseSession(closingSession.id, {
          closingFloat: closingFloatNum,
          cashSales: cashSalesNum,
          cardSales: cardSalesNum,
          variance,
          cashDenominations: denomsPayload,
        });
        resetClosingForm();
      } catch (error) {
        console.error('Failed to force close session:', error);
        setClosingError('Failed to close session. Try again.');
      } finally {
        setClosingLoading(false);
      }
    });
  }, [closingSession, closingFloat, closingCash, closingCard, closingCashDenoms, onForceCloseSession, onRequireManagerPin]);

  // Reload insights when the selected date range changes (after first load).
  useEffect(() => {
    if (!detailedReportsDataLoadedRef.current.insights) return;
    void processInsights();
  }, [salesStartDate, salesEndDate, processInsights]);

  // Dedicated Sales Insights page: load on mount (section is expanded by default).
  useEffect(() => {
    if (detailedSection !== 'sales-insights') return;
    ensureInsightsAndCouponLoaded();
  }, [detailedSection, ensureInsightsAndCouponLoaded]);

  useEffect(() => {
    if (!detailedReportsDataLoadedRef.current.registerLogs) return;
    void processRegisterLogs();
  }, [registerStartDate, registerEndDate, processRegisterLogs]);

  useEffect(() => {
    setSalesByInvoice([]);
    setSalesByInvoiceCursor(null);
    setSalesByInvoiceLastLoadedKey(null);
    setSalesByInvoiceError(null);
  }, [salesByInvoiceStartDate, salesByInvoiceEndDate]);

  const terminalOptions = useMemo(() => {
    const ids = new Set<string>();
    registerLogs.forEach((log) => {
      ids.add(log.terminalName || log.terminalId);
    });
    return Array.from(ids);
  }, [registerLogs]);

  const filteredRegisterLogs = useMemo(() => {
    if (selectedTerminal === 'all') return registerLogs;
    return registerLogs.filter((log) => (log.terminalName || log.terminalId) === selectedTerminal);
  }, [registerLogs, selectedTerminal]);

  const registerColumns: ExportColumn<RegisterLogRow>[] = [
    { key: 'date', label: 'Date' },
    { key: 'terminalName', label: 'Terminal', formatter: (_, row) => row.terminalName || row.terminalId },
    { key: 'cashierName', label: 'Cashier' },
    { key: 'status', label: 'Status', formatter: (_, row) => (row.isActive ? 'Open' : 'Closed') },
    { key: 'openingFloat', label: 'Opening Float', formatter: (value) => `$${Number(value).toFixed(2)}` },
    { key: 'closingFloat', label: 'Closing Float', formatter: (value) => (value !== undefined ? `$${Number(value).toFixed(2)}` : '—') },
    { key: 'cashSales', label: 'Cash Sales', formatter: (value) => `$${Number(value).toFixed(2)}` },
    { key: 'cardSales', label: 'Card Sales', formatter: (value) => `$${Number(value).toFixed(2)}` },
    { key: 'variance', label: 'Variance', formatter: (value) => (value !== undefined ? `$${Number(value).toFixed(2)}` : '—') },
    { key: 'startTime', label: 'Opened At', formatter: (value) => formatNzDateTime(value as number) },
    { key: 'endTime', label: 'Closed At', formatter: (value) => formatNzDateTime(value as number | undefined) },
    { key: 'lastReminderAt', label: 'Last Reminder', formatter: (value) => formatNzDateTime(value as number | undefined) },
    { key: 'durationHours', label: 'Duration (hrs)', formatter: (value, row) => (row.isActive ? `${formatHoursOpen(row.startTime)} (open)` : (value ? Number(value).toFixed(2) : '—')) },
  ];

  const salesColumns: ExportColumn<DailySalesRow>[] = [
    { key: 'date', label: 'Date' },
    { key: 'totalOrders', label: 'Total Orders' },
    { key: 'dineInOrders', label: 'Dine-in Orders' },
    { key: 'takeawayOrders', label: 'Takeaway Orders' },
    { key: 'deliveryOrders', label: 'Delivery Orders' },
    { key: 'grossSales', label: 'Gross Sales', formatter: (value) => formatCurrency(Number(value)) },
    { key: 'discounts', label: 'Discounts', formatter: (value) => formatCurrency(Number(value)) },
    // Net Sales is already the net / after-discount sales amount; keep label but format with helper.
    { key: 'netSales', label: 'Net Sales', formatter: (value) => formatCurrency(Number(value)) },
    { key: 'taxCollected', label: 'Tax Collected', formatter: (value) => formatCurrency(Number(value)) },
    { key: 'cashPayments', label: 'Cash Payments', formatter: (value) => formatCurrency(Number(value)) },
    { key: 'cardPayments', label: 'Card Payments', formatter: (value) => formatCurrency(Number(value)) },
    { key: 'bankTransferPayments', label: 'Bank Transfer', formatter: (value) => formatCurrency(Number(value)) },
  ];
  const paymentColumns: ExportColumn<PaymentBreakdownRow>[] = [
    { key: 'method', label: 'Method' },
    { key: 'amount', label: 'Amount', formatter: (value) => formatCurrency(Number(value)) },
    { key: 'orderCount', label: 'Orders' },
    { key: 'percentage', label: 'Share %', formatter: (value) => `${Number(value).toFixed(1)}%` },
  ];
  const categoryPerformanceColumns: ExportColumn<CategoryPerformanceRow>[] = [
    { key: 'categoryName', label: 'Category' },
    { key: 'itemsSold', label: 'Items Sold' },
    { key: 'grossSales', label: 'Gross Sales', formatter: (value) => formatCurrency(Number(value)) },
    { key: 'averageItemValue', label: 'Avg Item Value', formatter: (value) => formatCurrency(Number(value)) },
    { key: 'share', label: 'Sales Share', formatter: (value) => `${Number(value).toFixed(1)}%` },
  ];
  const topItemColumns: ExportColumn<TopItemRow>[] = [
    { key: 'name', label: 'Item' },
    { key: 'categoryName', label: 'Category' },
    { key: 'quantity', label: 'Qty Sold' },
    { key: 'grossSales', label: 'Gross Sales', formatter: (value) => formatCurrency(Number(value)) },
  ];
  const salesByInvoiceColumns: ExportColumn<SalesByInvoiceRow>[] = [
    { key: 'orderId', label: 'Invoice / Order ID' },
    { key: 'date', label: 'Date' },
    { key: 'dateTime', label: 'Date & Time' },
    { key: 'orderType', label: 'Type' },
    { key: 'totalAmount', label: 'Total', formatter: (v) => formatCurrency(Number(v)) },
    { key: 'cashAmount', label: 'Cash', formatter: (v) => formatCurrency(Number(v)) },
    { key: 'cardAmount', label: 'Card', formatter: (v) => formatCurrency(Number(v)) },
    { key: 'otherAmount', label: 'Other', formatter: (v) => formatCurrency(Number(v)) },
    { key: 'sessionId', label: 'Session ID', formatter: (v) => (v as string) || '—' },
    { key: 'terminalId', label: 'Terminal', formatter: (v) => (v as string) || '—' },
  ];
  const inventoryColumns: ExportColumn<{ name: string; stock: number; lowStockThreshold: number | undefined; unit: string; variance: number }>[]= [
    { key: 'name', label: 'Item' },
    { key: 'stock', label: 'On Hand' },
    { key: 'lowStockThreshold', label: 'Par Level', formatter: (value) => value ?? '—' },
    { key: 'variance', label: 'Diff', formatter: (value) => Number(value).toFixed(0) },
    { key: 'unit', label: 'Unit' },
  ];


  const renderDateInputs = (
    startDate: string,
    endDate: string,
    onStartChange: (value: string) => void,
    onEndChange: (value: string) => void,
  ) => (
    <div className="flex flex-wrap gap-4">
      <div className="flex flex-col">
        <label className="text-sm text-gray-600 mb-1">Start Date</label>
        <input
          type="date"
          value={startDate}
          max={endDate || undefined}
          onChange={(e) => onStartChange(e.target.value)}
          className="border border-gray-300 rounded px-3 py-2"
        />
      </div>
      <div className="flex flex-col">
        <label className="text-sm text-gray-600 mb-1">End Date</label>
        <input
          type="date"
          value={endDate}
          min={startDate || undefined}
          onChange={(e) => onEndChange(e.target.value)}
          className="border border-gray-300 rounded px-3 py-2"
        />
      </div>
    </div>
  );

  const renderDetailedView = () => (
    (() => {
      const showHome = detailedSection === 'home';
      const showSalesInsights = detailedSection === 'sales-insights';
      const showEventStall = detailedSection === 'event-stall';
      const showFinancial = detailedSection === 'financial';
      const showReconciliation = detailedSection === 'reconciliation';
      const showFoodCost = detailedSection === 'food-cost';
      const sectionTitle = showHome
        ? 'Detailed Reports'
        : showSalesInsights
          ? 'Sales Insights'
          : showEventStall
            ? 'Event / Stall sales'
          : showFinancial
            ? 'Financial & Payroll'
            : showReconciliation
              ? 'Sales Reconciliation'
              : 'Food Cost Report';
      return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0 border-b border-gray-100">
        <div className="max-w-full mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:py-0 sm:min-h-[4rem]">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800 truncate">{showHome ? 'Reports' : sectionTitle}</h1>
              {isVpsApiEnabled() ? (
                <LiveDataStatusBadge variant="inline" showWhenEmpty />
              ) : null}
              {!showHome && onNavigateToReportPage ? (
                <button
                  type="button"
                  onClick={() => onNavigateToReportPage('reports')}
                  className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 shadow-sm touch-manipulation min-h-[44px] sm:min-h-0 sm:py-1.5"
                >
                  Back
                </button>
              ) : null}
              {activeReportTab === 'detailed' && (externalStatementAppUrl || externalPayrollAppUrl) ? (
                <div className="flex items-center gap-2 flex-shrink-0">
                  {externalStatementAppUrl ? (
                    <button
                      type="button"
                      onClick={() => window.open(externalStatementAppUrl, '_blank', 'noopener,noreferrer')}
                      className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 shadow-sm"
                    >
                      Statement
                    </button>
                  ) : null}
                  {externalPayrollAppUrl ? (
                    <button
                      type="button"
                      onClick={() => window.open(externalPayrollAppUrl, '_blank', 'noopener,noreferrer')}
                      className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 shadow-sm"
                    >
                      Payroll
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            {isAdmin && showHome && (
              <div className="w-full sm:w-auto overflow-x-auto pb-1 -mx-1 px-1 sm:mx-0 sm:px-0">
                <div className="flex flex-nowrap gap-1 rounded-lg border border-gray-200 p-1 bg-gray-50 min-w-min">
                <button
                  type="button"
                  onClick={() => setActiveReportTab('detailed')}
                  className={`px-3 py-2.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap touch-manipulation ${activeReportTab === 'detailed' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Detailed
                </button>
                <button
                  type="button"
                  onClick={() => setActiveReportTab('executive')}
                  className={`px-3 py-2.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap touch-manipulation ${activeReportTab === 'executive' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Executive
                </button>
                <button
                  type="button"
                  onClick={() => setActiveReportTab('audit')}
                  className={`px-3 py-2.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap touch-manipulation ${activeReportTab === 'audit' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Audit
                </button>
                <button
                  type="button"
                  onClick={() => setActiveReportTab('expenses')}
                  className={`px-3 py-2.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap touch-manipulation ${activeReportTab === 'expenses' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Expenses
                </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1 p-3 sm:p-5 md:p-6 overflow-y-auto space-y-4 sm:space-y-6 overscroll-y-contain">
        {isAdmin && (
          <div
            className="rounded-lg border border-slate-200 bg-slate-50 px-3 sm:px-4 py-2.5 text-xs sm:text-sm text-slate-800 flex flex-wrap items-baseline gap-x-4 gap-y-1.5"
            role="status"
            aria-label="Connected Firebase backend"
          >
            <span className="font-semibold text-slate-600 shrink-0">Database</span>
            <span className="tabular-nums">
              <span className="text-slate-500">App env:</span>{' '}
              <span className="font-medium">{config.env}</span>
            </span>
            <span className="min-w-0">
              <span className="text-slate-500">Firebase project:</span>{' '}
              <code className="text-[11px] sm:text-xs bg-white px-1.5 py-0.5 rounded border border-slate-200 font-mono break-all">
                {config.firebase.projectId || '—'}
              </code>
            </span>
            {config.firebase.authDomain ? (
              <span className="text-slate-500 min-w-0 truncate max-w-full sm:max-w-md" title={config.firebase.authDomain}>
                Auth domain: <span className="font-mono text-slate-700">{config.firebase.authDomain}</span>
              </span>
            ) : null}
          </div>
        )}
        {showHome && activeReportTab === 'detailed' && (
          <div
            className="md:hidden sticky top-0 z-20 -mx-3 mb-1 px-3 py-2.5 bg-gradient-to-b from-white via-white/98 to-white/90 backdrop-blur-sm border-b border-gray-200/90 shadow-sm rounded-b-lg"
            aria-label="Summary at a glance"
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500">At a glance</span>
              <span className="text-xs text-gray-600 tabular-nums flex items-center gap-2">
                {reportSummaryDate}
                {useVpsSummaryForHeadline ? (
                  <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-800">
                    VPS
                  </span>
                ) : null}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-pink-50/90 px-1 py-2 border border-pink-100">
                <p className="text-[10px] font-medium text-pink-800/80">Revenue</p>
                <p className="text-base font-bold text-gray-900 tabular-nums leading-tight">{formatCurrency(totalRevenue)}</p>
              </div>
              <div className="rounded-lg bg-slate-50 px-1 py-2 border border-slate-200">
                <p className="text-[10px] font-medium text-slate-600">Orders</p>
                <p className="text-base font-bold text-gray-900 tabular-nums leading-tight">{totalOrders}</p>
              </div>
              <div className="rounded-lg bg-emerald-50/90 px-1 py-2 border border-emerald-100">
                <p className="text-[10px] font-medium text-emerald-800/80">AOV</p>
                <p className="text-base font-bold text-gray-900 tabular-nums leading-tight">{formatCurrency(averageOrderValue)}</p>
              </div>
            </div>
          </div>
        )}
        {showHome && activeReportTab === 'executive' && isAdmin && executiveMetrics && (
          <>
            <section className="bg-slate-800 text-white rounded-xl shadow-lg p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">Executive Dashboard</h2>
                <span className="text-xs text-slate-300">Real-time metrics (5-min delay max)</span>
              </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-xs text-slate-400">Sales velocity ($/hr)</p>
                <p className="text-2xl font-semibold">{formatCurrency(executiveMetrics.salesVelocityCurrent)}</p>
                <p className="text-xs mt-1">
                  vs same day last week: {formatCurrency(executiveMetrics.salesVelocityLastWeek)}
                  <span className={executiveMetrics.salesVelocityChangePercent >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                    {' '}({executiveMetrics.salesVelocityChangePercent >= 0 ? '+' : ''}{executiveMetrics.salesVelocityChangePercent.toFixed(1)}%)
                  </span>
                </p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-xs text-slate-400">Open tables / turn rate</p>
                <p className="text-2xl font-semibold">{executiveMetrics.openTableCount} / {executiveMetrics.totalTables}</p>
                <p className="text-xs mt-1">Turn rate: {executiveMetrics.turnRatePerHour.toFixed(2)}/hr</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-xs text-slate-400">Labor cost ratio</p>
                <p className="text-2xl font-semibold">{executiveMetrics.laborCostRatio != null ? `${(executiveMetrics.laborCostRatio * 100).toFixed(1)}%` : '—'}</p>
                <p className="text-xs mt-1 text-slate-400">{executiveMetrics.laborCostRatioLabel}</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-xs text-slate-400">Free or cancelled sales today</p>
                <p className="text-2xl font-semibold">{formatCurrency(executiveMetrics.voidCompTotalToday)}</p>
                <p className="text-xs mt-1">{executiveMetrics.voidCompEntries.length} entries</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-xs text-slate-400">Items not available</p>
                <p className="text-2xl font-semibold">{executiveMetrics.eightySixedItems.length}</p>
                {executiveMetrics.eightySixedItems.length > 0 && (
                  <p className="text-xs mt-1 text-amber-300">
                    {executiveMetrics.eightySixedItems.slice(0, 3).map((i) => i.name).join(', ')}
                    {executiveMetrics.eightySixedItems.length > 3 ? '…' : ''}
                  </p>
                )}
                <p className="text-xs mt-1 text-slate-400">Can&apos;t be ordered right now</p>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-300">Order SLA &amp; timing (Settings: Kitchen SLA)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400">Avg time to start (kitchen)</p>
                  <p className="text-xl font-semibold">
                    {executiveMetrics.orderSLA.averageMinutesToStart != null
                      ? `${executiveMetrics.orderSLA.averageMinutesToStart.toFixed(1)} min`
                      : '—'}
                  </p>
                  <p className="text-xs mt-1 text-slate-400">n={executiveMetrics.orderSLA.sampleCountToStart}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400">Avg prep (start → ready)</p>
                  <p className="text-xl font-semibold">
                    {executiveMetrics.orderSLA.averagePrepMinutes != null
                      ? `${executiveMetrics.orderSLA.averagePrepMinutes.toFixed(1)} min`
                      : '—'}
                  </p>
                  <p className="text-xs mt-1 text-slate-400">n={executiveMetrics.orderSLA.sampleCountPrep}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400">Avg serve (ready → served)</p>
                  <p className="text-xl font-semibold">
                    {executiveMetrics.orderSLA.averageServeMinutes != null
                      ? `${executiveMetrics.orderSLA.averageServeMinutes.toFixed(1)} min`
                      : '—'}
                  </p>
                  <p className="text-xs mt-1 text-slate-400">n={executiveMetrics.orderSLA.sampleCountServe}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-xs text-slate-400">Avg total (placed → served)</p>
                  <p className="text-xl font-semibold">
                    {executiveMetrics.orderSLA.averageTotalMinutes != null
                      ? `${executiveMetrics.orderSLA.averageTotalMinutes.toFixed(1)} min`
                      : '—'}
                  </p>
                  <p className="text-xs mt-1 text-slate-400">n={executiveMetrics.orderSLA.sampleCountTotal}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-red-900/30 rounded-lg p-4 border border-red-400/50">
                  <p className="text-xs font-semibold text-red-300">Delayed: not started in kitchen</p>
                  <p className="text-lg font-bold text-white mt-1">{executiveMetrics.orderSLA.delayedNotStarted.length}</p>
                  <p className="text-xs text-slate-400 mt-1">Order placed but kitchen hasn&apos;t started (past SLA)</p>
                  {executiveMetrics.orderSLA.delayedNotStarted.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-slate-200 max-h-24 overflow-y-auto">
                      {executiveMetrics.orderSLA.delayedNotStarted.map((d) => (
                        <li key={d.orderId}>{d.orderLabel} — {d.minutesWaiting.toFixed(1)} min (SLA {d.slaMinutes} min)</li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="bg-amber-900/30 rounded-lg p-4 border border-amber-400/50">
                  <p className="text-xs font-semibold text-amber-300">Delayed: preparing too long</p>
                  <p className="text-lg font-bold text-white mt-1">{executiveMetrics.orderSLA.delayedPreparing.length}</p>
                  <p className="text-xs text-slate-400 mt-1">Started in kitchen but past warning/critical</p>
                  {executiveMetrics.orderSLA.delayedPreparing.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-slate-200 max-h-24 overflow-y-auto">
                      {executiveMetrics.orderSLA.delayedPreparing.map((d) => (
                        <li key={d.orderId}>{d.orderLabel} — {d.minutesInPrep.toFixed(1)} min in prep</li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="bg-orange-900/30 rounded-lg p-4 border border-orange-400/50">
                  <p className="text-xs font-semibold text-orange-300">Delayed: ready, not served</p>
                  <p className="text-lg font-bold text-white mt-1">{executiveMetrics.orderSLA.delayedReadyNotServed.length}</p>
                  <p className="text-xs text-slate-400 mt-1">Ready for pickup but not served (past SLA)</p>
                  {executiveMetrics.orderSLA.delayedReadyNotServed.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-slate-200 max-h-24 overflow-y-auto">
                      {executiveMetrics.orderSLA.delayedReadyNotServed.map((d) => (
                        <li key={d.orderId}>{d.orderLabel} — {d.minutesWaiting.toFixed(1)} min (SLA {d.slaMinutes} min)</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-300 mb-2">Free or cancelled sales</h3>
                <p className="text-xs text-slate-400 mb-2">Shows the amount and which staff member did it.</p>
                {executiveMetrics.voidCompEntries.length === 0 ? (
                  <p className="text-sm text-slate-400">No free or cancelled sales today</p>
                ) : (
                  <ul className="space-y-1 max-h-40 overflow-y-auto text-sm">
                    {executiveMetrics.voidCompEntries.slice(0, 10).map((e, i) => (
                      <li key={`${e.orderId}-${e.timestamp}-${i}`} className="flex justify-between">
                        <span>{e.type === 'comp' ? 'Free' : e.type === 'void' ? 'Cancelled' : 'Discount'} · {e.orderId}</span>
                        <span>{formatCurrency(e.amount)} · {e.employeeName ?? (e.employeeId != null ? `ID ${e.employeeId}` : '—')}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-300 mb-2">Items not available</h3>
                <p className="text-xs text-slate-400 mb-2">Items that can&apos;t be ordered right now (sold out or turned off).</p>
                {executiveMetrics.eightySixedItems.length === 0 ? (
                  <p className="text-sm text-slate-400">No items unavailable</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {executiveMetrics.eightySixedItems.map((i) => (
                      <li key={i.id}>{i.name} <span className="text-amber-300">({i.reason === 'daily_limit' ? 'daily limit' : 'unavailable'})</span></li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            </section>

            {flashPnlReport && (
              <section className="bg-slate-800 text-white rounded-xl shadow-lg p-6 mt-6 border border-slate-600">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold">Flash P&L (Investor Format)</h2>
                    <p className="text-sm text-slate-400 mt-1">Week ending {flashPnlReport.weekEnding}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Revenue</p>
                      <p className="text-2xl font-bold">{formatCurrency(flashPnlReport.revenue)}</p>
                      {flashPnlReport.budget != null && (
                        <p className="text-sm text-slate-300 mt-2">
                          Budget: {formatCurrency(flashPnlReport.budget)}
                          <span className={flashPnlReport.variancePercent != null && flashPnlReport.variancePercent < 0 ? ' text-red-400' : ' text-emerald-400'}>
                            {' '}Variance: {flashPnlReport.variancePercent != null ? `${flashPnlReport.variancePercent >= 0 ? '+' : ''}${flashPnlReport.variancePercent.toFixed(1)}%` : '—'}
                          </span>
                        </p>
                      )}
                      <div className="mt-3 pt-3 border-t border-slate-600 flex gap-4 text-sm">
                        <span className="text-slate-400">Food: {formatCurrency(flashPnlReport.foodSales)}</span>
                        <span className="text-slate-400">Beverage: {formatCurrency(flashPnlReport.beverageSales)}</span>
                      </div>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Controllable Costs</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-300">COGS</span>
                          <span>{flashPnlReport.cogsPercent.toFixed(1)}%{flashPnlReport.cogsTargetPercent != null && ` (Target: ${flashPnlReport.cogsTargetPercent}%)`}</span>
                        </div>
                        {flashPnlReport.cogsVarianceImpact != null && (
                          <p className="text-slate-400 text-xs">Variance impact: {formatCurrency(flashPnlReport.cogsVarianceImpact)}</p>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t border-slate-600">
                          <span className="text-slate-300">Labor</span>
                          <span>{flashPnlReport.laborPercent.toFixed(1)}%{flashPnlReport.laborTargetPercent != null && ` (Target: ${flashPnlReport.laborTargetPercent}%)`}</span>
                        </div>
                        {flashPnlReport.overtimeCost > 0 && (
                          <p className="text-amber-400 text-xs">OT: {formatCurrency(flashPnlReport.overtimeCost)}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-emerald-900/30 rounded-lg p-4 border border-emerald-700/50">
                      <p className="text-xs font-semibold text-emerald-300 uppercase tracking-wide mb-1">Contribution Margin</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(flashPnlReport.contributionMargin)}</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Fixed Costs</p>
                      <p className="text-sm text-slate-300">Rent, Insurance, Salaries</p>
                      {flashPnlReport.fixedCostsTotal > 0 ? (
                        <p className="text-lg font-semibold mt-2">{formatCurrency(flashPnlReport.fixedCostsTotal)}</p>
                      ) : (
                        <p className="text-slate-500 text-sm mt-1">— Set in Settings → Flash P&L (Admin)</p>
                      )}
                    </div>
                    <div className={`rounded-lg p-4 border ${flashPnlReport.netOperatingIncome >= 0 ? 'bg-emerald-900/30 border-emerald-700/50' : 'bg-red-900/30 border-red-700/50'}`}>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Net Operating Income</p>
                      <p className="text-2xl font-bold">{formatCurrency(flashPnlReport.netOperatingIncome)}</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Cash Position</p>
                      <p className="text-xl font-semibold">
                        {flashPnlReport.cashPosition != null ? formatCurrency(flashPnlReport.cashPosition) : '—'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Bank balance · Set in Settings (Admin)</p>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {showHome && activeReportTab === 'audit' && isAdmin && (
          <section className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Audit Log (Admin)</h2>
                <p className="text-xs text-gray-500">
                  Register open/close, orders (placed, status, paid, cancelled, print), kitchen start, inventory. Data stored as JSON.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={auditLogStartDate}
                  onChange={(e) => setAuditLogStartDate(e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                />
                <input
                  type="date"
                  value={auditLogEndDate}
                  onChange={(e) => setAuditLogEndDate(e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                />
                <button
                  type="button"
                  onClick={loadAuditLog}
                  disabled={auditLogLoading}
                  className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {auditLogLoading ? 'Loading…' : 'Load audit log'}
                </button>
                {auditLogEntries && auditLogEntries.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const blob = new Blob([JSON.stringify(auditLogEntries, null, 2)], { type: 'application/json' });
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `audit-log-${auditLogStartDate}-to-${auditLogEndDate}.json`;
                      a.click();
                      URL.revokeObjectURL(a.href);
                    }}
                    className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Export JSON
                  </button>
                )}
                <input
                  type="text"
                  value={auditOrderFilter}
                  onChange={(e) => setAuditOrderFilter(e.target.value)}
                  placeholder="Filter by order ID or action"
                  className="border rounded px-2 py-1 text-sm min-w-[220px]"
                />
                <div className="inline-flex rounded border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setAuditViewMode('timeline')}
                    className={`px-2.5 py-1 text-xs font-semibold ${auditViewMode === 'timeline' ? 'bg-pink-500 text-white' : 'bg-white text-gray-700'}`}
                  >
                    Order timeline
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuditViewMode('table')}
                    className={`px-2.5 py-1 text-xs font-semibold ${auditViewMode === 'table' ? 'bg-pink-500 text-white' : 'bg-white text-gray-700'}`}
                  >
                    Raw table
                  </button>
                </div>
              </div>
            </div>
            {auditLogError && (
              <p className="text-sm text-red-600 mb-2">{auditLogError}</p>
            )}
            {filteredAuditEntries.length > 0 ? (
              auditViewMode === 'timeline' ? (
                <div className="mt-2 max-h-[28rem] overflow-y-auto space-y-3">
                  {auditOrderTimelineGroups.length === 0 ? (
                    <p className="text-sm text-gray-500 border border-gray-200 rounded-lg p-3">
                      No order-linked audit entries in this range/filter.
                    </p>
                  ) : auditOrderTimelineGroups.map((group) => (
                    <div key={group.orderId} className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-semibold text-gray-900">Order {group.orderId}</p>
                        <p className="text-xs text-gray-500">{group.entries.length} events</p>
                      </div>
                      <ul className="space-y-2">
                        {group.entries.map((entry) => (
                          <li key={entry.id} className="rounded border border-gray-100 bg-gray-50 p-2">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                              <span className="font-semibold text-gray-700">{formatNzDateTime(entry.timestamp)}</span>
                              <span className="text-gray-900">{AUDIT_ACTION_LABELS[entry.action] ?? entry.action}</span>
                              <span className="text-gray-600">
                                by {entry.actorName ?? (entry.actorId != null ? `ID ${entry.actorId}` : '—')}
                                {entry.actorRole ? ` (${entry.actorRole})` : ''}
                              </span>
                            </div>
                            <pre className="text-xs text-gray-600 mt-1 bg-white p-2 rounded overflow-x-auto whitespace-pre-wrap font-sans">
                              {formatAuditDetails(entry.details)}
                            </pre>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
              <div className="mt-2 max-h-[28rem] overflow-y-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Time</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Action</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Entity</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Actor</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Details (JSON)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredAuditEntries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50/80">
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {formatNzDateTime(entry.timestamp)}
                        </td>
                        <td className="px-3 py-2">
                          <span className="font-medium text-gray-900" title={entry.action}>
                            {AUDIT_ACTION_LABELS[entry.action] ?? entry.action}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {entry.entityType ?? '—'}
                          {entry.entityId ? ` · ${entry.entityId}` : ''}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {entry.actorName ?? (entry.actorId != null ? `ID ${entry.actorId}` : '—')}
                          {entry.actorRole ? ` (${entry.actorRole})` : ''}
                        </td>
                        <td className="px-3 py-2">
                          <pre className="text-xs text-gray-600 bg-gray-50 p-2 rounded overflow-x-auto max-w-md whitespace-pre-wrap font-sans">
                            {formatAuditDetails(entry.details)}
                          </pre>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )
            ) : (
              !auditLogLoading && (
                <p className="text-sm text-gray-500 mt-2">
                  No audit entries loaded yet. Choose a date range and click "Load audit log".
                </p>
              )
            )}
          </section>
        )}

        {showHome && activeReportTab === 'expenses' && isAdmin && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-5">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Daily Expenses</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Record expenses and attach receipts for GST, tax and bank reconciliation. Export to CSV.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">From</label>
                    <input
                      type="date"
                      value={expenseStartDate}
                      onChange={(e) => setExpenseStartDate(e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">To</label>
                    <input
                      type="date"
                      value={expenseEndDate}
                      onChange={(e) => setExpenseEndDate(e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={loadExpenses}
                    disabled={expenseLoading}
                    className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    {expenseLoading ? 'Loading…' : 'Load'}
                  </button>
                  {expenseList.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                          const headers = ['Date', 'Vendor', 'Type of transaction', 'Amount', 'Category', 'Receipt Note', 'Receipt URL', 'Created By', 'Created At'];
                        const rows = expenseList.map((e) => [
                          e.date,
                            (e.vendorName ?? '').replace(/"/g, '""'),
                          (e.description ?? '').replace(/"/g, '""'),
                          String(e.amount),
                          (e.category ?? '').replace(/"/g, '""'),
                          (e.receiptNote ?? '').replace(/"/g, '""'),
                          e.receiptUrl ?? '',
                          (e.createdByName ?? '').replace(/"/g, '""'),
                          e.createdAt ? new Date(e.createdAt).toISOString() : '',
                        ]);
                        const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(blob);
                        a.download = `daily-expenses-${expenseStartDate}-to-${expenseEndDate}.csv`;
                        a.click();
                        URL.revokeObjectURL(a.href);
                      }}
                      className="px-4 py-2 text-sm font-medium rounded-lg bg-pink-600 text-white hover:bg-pink-700 transition-colors"
                    >
                      Export CSV
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {expenseError && (
                <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">
                  {expenseError}
                </div>
              )}

              {/* Add expense form */}
              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-4">Add expense</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                    <input
                      type="date"
                      value={expenseForm.date}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, date: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Vendor name</label>
                    <input
                      type="text"
                      placeholder="e.g. Countdown, BP"
                      value={expenseForm.vendorName}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, vendorName: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Type of transaction</label>
                    <input
                      type="text"
                      placeholder="e.g. Cleaning, Fuel, Office supplies"
                      value={expenseForm.description}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, description: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={expenseForm.amount}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, amount: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                    <select
                      value={expenseForm.category}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, category: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500 bg-white"
                    >
                      {EXPENSE_CATEGORIES.map((cat) => (
                        <option key={cat || '_blank'} value={cat}>
                          {cat || '— Select category —'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Receipt note (optional)</label>
                    <input
                      type="text"
                      placeholder="e.g. Invoice #1234"
                      value={expenseForm.receiptNote}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, receiptNote: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Receipt file (optional)</label>
                    <label className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        className="sr-only"
                        onChange={(e) => setExpenseReceiptFile(e.target.files?.[0] ?? null)}
                      />
                      <span>{expenseReceiptFile ? expenseReceiptFile.name : 'Choose file'}</span>
                    </label>
                  </div>
                  <button
                    type="button"
                    disabled={
                      expenseSaving ||
                      !expenseForm.vendorName.trim() ||
                      !expenseForm.description.trim() ||
                      !expenseForm.amount.trim()
                    }
                    className="px-5 py-2.5 text-sm font-medium rounded-lg bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    onClick={async () => {
                      const amount = parseFloat(expenseForm.amount);
                      if (Number.isNaN(amount) || amount < 0) return;
                      setExpenseSaving(true);
                      setExpenseError(null);
                      try {
                        const created = await dailyExpensesService.create({
                          date: expenseForm.date,
                          vendorName: expenseForm.vendorName.trim() || undefined,
                          description: expenseForm.description.trim(),
                          amount,
                          category: expenseForm.category.trim() || undefined,
                          receiptNote: expenseForm.receiptNote.trim() || undefined,
                          createdAt: Date.now(),
                          createdBy: currentUser?.id,
                          createdByName: currentUser?.name ?? currentUser?.email ?? undefined,
                        });
                        if (expenseReceiptFile) {
                          const { url, fileName } = await uploadExpenseReceipt(created.id, expenseReceiptFile);
                          await dailyExpensesService.update(created.id, { receiptUrl: url, receiptFileName: fileName });
                        }
                        setExpenseForm({
                          date: expenseForm.date,
                          vendorName: '',
                          description: '',
                          amount: '',
                          category: '',
                          receiptNote: '',
                        });
                        setExpenseReceiptFile(null);
                        await loadExpenses();
                      } catch (err) {
                        console.error('Failed to add expense:', err);
                        setExpenseError('Failed to add expense. Please try again.');
                      } finally {
                        setExpenseSaving(false);
                      }
                    }}
                  >
                    {expenseSaving ? 'Saving…' : 'Add expense'}
                  </button>
                </div>
              </div>

              {/* Expense list */}
              {expenseList.length > 0 ? (
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="max-h-[28rem] overflow-y-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Description</th>
                          <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Category</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Receipt</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Created</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700 w-20">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {expenseList.map((e) => (
                          <tr key={e.id} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap text-gray-600">{e.date}</td>
                            <td className="px-4 py-3 text-gray-900">{e.description}</td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">${Number(e.amount).toFixed(2)}</td>
                            <td className="px-4 py-3">
                              <span className="text-gray-600">{e.category || '—'}</span>
                            </td>
                            <td className="px-4 py-3">
                              {e.receiptUrl ? (
                                <a href={e.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700 font-medium">
                                  View receipt
                                </a>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs">
                              {e.createdByName ?? '—'}
                              {e.createdAt ? ` · ${new Date(e.createdAt).toLocaleString()}` : ''}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                className="text-red-600 hover:text-red-700 text-sm font-medium"
                                onClick={async () => {
                                  if (!window.confirm('Delete this expense?')) return;
                                  try {
                                    await dailyExpensesService.remove(e.id);
                                    await loadExpenses();
                                  } catch (err) {
                                    console.error('Delete expense failed:', err);
                                    setExpenseError('Failed to delete expense.');
                                  }
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                !expenseLoading && (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center">
                    <p className="text-gray-500">No expenses in this range.</p>
                    <p className="text-sm text-gray-400 mt-1">Choose dates and click Load, or add an expense above.</p>
                  </div>
                )
              )}
            </div>
          </section>
        )}

        {activeReportTab === 'detailed' && (
        <>
        {dashboardBusy && (
          <section className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2">
            <div className="flex items-center justify-between text-xs text-blue-800 mb-1">
              <span>Loading dashboard data…</span>
              <span>{Math.round(dashboardProgressPercent)}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded bg-blue-100">
              <div
                className="h-full rounded bg-blue-500 transition-all duration-300 ease-out"
                style={{ width: `${Math.max(8, dashboardProgressPercent)}%` }}
              />
            </div>
            <div className="mt-1 flex items-center justify-between text-[11px] text-blue-700">
              <span>Elapsed: {formatWaitTime(dashboardElapsedMs)}</span>
              <span>
                {dashboardLoadDelayed
                  ? 'Taking longer than usual...'
                  : dashboardRemainingMs > 0
                    ? `ETA: ~${formatWaitTime(dashboardRemainingMs)}`
                    : 'Finalizing…'}
              </span>
            </div>
          </section>
        )}
        {showHome && homeWindowError && (
          <section className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {homeWindowError}
          </section>
        )}
        <section className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <label className="text-sm font-medium text-gray-600 shrink-0">Summary for:</label>
          <input
            type="date"
            value={reportSummaryDate}
            onChange={(e) => setReportSummaryDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm min-h-[44px] flex-1 min-w-[10rem] sm:min-w-0 sm:py-1.5 touch-manipulation"
          />
          <button
            type="button"
            onClick={() => setReportSummaryDate(formatInputDate(new Date()))}
            className="rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 min-h-[44px] sm:min-h-0 sm:py-1.5 touch-manipulation"
          >
            Today
          </button>
          {/* Manual sales + backup: desktop/tablet only — mobile quick view stays date + Today only */}
          <button
            type="button"
            onClick={() => {
              setManualSalesForm({
                date: reportSummaryDate,
                totalRevenue: manualOverride != null ? String(manualOverride.totalRevenue) : '',
                totalOrders: manualOverride != null ? String(manualOverride.totalOrders) : '',
                note: manualOverride?.note ?? '',
              });
              setManualSalesError(null);
              setManualSalesModalOpen(true);
            }}
            className="hidden sm:inline-flex rounded-lg bg-pink-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-pink-700 touch-manipulation items-center justify-center"
          >
            {manualOverride != null ? 'Edit manual sales' : 'Add manual sales'}
          </button>
          {manualOverride != null && (
            <>
              <span className="hidden sm:inline-flex rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Manual entry for this date</span>
              <span className="sm:hidden text-xs text-amber-800 font-medium">Manual override on</span>
            </>
          )}
          </div>
          <div className="hidden sm:flex flex-wrap items-center gap-2 border-t border-gray-200 pt-3 sm:border-t-0 sm:border-l sm:border-gray-200 sm:pl-4 sm:pt-0 sm:ml-auto">
            <button
              type="button"
              onClick={handleExportOrdersBackup}
              disabled={ordersBackupExportLoading}
              className="rounded bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              {ordersBackupExportLoading ? 'Exporting…' : 'Export orders backup (JSON)'}
            </button>
            {canImportOrdersBackup && (
              <>
                <label className="rounded bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 cursor-pointer">
                  <input
                    type="file"
                    accept=".json,.csv,application/json,text/csv"
                    className="sr-only"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      setOrdersBackupImportFile(f || null);
                      setOrdersBackupImportError(null);
                      setOrdersBackupImportSuccess(null);
                      e.target.value = '';
                    }}
                  />
                  Choose file
                </label>
                <button
                  type="button"
                  onClick={handleImportOrdersBackup}
                  disabled={ordersBackupImportLoading || !ordersBackupImportFile}
                  className="rounded bg-pink-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {ordersBackupImportLoading ? 'Importing…' : 'Import orders backup'}
                </button>
                {ordersBackupImportFile && (
                  <span className="text-xs text-gray-500 truncate max-w-[10rem]">{ordersBackupImportFile.name}</span>
                )}
              </>
            )}
          </div>
          {ordersBackupImportError && (
            <p className="hidden sm:block w-full text-sm text-red-600 mt-1">{ordersBackupImportError}</p>
          )}
          {ordersBackupImportSuccess && (
            <p className="hidden sm:block w-full text-sm text-green-600 mt-1">{ordersBackupImportSuccess}</p>
          )}
          <p className="w-full text-xs text-gray-500 mt-1 hidden sm:block">
            Export downloads full order data (JSON) for the selected date. Import: <strong>CSV</strong> (from close register backup) applies as manual sales override; <strong>JSON</strong> restores full orders (e.g. prod to dev).
          </p>
        </section>

        {(showHome) && <CollapsibleReportSection
          id="report-dashboard-overview"
          title="Dashboard overview"
          subtitle="KPI cards, top items, category view, and optional admin reconciliation (no extra server load to expand)."
          defaultOpen
          emphasizeExpandable={false}
          className="mb-6"
        >
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-6 mb-6">
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-sm font-medium text-gray-500">
              Total Revenue {reportSummaryDate === formatInputDate(new Date()) ? '(Today)' : `(${reportSummaryDate})`}
            </h3>
            {summaryCardsLoading ? (
              <>
                <p className="mt-1 text-3xl font-semibold text-gray-300 animate-pulse">—</p>
                <p className="mt-2 text-xs text-gray-400">Loading…</p>
              </>
            ) : (
            <>
            <p className="mt-1 text-3xl font-semibold text-gray-900">${totalRevenue.toFixed(2)}</p>
            <div className="mt-2 text-xs text-gray-500 space-y-0.5">
              <p>From orders: {formatCurrency(computedRevenue)}</p>
              {invoiceTotalForSummaryDate != null && (
                <p>
                  Sales by Invoice (this date): {formatCurrency(invoiceTotalForSummaryDate)}
                  {Math.abs(computedRevenue - invoiceTotalForSummaryDate) > 0.01 && (
                    <span className="ml-1 text-amber-600" title="Summary uses business timezone for the day; Sales by Invoice uses local date.">
                      (diff)
                    </span>
                  )}
                </p>
              )}
              {manualOverride != null && (
                <p className="text-amber-700">
                  Manual override: {formatCurrency(manualOverride.totalRevenue)} (displayed)
                </p>
              )}
            </div>
            </>
            )}
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-sm font-medium text-gray-500">
              Total Orders {reportSummaryDate === formatInputDate(new Date()) ? '(Today)' : `(${reportSummaryDate})`}
            </h3>
            {summaryCardsLoading ? (
              <>
                <p className="mt-1 text-3xl font-semibold text-gray-300 animate-pulse">—</p>
                <p className="mt-2 text-xs text-gray-400">Loading…</p>
              </>
            ) : (
            <>
            <p className="mt-1 text-3xl font-semibold text-gray-900">{totalOrders}</p>
            <div className="mt-2 text-xs text-gray-500 space-y-0.5">
              <p>From orders: {effectiveOrdersForSummaryDate.length}</p>
              {manualOverride != null && (
                <p className="text-amber-700">
                  Manual override: {manualOverride.totalOrders} (displayed)
                </p>
              )}
            </div>
            </>
            )}
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-sm font-medium text-gray-500">Average Order Value</h3>
            {summaryCardsLoading ? (
              <p className="mt-1 text-3xl font-semibold text-gray-300 animate-pulse">—</p>
            ) : (
              <p className="mt-1 text-3xl font-semibold text-gray-900">${averageOrderValue.toFixed(2)}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => !summaryCardsLoading && pendingOrdersForSummaryDate.length > 0 && setShowPendingOrdersModal(true)}
            className={`p-4 sm:p-6 rounded-xl shadow-sm border text-left w-full transition touch-manipulation ${!summaryCardsLoading && pendingOrdersForSummaryDate.length > 0 ? 'bg-amber-50 border-amber-200 cursor-pointer hover:bg-amber-100' : 'bg-white border-gray-100 cursor-default'}`}
            disabled={summaryCardsLoading || pendingOrdersForSummaryDate.length === 0}
          >
            <h3 className="text-sm font-medium text-gray-500">Pending Order Revenue</h3>
            {summaryCardsLoading ? (
              <>
                <p className="mt-1 text-3xl font-semibold text-gray-300 animate-pulse">—</p>
                <p className="text-xs text-gray-400 mt-1">Loading…</p>
              </>
            ) : (
              <>
                <p className="mt-1 text-3xl font-semibold text-amber-700">${totalPendingOrderRevenue.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {pendingOrdersForSummaryDate.length} unpaid order(s)
                  {pendingOrdersForSummaryDate.length > 0 && <span className="text-amber-700"> · tap to view</span>}
                </p>
              </>
            )}
          </button>
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-sm font-medium text-gray-500">Opening Float</h3>
            <p className="mt-1 text-3xl font-semibold text-gray-900">
              {formatCurrency(openingFloatTotal)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {openingFloatSessionCount === 0
                ? 'No registers opened'
                : `${openingFloatSessionCount} register${openingFloatSessionCount > 1 ? 's' : ''} opened`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (openRegisterCount > 0) {
                void refreshActiveRegisterSessions();
                setShowOpenRegisterModal(true);
              }
            }}
            className={`p-4 sm:p-6 rounded-xl shadow-sm border text-left w-full transition touch-manipulation ${openRegisterCount > 0 ? 'bg-red-50 border-red-200 cursor-pointer hover:bg-red-100' : 'bg-white border-gray-100 cursor-default'}`}
            disabled={openRegisterCount === 0}
          >
            <h3 className={`text-sm font-medium ${openRegisterCount > 0 ? 'text-red-700' : 'text-gray-500'}`}>Open Registers</h3>
            <p className={`mt-1 text-3xl font-semibold ${openRegisterCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>{openRegisterCount}</p>
            {openRegisterCount > 0 && (
              <p className="text-xs text-red-600 mt-1">Longest open: {longestOpenDuration.toFixed(1)} hrs</p>
            )}
          </button>
        </section>

        {isAdmin && (
          <section className="bg-white rounded-lg shadow p-6 border border-dashed border-gray-300">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Revenue reconciliation (debug – {reportSummaryDate})</h3>
            <p className="text-xs text-gray-500 mb-3">
              Compare summary total vs Sales by Invoice and manual overrides to detect overrides or potential double counting.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Summary total (card above)</p>
                <p className="font-semibold text-gray-900">{formatCurrency(totalRevenue)}</p>
                <p className="text-xs text-gray-500">
                  From {effectiveOrdersForSummaryDate.length} paid order(s){manualOverride && ' (overridden)'}.
                </p>
              </div>
              <div>
                <p className="text-gray-500">Sales by Invoice (this date)</p>
                <p className="font-semibold text-gray-900">
                  {invoiceTotalForSummaryDate != null ? formatCurrency(invoiceTotalForSummaryDate) : '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {invoiceTotalForSummaryDate != null
                    ? `${salesByInvoiceForSummaryDate.length} invoice row(s).`
                    : !salesByInvoiceStartDate || !salesByInvoiceEndDate
                      ? 'Choose a Sales by Invoice range and load (section below).'
                      : reportSummaryDate < salesByInvoiceStartDate || reportSummaryDate > salesByInvoiceEndDate
                        ? 'Summary date is outside the selected invoice range.'
                        : 'Load Sales by Invoice (below) to compare.'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Manual override for this date</p>
                <p className="font-semibold text-gray-900">
                  {manualOverride ? formatCurrency(manualOverride.totalRevenue) : 'None'}
                </p>
                {manualOverride && (
                  <p className="text-xs text-gray-500">
                    {manualOverride.totalOrders} order(s) · {manualOverride.note || 'no note'}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-gray-600">
              <div>
                <p className="font-semibold text-gray-700">
                  Orders in summary but not in Sales by Invoice ({extraOrdersInSummaryNotInInvoices.length})
                </p>
                {extraOrdersInSummaryNotInInvoices.length === 0 ? (
                  <p>No extra summary orders.</p>
                ) : (
                  <ul className="mt-1 max-h-32 overflow-y-auto list-disc list-inside">
                    {extraOrdersInSummaryNotInInvoices.slice(0, 10).map((o) => (
                      <li key={o.id}>
                        {o.id} · {o.orderType || '—'} · {formatCurrency(getOrderTotalFromItems(o))}
                      </li>
                    ))}
                    {extraOrdersInSummaryNotInInvoices.length > 10 && (
                      <li>+ {extraOrdersInSummaryNotInInvoices.length - 10} more…</li>
                    )}
                  </ul>
                )}
              </div>
              <div>
                <p className="font-semibold text-gray-700">
                  Invoices not in summary ({extraInvoicesNotInSummary.length})
                </p>
                {extraInvoicesNotInSummary.length === 0 ? (
                  <p>No extra invoices.</p>
                ) : (
                  <ul className="mt-1 max-h-32 overflow-y-auto list-disc list-inside">
                    {extraInvoicesNotInSummary.slice(0, 10).map((r) => (
                      <li key={r.orderId}>
                        {r.orderId} · {r.orderType || '—'} · {formatCurrency(r.totalAmount)}
                      </li>
                    ))}
                    {extraInvoicesNotInSummary.length > 10 && (
                      <li>+ {extraInvoicesNotInSummary.length - 10} more…</li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </section>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <button
            type="button"
            onClick={() => setShowSalesByHourModal(true)}
            className="bg-white p-6 rounded-lg shadow text-left hover:shadow-md hover:ring-1 hover:ring-pink-200 transition focus:outline-none focus:ring-2 focus:ring-pink-400"
          >
            <BarChart
              data={detailedSalesByHourChartData}
              title={`Sales by Hour (${reportSummaryDate})`}
            />
            <p className="mt-2 text-[11px] text-gray-400">Click to view full-size chart</p>
          </button>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">
              Top Selling Items {reportSummaryDate === formatInputDate(new Date()) ? '(Today)' : `(${reportSummaryDate})`}
            </h3>
            <ul>
              {topSellingItems.length > 0 ? topSellingItems.map(item => (
                <li key={item.name} className="flex justify-between py-2 border-b last:border-0">
                  <span className="text-gray-600">{item.name}</span>
                  <span className="font-semibold text-gray-800">{item.quantity} sold</span>
                </li>
              )) : (
                <p className="text-gray-500">
                  {reportSummaryDate === formatInputDate(new Date()) ? 'No sales yet today.' : `No order data for ${reportSummaryDate}. Add manual sales to backfill.`}
                </p>
              )}
            </ul>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">
              Category Performance {reportSummaryDate === formatInputDate(new Date()) ? '(Today)' : `(${reportSummaryDate})`}
            </h3>
            {categoryPerformanceForSummaryDate.length === 0 ? (
              <p className="text-gray-500 text-sm">
                {reportSummaryDate === formatInputDate(new Date())
                  ? 'No sales by category yet today.'
                  : `No category data for ${reportSummaryDate}.`}
              </p>
            ) : (
              <ul className="space-y-2 text-sm">
                {categoryPerformanceForSummaryDate.map((row, idx) => (
                  <li key={`${row.name}-${idx}`} className="flex justify-between items-baseline">
                    <div>
                      <p className="font-medium text-gray-800">{row.name}</p>
                      <p className="text-xs text-gray-500">
                        {row.itemsSold} item{row.itemsSold !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{formatCurrency(row.grossSales)}</p>
                      <p className="text-xs text-gray-500">{row.share.toFixed(1)}% of sales</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        </CollapsibleReportSection>}

        {isAdmin && showFinancial && (
          <CollapsibleReportSection
            id="report-financial-date-range"
            title="Income, expenses, salary & GST (date range)"
            subtitle="Choose one day (same From and To) or any period. POS income, monthly-report expenses, GST, and salary (payroll gross prorated to your selected dates)."
            emphasizeExpandable
            className="mb-6"
          >
            <ReportErrorBoundary title="Income, expenses, salary & GST">
              <Suspense
                fallback={
                  <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-6 text-sm text-gray-600">
                    Loading financial summary…
                  </div>
                }
              >
                <FinancialDateRangePanel />
              </Suspense>
            </ReportErrorBoundary>
          </CollapsibleReportSection>
        )}

        {showOpenRegisterModal && openRegisterSummaries.length > 0 && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="px-4 sm:px-6 py-3 border-b flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-red-700">Register attention needed</h3>
                  <p className="text-xs sm:text-sm text-red-600 mt-1">
                    These registers are still open because the Close Register flow was not completed. Please close them to avoid reconciliation issues.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowOpenRegisterModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-lg"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <div className="px-4 sm:px-6 py-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-red-700">
                    {openRegisterSummaries.length} open register{openRegisterSummaries.length > 1 ? 's' : ''}
                  </span>
                  <span className="text-xs text-gray-500">
                    Longest open: {longestOpenDuration.toFixed(1)} hrs
                  </span>
                </div>
                <div className="space-y-2">
                  {openRegisterSummaries.map((session) => (
                    <div
                      key={session.id}
                      className="bg-red-50 border border-red-100 rounded-md p-3 flex flex-wrap justify-between gap-2 items-start text-sm"
                    >
                      <div>
                        <p className="font-semibold text-gray-900">{session.terminal}</p>
                        <p className="text-gray-600">Cashier: {session.cashier}</p>
                        {session.openingFloat != null && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Opening float: {formatCurrency(session.openingFloat)}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-xs text-gray-600 space-y-1">
                        <p>Opened: {formatNzDateTime(session.openedAt)}</p>
                        <p>Open for {session.hoursOpen === '—' ? '—' : `${session.hoursOpen} hrs`}</p>
                        <p>
                          Last reminder:{' '}
                          {session.lastReminderAt ? formatNzDateTime(session.lastReminderAt) : 'No reminder sent'}
                        </p>
                        <button
                          onClick={() => {
                            const target = sessionsForReports.find((rs) => rs.id === session.id)
                              ?? hydratedActiveRegisterSessions.find((rs) => rs.id === session.id)
                              ?? activeRegisterSessions.find((rs) => rs.id === session.id)
                              ?? (currentRegisterSession?.id === session.id ? currentRegisterSession : null);
                            if (target) openClosingModal(target);
                          }}
                          className="mt-1 inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold text-white bg-pink-600 rounded-md hover:bg-pink-700"
                          disabled={
                            !sessionsForReports.some((rs) => rs.id === session.id) &&
                            !hydratedActiveRegisterSessions.some((rs) => rs.id === session.id) &&
                            currentRegisterSession?.id !== session.id
                          }
                        >
                          Close Session
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t border-red-100 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowOpenRegisterModal(false)}
                    className="px-4 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showPendingOrdersModal && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="px-4 sm:px-6 py-3 border-b flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-amber-700">Pending (unpaid) orders – {reportSummaryDate}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Open tickets that were never paid or cancelled. They are not counted as revenue, but they inflate
                    Pending Order Revenue until paid or cancelled.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPendingOrdersModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-lg"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <div className="px-4 sm:px-6 py-4 space-y-3">
                {pendingOrdersActionError && (
                  <p className="text-sm text-red-600">{pendingOrdersActionError}</p>
                )}
                {pendingOrdersForSummaryDate.length === 0 ? (
                  <p className="text-sm text-gray-500 py-6 text-center">No pending orders for this date.</p>
                ) : (
                  <div className="space-y-2">
                    {pendingOrdersForSummaryDate.map((order) => {
                      const itemSummary = (order.items ?? [])
                        .filter((i) => !i.removed)
                        .slice(0, 3)
                        .map((i) => `${i.quantity}× ${i.name}`)
                        .join(', ');
                      const extraItems = (order.items ?? []).filter((i) => !i.removed).length - 3;
                      return (
                        <div
                          key={order.id}
                          className="bg-amber-50 border border-amber-100 rounded-md p-3 flex flex-wrap justify-between gap-2 items-start text-sm"
                        >
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900">{order.id}</p>
                            <p className="text-gray-600">
                              {order.orderType || '—'}
                              {order.tableName ? ` · ${order.tableName}` : order.tableNumber != null ? ` · Table ${order.tableNumber}` : ''}
                              {order.customerName ? ` · ${order.customerName}` : ''}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[24rem]">
                              {itemSummary || 'No items'}
                              {extraItems > 0 && ` +${extraItems} more`}
                            </p>
                            <p className="text-xs text-gray-500">Created: {formatNzDateTime(order.timestamp)} · Status: {order.status}</p>
                          </div>
                          <div className="text-right space-y-1">
                            <p className="font-semibold text-amber-700">{formatCurrency(getOrderTotalFromItems(order))}</p>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => void handleCancelPendingOrder(order)}
                                disabled={cancellingPendingOrderId === order.id}
                                className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold text-white bg-rose-600 rounded-md hover:bg-rose-700 disabled:opacity-50"
                              >
                                {cancellingPendingOrderId === order.id ? 'Cancelling…' : 'Cancel order'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Cancelling marks the order as cancelled (kept for audit history) and removes it from Pending Order
                  Revenue. To take payment instead, open the order in POS.
                </p>
                <div className="pt-2 border-t border-amber-100 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowPendingOrdersModal(false)}
                    className="px-4 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showSalesByHourModal && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
              <div className="px-4 sm:px-6 py-3 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">
                  Sales by Hour & Order Type ({reportSummaryDate})
                </h3>
                <button
                  type="button"
                  onClick={() => setShowSalesByHourModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-lg"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <div className="px-4 sm:px-6 py-4">
                <BarChart
                  data={detailedSalesByHourChartData}
                  title={`Sales by Hour (${reportSummaryDate})`}
                  containerHeight={320}
                />
              </div>
            </div>
          </div>
        )}
        {showEventStallHourModal && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[92vh] overflow-y-auto">
              <div className="px-4 sm:px-6 py-3 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                <h3 className="text-lg font-semibold text-gray-800">Sales by category by hour</h3>
                <button
                  type="button"
                  onClick={() => setShowEventStallHourModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-lg"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <div className="px-4 sm:px-6 py-4">
                <Suspense
                  fallback={
                    <div className="rounded-xl bg-gray-50/80 p-4">
                      <div className="rounded-lg bg-white/70 border border-gray-200 animate-pulse" style={{ height: 420 }} />
                    </div>
                  }
                >
                  <EventStallCategoryByHourEChart
                    data={eventStallCategoryByHourChartData}
                    title={`Sales by category by hour (${salesStartDate} → ${salesEndDate})`}
                    containerHeight={420}
                    compactLegend
                  />
                </Suspense>
              </div>
            </div>
          </div>
        )}
        {closingSession && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-40 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto my-auto">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Close {closingSession.terminalName || closingSession.terminalId}</h3>
                <button
                  onClick={resetClosingForm}
                  className="text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  ✕
                </button>
              </div>
              <div className="px-6 py-4 space-y-4">
                <p className="text-sm text-gray-500">
                  Provide the ending floats and (optionally) cash breakdown to finalize this register. A manager PIN is required.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="text-sm text-gray-600">
                    Opening Float
                    <p className="mt-1 font-semibold text-gray-900">${(closingSession.openingFloat ?? 0).toFixed(2)}</p>
                  </div>
                  <label className="text-sm text-gray-600">
                    Closing Float
                    <input
                      type="number"
                      step="0.01"
                      value={closingFloat}
                      onChange={(e) => setClosingFloat(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </label>
                  <label className="text-sm text-gray-600">
                    Cash Sales
                    <input
                      type="number"
                      value={closingCash}
                      onChange={(e) => setClosingCash(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </label>
                  <label className="text-sm text-gray-600">
                    Card Sales
                    <input
                      type="number"
                      value={closingCard}
                      onChange={(e) => setClosingCard(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </label>
                  <div className="text-sm text-gray-600">
                    Variance: {closingFloat ? `$${(Number(closingFloat) - (closingSession.openingFloat || 0) - Number(closingCash || 0)).toFixed(2)}` : '—'}
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Cash denominations (optional)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                    {closingCashDenoms.map((d) => (
                      <label key={d.denom} className="flex items-center gap-1 text-gray-700">
                        <span className="w-16 inline-block">${d.denom.toFixed(2)}</span>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={d.count}
                          onChange={(e) =>
                            setClosingCashDenoms((prev) =>
                              prev.map((p) => (p.denom === d.denom ? { ...p, count: e.target.value } : p)),
                            )
                          }
                          className="w-16 border border-gray-300 rounded px-1 py-0.5 text-right"
                        />
                        <span className="text-gray-500">pcs</span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-600">
                    This helps reconcile cash in drawer vs recorded cash sales.
                  </p>
                  <p className="mt-2 text-sm font-semibold text-gray-900">
                    Total from denominations: {formatCurrency(closingCashDenomTotal)}
                  </p>
                  {closingFloat.trim() !== '' && Number.isFinite(Number(closingFloat)) ? (
                    <p className="text-xs text-gray-600">
                      Closing float − denomination total:{' '}
                      {formatCurrency(Number(closingFloat) - closingCashDenomTotal)}
                    </p>
                  ) : null}
                </div>
                {closingError && (
                  <p className="text-sm text-red-600">{closingError}</p>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button
                    type="button"
                    disabled={closingLoading || !closingSession}
                    onClick={() => {
                      if (!closingSession) return;
                      printRegisterCloseSummary({
                        session: closingSession,
                        closedByName:
                          (currentUser?.name && String(currentUser.name).trim()) ||
                          (currentUser?.email && String(currentUser.email).trim()) ||
                          undefined,
                        closingFloatStr: closingFloat,
                        closingCashStr: closingCash,
                        closingCardStr: closingCard,
                        cashDenoms: closingCashDenoms,
                        cashDenomTotal: closingCashDenomTotal,
                      });
                    }}
                    className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Print summary
                  </button>
                  <div className="flex gap-3">
                  <button
                    onClick={resetClosingForm}
                    className="px-4 py-2 rounded-md border border-gray-300 text-gray-700"
                    type="button"
                    disabled={closingLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCloseSession}
                    className="px-4 py-2 rounded-md bg-pink-600 text-white flex items-center gap-2 disabled:bg-gray-400"
                    type="button"
                    disabled={closingLoading}
                  >
                    {closingLoading ? 'Closing…' : 'Close Session'}
                  </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {manualSalesModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-40 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Manual sales for date</h3>
                <button
                  onClick={() => setManualSalesModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  ✕
                </button>
              </div>
              <div className="px-6 py-4 space-y-4">
                <p className="text-sm text-gray-500">
                  Enter total revenue and order count for a date (e.g. to backfill when orders are missing from the system).
                </p>
                <label className="block text-sm font-medium text-gray-700">
                  Date
                  <input
                    type="date"
                    value={manualSalesForm.date}
                    onChange={(e) => setManualSalesForm((f) => ({ ...f, date: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
                  />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Total revenue ($)
                  <input
                    type="text"
                    inputMode="decimal"
                    id="manual-sales-revenue"
                    value={manualSalesForm.totalRevenue}
                    {...manualRevenueField}
                    className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
                  />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Total orders
                  <input
                    type="text"
                    inputMode="numeric"
                    id="manual-sales-orders"
                    value={manualSalesForm.totalOrders}
                    {...manualOrdersField}
                    className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
                  />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Note (optional)
                  <input
                    type="text"
                    id="manual-sales-note"
                    value={manualSalesForm.note}
                    {...manualNoteField}
                    placeholder="e.g. Backfilled from paper records"
                    className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
                  />
                </label>
                {manualSalesError && <p className="text-sm text-red-600">{manualSalesError}</p>}
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => setManualSalesModalOpen(false)}
                    className="px-4 py-2 rounded-md border border-gray-300 text-gray-700"
                    type="button"
                    disabled={manualSalesSaving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      const rev = Number(manualSalesForm.totalRevenue);
                      const ord = Number(manualSalesForm.totalOrders);
                      if (!manualSalesForm.date) {
                        setManualSalesError('Please select a date.');
                        return;
                      }
                      if (Number.isNaN(rev) || rev < 0) {
                        setManualSalesError('Enter a valid total revenue.');
                        return;
                      }
                      if (Number.isNaN(ord) || ord < 0 || !Number.isInteger(ord)) {
                        setManualSalesError('Enter a valid whole number for total orders.');
                        return;
                      }
                      setManualSalesError(null);
                      setManualSalesSaving(true);
                      try {
                        // Ensure date is YYYY-MM-DD for Firestore document id
                        const dateKey = manualSalesForm.date.includes('-')
                          ? manualSalesForm.date
                          : (() => {
                              const [d, m, y] = manualSalesForm.date.split(/[/-]/);
                              return y && m && d ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : manualSalesForm.date;
                            })();
                        await manualSalesOverridesService.set({
                          date: dateKey,
                          totalRevenue: rev,
                          totalOrders: ord,
                          note: manualSalesForm.note || undefined,
                        });
                        const updated = await manualSalesOverridesService.get(dateKey);
                        setReportSummaryDate(dateKey);
                        setManualOverride(updated);
                        setManualSalesModalOpen(false);
                      } catch (e) {
                        console.error('Manual sales save failed:', e);
                        const msg = e && typeof e === 'object' && 'code' in e && (e as { code?: string }).code === 'permission-denied'
                          ? 'Permission denied. Ensure you are signed in and production Firestore rules are deployed (manual_sales_overrides).'
                          : 'Failed to save. Try again.';
                        setManualSalesError(msg);
                      } finally {
                        setManualSalesSaving(false);
                      }
                    }}
                    className="px-4 py-2 rounded-md bg-pink-600 text-white disabled:bg-gray-400"
                    type="button"
                    disabled={manualSalesSaving}
                  >
                    {manualSalesSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showHome && <CollapsibleReportSection
          id="report-trends"
          title="Trends & Forecast"
          subtitle={
            <>
              Compare revenue and orders (default: this week Mon–Sun). Current: {trendsStartDate} → {trendsEndDate}
            </>
          }
          defaultOpen
          emphasizeExpandable={false}
          className="mb-6"
        >
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="ml-auto w-full sm:w-auto">
              {renderDateInputs(trendsStartDate, trendsEndDate, setTrendsStartDate, setTrendsEndDate)}
            </div>
          </div>
          {trendsAndForecast.daysWithData === 0 ? (
            <p className="text-gray-500 py-4">
              No paid order data in the selected date range. Trends and forecast will appear once you have sales history
              for this period.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-sm text-slate-600">Revenue (selected range)</p>
                  <p className="text-2xl font-semibold text-slate-900 mt-1">
                    {formatCurrency(trendsAndForecast.totalRevenueLastWeek)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Avg {formatCurrency(trendsAndForecast.avgDailyRevenue)}/day with activity</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-sm text-slate-600">Orders (selected range)</p>
                  <p className="text-2xl font-semibold text-slate-900 mt-1">{trendsAndForecast.totalOrdersLastWeek}</p>
                  <p className="text-xs text-slate-500 mt-1">Avg {trendsAndForecast.avgOrdersPerDay.toFixed(1)}/day</p>
                </div>
                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                  <p className="text-sm text-indigo-700">This week forecast (revenue)</p>
                  <p className="text-2xl font-semibold text-indigo-900 mt-1">
                    {formatCurrency(trendsAndForecast.thisWeekRevenueForecast)}
                  </p>
                  <p className="text-xs text-indigo-600 mt-1">Based on average from selected range</p>
                </div>
                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                  <p className="text-sm text-indigo-700">This week forecast (orders)</p>
                  <p className="text-2xl font-semibold text-indigo-900 mt-1">{trendsAndForecast.thisWeekOrdersForecast}</p>
                  <p className="text-xs text-indigo-600 mt-1">Expected order volume</p>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-2">
                <div className="p-4 border border-gray-100 rounded-lg">
                  <h3 className="text-base font-semibold text-gray-800 mb-3">Daily trend (selected range)</h3>
                  <div className="space-y-2">
                    {lastSevenDaysData.map((d) => {
                      const maxRev = Math.max(...lastSevenDaysData.map((x) => x.revenue), 1);
                      const pct = maxRev > 0 ? (d.revenue / maxRev) * 100 : 0;
                      const dayLabel = (() => {
                        const [y, m, day] = d.dateKey.split('-');
                        const dt = new Date(Number(y), Number(m) - 1, Number(day));
                        return dt.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' });
                      })();
                      return (
                        <div key={d.dateKey} className="flex items-center gap-3">
                          <span className="text-sm text-gray-600 w-24 flex-shrink-0">{dayLabel}</span>
                          <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                            <div
                              className="h-full bg-pink-500 rounded min-w-[2px]"
                              style={{ width: `${Math.max(pct, 2)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-800 w-20 text-right">{formatCurrency(d.revenue)}</span>
                          <span className="text-xs text-gray-500 w-12 text-right">{d.orderCount} ord</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="p-4 border border-slate-100 rounded-lg bg-slate-50/30">
                  <h3 className="text-base font-semibold text-gray-800 mb-3">Range highlights</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    Peak hour over the selected range was{' '}
                    <strong>
                      {trendsAndForecast.peakHour != null
                        ? `${trendsAndForecast.peakHour}:00–${trendsAndForecast.peakHour + 1}:00`
                        : '—'}
                    </strong>
                    {trendsAndForecast.peakHourOrders > 0 && (
                      <> with ~{Math.round(trendsAndForecast.avgOrdersAtPeak)} orders/hour on average in that hour (aggregated over days).</>
                    )}
                  </p>
                  {trendsAndForecast.busiest && (
                    <p className="text-sm text-gray-600">
                      Busiest day: <strong>{trendsAndForecast.busiest.dateKey}</strong> —{' '}
                      {formatCurrency(trendsAndForecast.busiest.revenue)} ({trendsAndForecast.busiest.orderCount} orders).
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </CollapsibleReportSection>}
        {showHome && onNavigateToReportPage ? (
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">More reports</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
              <button type="button" onClick={() => onNavigateToReportPage('reports-sales-insights')} className="rounded-xl border border-gray-200 bg-gray-50 active:bg-gray-100 hover:bg-gray-100 px-4 py-3.5 sm:py-3 text-left min-h-[56px] touch-manipulation">
                <p className="font-medium text-gray-900">Sales Insights</p>
                <p className="text-xs text-gray-600 mt-1">Payments, categories, coupons</p>
              </button>
              <button type="button" onClick={() => onNavigateToReportPage('reports-event-stall')} className="rounded-xl border border-gray-200 bg-gray-50 active:bg-gray-100 hover:bg-gray-100 px-4 py-3.5 sm:py-3 text-left min-h-[56px] touch-manipulation">
                <p className="font-medium text-gray-900">Event / Stall sales</p>
                <p className="text-xs text-gray-600 mt-1">Event & legacy stall sales — categories, products & hourly chart</p>
              </button>
              <button type="button" onClick={() => onNavigateToReportPage('reports-monthly')} className="rounded-xl border border-gray-200 bg-gray-50 active:bg-gray-100 hover:bg-gray-100 px-4 py-3.5 sm:py-3 text-left min-h-[56px] touch-manipulation sm:col-span-2 lg:col-span-1">
                <p className="font-medium text-gray-900">Operating report</p>
                <p className="text-xs text-gray-600 mt-1">
                  Bank import and GST lines (cash wages), IRD-style GST summary, payroll comparison, daily sales table
                </p>
              </button>
              <button type="button" onClick={() => onNavigateToReportPage('reports-food-cost')} className="rounded-xl border border-gray-200 bg-gray-50 active:bg-gray-100 hover:bg-gray-100 px-4 py-3.5 sm:py-3 text-left min-h-[56px] touch-manipulation">
                <p className="font-medium text-gray-900">Food Cost Report</p>
                <p className="text-xs text-gray-600 mt-1">Theoretical vs actual COGS</p>
              </button>
            </div>
          </section>
        ) : null}
        {showEventStall && (
          <CollapsibleReportSection
            id="report-event-stall"
            title="Event / Stall sales"
            subtitle="Paid sales with order type Event, event operator / fulfillment data, or (legacy) ≥85% revenue from items on your current Event menu. Expand the date range to include older events."
            emphasizeExpandable
            className="mb-6"
          >
            <div className="flex flex-wrap items-center gap-4 mb-4">
              {renderDateInputs(salesStartDate, salesEndDate, setSalesStartDate, setSalesEndDate)}
              <div className="flex flex-col">
                <label className="text-sm text-gray-600 mb-1">Event filter</label>
                <select
                  value={eventReportFilter}
                  onChange={(e) => setEventReportFilter(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 min-w-[220px]"
                >
                  <option value="all">All events</option>
                  <option value="legacy-unassigned">Legacy / unassigned</option>
                  {activeEventId ? <option value="active">Current active event only</option> : null}
                  {eventProfiles.map((evt) => (
                    <option key={evt.id} value={evt.id}>
                      {evt.name} {evt.status === 'archived' ? '(archived)' : '(active)'}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setShowEventStallHourModal(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowEventStallHourModal(true);
                }
              }}
              className="mb-2 rounded-xl border border-dashed border-gray-200 bg-gray-50/30 cursor-pointer hover:border-pink-300 hover:bg-pink-50/20 transition-colors focus:outline-none focus:ring-2 focus:ring-pink-400 focus:ring-offset-2"
              aria-label="Open sales by category by hour chart full screen"
            >
              <Suspense
                fallback={
                  <div className="rounded-xl bg-gray-50/80 p-4">
                    <h3 className="text-base font-semibold text-gray-800 mb-3">Sales by category by hour</h3>
                    <div className="rounded-lg bg-white/70 border border-gray-200 animate-pulse" style={{ height: 260 }} />
                  </div>
                }
              >
                <EventStallCategoryByHourEChart
                  data={eventStallCategoryByHourChartData}
                  title="Sales by category by hour"
                  containerHeight={260}
                />
              </Suspense>
              <p className="text-center text-xs text-pink-600 pb-3 px-2">Click chart for large view</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              <div className="p-4 rounded-xl border border-gray-100 bg-white shadow-sm">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Orders</p>
                <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">{eventStallSalesReport.orderCount}</p>
              </div>
              <div className="p-4 rounded-xl border border-gray-100 bg-white shadow-sm">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Gross (inc GST)</p>
                <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">{formatCurrency(eventStallSalesReport.gross)}</p>
              </div>
              <div className="p-4 rounded-xl border border-gray-100 bg-white shadow-sm">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Net (ex GST)</p>
                <p className="text-2xl font-bold text-gray-900 mt-1 tabular-nums">{formatCurrency(eventStallSalesReport.net)}</p>
              </div>
            </div>
            <div className="rounded-xl border border-gray-100 overflow-hidden mb-6">
              <h3 className="text-base font-semibold text-gray-800 px-4 py-3 bg-gray-50 border-b border-gray-100">Sales by category</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50/80 text-gray-600">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Category</th>
                      <th className="text-right px-4 py-2 font-medium">Qty</th>
                      <th className="text-right px-4 py-2 font-medium">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventStallSalesReport.categoryRows.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                          No paid stall/event orders in this range.
                        </td>
                      </tr>
                    ) : (
                      eventStallSalesReport.categoryRows.map((row) => (
                        <tr key={row.category} className="border-t border-gray-100 hover:bg-gray-50/50">
                          <td className="px-4 py-2 font-medium text-gray-900">{row.category}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{row.qty}</td>
                          <td className="px-4 py-2 text-right tabular-nums font-semibold text-gray-900">{formatCurrency(row.revenue)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="rounded-xl border border-gray-100 overflow-hidden">
              <h3 className="text-base font-semibold text-gray-800 px-4 py-3 bg-gray-50 border-b border-gray-100">Sales by product</h3>
              <p className="text-xs text-gray-500 px-4 py-2 bg-white border-b border-gray-100">
                Unit costs come from Settings → Event/Stall (ex GST). Revenue from orders is GST-inclusive. Profit (revenue − total cost)
                mixes those bases—it is indicative unless you compare using net (ex-GST) revenue. Rows aggregate by menu item id so renames do not split history.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50/80 text-gray-600">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Product</th>
                      <th className="text-right px-4 py-2 font-medium">Qty</th>
                      <th className="text-right px-4 py-2 font-medium">Revenue</th>
                      <th className="text-right px-4 py-2 font-medium">Unit cost</th>
                      <th className="text-right px-4 py-2 font-medium">Total cost</th>
                      <th className="text-right px-4 py-2 font-medium">Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventStallSalesReport.productRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                          No line items in this range.
                        </td>
                      </tr>
                    ) : (
                      <>
                        {eventStallSalesReport.productRows.map((row, idx) => (
                          <tr
                            key={`${row.menuItemId ?? 'x'}-${idx}-${row.name}`}
                            className="border-t border-gray-100 hover:bg-gray-50/50"
                          >
                            <td className="px-4 py-2 font-medium text-gray-900">{row.name}</td>
                            <td className="px-4 py-2 text-right tabular-nums">{row.qty}</td>
                            <td className="px-4 py-2 text-right tabular-nums font-semibold text-gray-900">{formatCurrency(row.revenue)}</td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-800">
                              {row.unitCost != null ? (
                                <span>{formatCurrency(row.unitCost)}</span>
                              ) : row.menuItemId != null && canManageEventCosts ? (
                                <div className="flex items-center justify-end gap-1">
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={eventCostDraftById[String(row.menuItemId)] ?? ''}
                                    onChange={(e) =>
                                      setEventCostDraftById((prev) => ({
                                        ...prev,
                                        [String(row.menuItemId)]: e.target.value,
                                      }))
                                    }
                                    placeholder="cost"
                                    className="w-20 border border-gray-300 rounded px-2 py-1 text-xs text-right"
                                  />
                                  <button
                                    type="button"
                                    disabled={eventCostSavingById[String(row.menuItemId)]}
                                    onClick={() =>
                                      void saveFallbackEventItemCost(
                                        row.menuItemId!,
                                        eventCostDraftById[String(row.menuItemId)] ?? ''
                                      )
                                    }
                                    className="px-2 py-1 text-xs rounded bg-pink-500 text-white hover:bg-pink-600 disabled:opacity-50"
                                  >
                                    Save
                                  </button>
                                </div>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-800">
                              {row.unitCost != null ? formatCurrency(row.cost) : '—'}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                              {row.profit != null ? formatCurrency(row.profit) : '—'}
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-gray-200 bg-gray-50/80 font-semibold">
                          <td className="px-4 py-2 text-gray-900">Totals (products)</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                            {eventStallSalesReport.productRows.reduce((s, r) => s + r.qty, 0)}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                            {formatCurrency(eventStallSalesReport.productRevenueTotal)}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-400">—</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                            {eventStallSalesReport.productRows.some((r) => r.unitCost != null)
                              ? formatCurrency(eventStallSalesReport.productCostTotal)
                              : '—'}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                            {eventStallSalesReport.productRows.some((r) => r.profit != null)
                              ? formatCurrency(eventStallSalesReport.productProfitTotal)
                              : '—'}
                          </td>
                        </tr>
                        {eventStallSalesReport.productRevenueTotal > 0 &&
                        eventStallSalesReport.productMarginPercent != null &&
                        Number.isFinite(eventStallSalesReport.productMarginPercent) ? (
                          <tr className="bg-gray-50/50 text-xs text-gray-600">
                            <td className="px-4 py-2" colSpan={5}>
                              Margin on totals (profit ÷ revenue)
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums font-medium text-gray-800">
                              {eventStallSalesReport.productMarginPercent.toFixed(1)}%
                            </td>
                          </tr>
                        ) : null}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </CollapsibleReportSection>
        )}
        {showSalesInsights && <CollapsibleReportSection
          id="report-sales-insights"
          title="Sales Insights"
          subtitle="Payment and category performance for the selected dates. Change dates and click Load insights to refresh."
          defaultOpen={showSalesInsights}
          emphasizeExpandable={!showSalesInsights}
          onFirstExpand={ensureInsightsAndCouponLoaded}
          className="mb-6"
        >
          <div className="flex flex-wrap items-center gap-4 mb-4">
            {renderDateInputs(salesStartDate, salesEndDate, setSalesStartDate, setSalesEndDate)}
            <button
              type="button"
              onClick={() => void processInsights()}
              disabled={insightsLoading}
              className="px-3 py-2 text-sm rounded border border-pink-600 bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-50 shrink-0"
            >
              {insightsLoading ? 'Loading…' : 'Load insights'}
            </button>
            <div className="flex flex-wrap gap-2 ml-auto">
              <button
                onClick={() => salesInsights && exportToCSV('payment-breakdown', paymentColumns, salesInsights.payments)}
                className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                disabled={!salesInsights || !salesInsights.payments.length}
              >
                Payments CSV
              </button>
              <button
                onClick={() => salesInsights && exportToCSV('category-performance', categoryPerformanceColumns, salesInsights.categoryPerformance)}
                className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                disabled={!salesInsights || !salesInsights.categoryPerformance.length}
              >
                Categories CSV
              </button>
              <button
                onClick={() => salesInsights && exportToCSV('top-items', topItemColumns, salesInsights.topItems)}
                className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                disabled={!salesInsights || !salesInsights.topItems.length}
              >
                Top Items CSV
              </button>
              <button
                onClick={() =>
                  salesInsights &&
                  exportToCSV(
                    `all-items-${salesStartDate}-to-${salesEndDate}`,
                    topItemColumns,
                    salesInsights.allItems,
                  )
                }
                className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                disabled={!salesInsights || !salesInsights.allItems.length}
              >
                All Items & Qty CSV
              </button>
            </div>
          </div>
          {insightsLoading ? (
            <p className="text-gray-500">Loading insights...</p>
          ) : insightsError ? (
            <p className="text-red-500">{insightsError}</p>
          ) : !salesInsights ? (
            <p className="text-gray-500">No paid orders for the selected range.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-sm text-gray-500">Net Sales</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">{formatCurrency(salesInsights.summary.netSales)}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-sm text-gray-500">Avg. Order Value</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">{formatCurrency(salesInsights.summary.averageOrderValue)}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-sm text-gray-500">Items Sold</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">{salesInsights.summary.itemsSold}</p>
                  <p className="text-xs text-gray-500 mt-1">{salesInsights.summary.averageItemsPerOrder.toFixed(1)} items / order</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
                  <p className="text-sm text-yellow-700">Discounts Applied</p>
                  <p className="text-2xl font-semibold text-yellow-900 mt-1">{formatCurrency(salesInsights.discountSummary.totalDiscount)}</p>
                  <p className="text-xs text-yellow-700 mt-1">{salesInsights.discountSummary.ordersWithDiscount} orders with discounts</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="p-4 border border-gray-100 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">Payment Breakdown</h3>
                    <span className="text-xs text-gray-500">{salesInsights.payments.reduce((sum, row) => sum + row.orderCount, 0)} payment records</span>
                  </div>
                  {salesInsights.payments.length === 0 ? (
                    <p className="text-sm text-gray-500">No payments recorded for this range.</p>
                  ) : (
                    <ul className="space-y-3">
                      {salesInsights.payments.map((row) => (
                        <li key={row.method}>
                          <div className="flex justify-between text-sm font-medium text-gray-800">
                            <span>{row.method}</span>
                            <span>{formatCurrency(row.amount)} ({row.percentage.toFixed(1)}%)</span>
                          </div>
                          <div className="h-2 rounded-full bg-gray-200 mt-1">
                            <div
                              className="h-full rounded-full bg-pink-500"
                              style={{ width: `${row.percentage}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{row.orderCount} orders</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="p-4 border border-gray-100 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Discount Governance</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-3 rounded-lg bg-gray-50">
                      <p className="text-gray-500">Manual Discounts</p>
                      <p className="text-xl font-semibold text-gray-900">{formatCurrency(salesInsights.discountSummary.manualDiscount)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-50">
                      <p className="text-gray-500">Loyalty / Rewards</p>
                      <p className="text-xl font-semibold text-gray-900">{formatCurrency(salesInsights.discountSummary.rewardDiscount)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-50">
                      <p className="text-gray-500">Coupon Discounts</p>
                      <p className="text-xl font-semibold text-gray-900">{formatCurrency(salesInsights.discountSummary.couponDiscount)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-50">
                      <p className="text-gray-500">Resident Discounts</p>
                      <p className="text-xl font-semibold text-gray-900">{formatCurrency(salesInsights.discountSummary.residentDiscount)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                      <p className="text-amber-700 text-sm font-semibold">Approvals Required</p>
                      <p className="text-2xl font-bold text-amber-900">{salesInsights.discountSummary.approvalsRequired}</p>
                      <p className="text-xs text-amber-700 mt-1">{salesInsights.discountSummary.approvalsCompleted} approved · {salesInsights.discountSummary.approvalsPending} pending</p>
                    </div>
                    <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                      <p className="text-emerald-700 text-sm font-semibold">Orders Discounted</p>
                      <p className="text-2xl font-bold text-emerald-900">{salesInsights.discountSummary.ordersWithDiscount}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="border border-gray-100 rounded-lg">
                  <div className="flex items-center justify-between px-4 py-3 border-b">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">Category Performance</h3>
                      <p className="text-xs text-gray-500">Top categories by gross sales</p>
                    </div>
                    <button
                      onClick={() => exportToExcel('category-performance', categoryPerformanceColumns, salesInsights.categoryPerformance)}
                      className="text-xs px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Export XLSX
                    </button>
                  </div>
                  {salesInsights.categoryPerformance.length === 0 ? (
                    <p className="text-sm text-gray-500 p-4">No category data available.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left font-semibold text-gray-600">Category</th>
                            <th className="px-4 py-2 text-right font-semibold text-gray-600">Items</th>
                            <th className="px-4 py-2 text-right font-semibold text-gray-600">Gross</th>
                            <th className="px-4 py-2 text-right font-semibold text-gray-600">Share</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {salesInsights.categoryPerformance.slice(0, 8).map((row, idx) => (
                            <tr key={`cat-${idx}-${row.categoryId ?? row.categoryName}`}>
                              <td className="px-4 py-2 text-gray-800">{row.categoryName}</td>
                              <td className="px-4 py-2 text-right">{row.itemsSold}</td>
                              <td className="px-4 py-2 text-right">{formatCurrency(row.grossSales)}</td>
                              <td className="px-4 py-2 text-right">{row.share.toFixed(1)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="border border-gray-100 rounded-lg">
                  <div className="flex items-center justify-between px-4 py-3 border-b">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">Top Items</h3>
                      <p className="text-xs text-gray-500">Best sellers by revenue</p>
                    </div>
                    <button
                      onClick={() => exportToPDF('top-items', 'Top Items', topItemColumns, salesInsights.topItems)}
                      className="text-xs px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Export PDF
                    </button>
                  </div>
                  {salesInsights.topItems.length === 0 ? (
                    <p className="text-sm text-gray-500 p-4">No item sales for this range.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left font-semibold text-gray-600">Item</th>
                            <th className="px-4 py-2 text-left font-semibold text-gray-600">Category</th>
                            <th className="px-4 py-2 text-right font-semibold text-gray-600">Qty</th>
                            <th className="px-4 py-2 text-right font-semibold text-gray-600">Gross</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {salesInsights.topItems.map((row) => (
                            <tr key={row.itemId}>
                              <td className="px-4 py-2 text-gray-800">{row.name}</td>
                              <td className="px-4 py-2 text-gray-500">{row.categoryName}</td>
                              <td className="px-4 py-2 text-right">{row.quantity}</td>
                              <td className="px-4 py-2 text-right">{formatCurrency(row.grossSales)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </CollapsibleReportSection>}

        {showSalesInsights && <CollapsibleReportSection
          id="report-coupon"
          title="Coupon Campaign Dashboard"
          subtitle={
            <>Same range as Sales Insights above ({salesStartDate} → {salesEndDate}). Expanding either section loads both; use &quot;Load insights&quot; there to refresh.</>
          }
          emphasizeExpandable
          onFirstExpand={ensureInsightsAndCouponLoaded}
          className="mb-6"
        >
          {canManageCouponCampaigns && onNavigateToCouponCampaigns && (
            <div className="flex flex-wrap items-center justify-end gap-2 mb-3">
              <button
                type="button"
                onClick={() => onNavigateToCouponCampaigns()}
                className="px-2.5 py-1.5 text-xs rounded-md bg-indigo-700 text-white hover:bg-indigo-800 shrink-0"
              >
                Campaign manager
              </button>
            </div>
          )}
          {couponDashboard ? (
            <div className="border border-indigo-100 rounded-lg bg-indigo-50/50 p-3">
              <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                <div className="rounded bg-white border border-indigo-100 px-2 py-1.5">
                  <p className="text-[10px] text-indigo-700">Coupon revenue</p>
                  <p className="text-sm font-semibold text-indigo-900">{formatCurrency(couponDashboard.totalRevenueFromCoupons)}</p>
                </div>
                <div className="rounded bg-white border border-indigo-100 px-2 py-1.5">
                  <p className="text-[10px] text-indigo-700">Redemptions</p>
                  <p className="text-sm font-semibold text-indigo-900">{couponDashboard.recentRedemptions.length}</p>
                </div>
                <div className="rounded bg-white border border-indigo-100 px-2 py-1.5">
                  <p className="text-[10px] text-indigo-700">Campaigns</p>
                  <p className="text-sm font-semibold text-indigo-900">{couponDashboard.campaigns.length}</p>
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto overflow-x-auto bg-white rounded border border-indigo-100">
                <table className="min-w-full text-xs">
                  <thead className="bg-indigo-100/60 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">Campaign</th>
                      <th className="px-2 py-1 text-right">Total</th>
                      <th className="px-2 py-1 text-right">Used</th>
                      <th className="px-2 py-1 text-right">Rem.</th>
                      <th className="px-2 py-1 text-right">Exp.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {couponDashboard.campaigns.map((campaign) => (
                      <tr
                        key={campaign.campaignId}
                        className={`border-t border-indigo-50 ${
                          canManageCouponCampaigns && onNavigateToCouponCampaigns
                            ? 'cursor-pointer hover:bg-indigo-50/80'
                            : ''
                        }`}
                        onClick={() => {
                          if (canManageCouponCampaigns && onNavigateToCouponCampaigns) {
                            onNavigateToCouponCampaigns(campaign.campaignId);
                          }
                        }}
                      >
                        <td className="px-2 py-1">
                          {campaign.campaignName}
                          {campaign.campaignKind === 'building_unit' && (
                            <span className="ml-1 text-[10px] text-indigo-600">(Building)</span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-right">{campaign.totalCoupons}</td>
                        <td className="px-2 py-1 text-right">{campaign.used}</td>
                        <td className="px-2 py-1 text-right">{campaign.remaining}</td>
                        <td className="px-2 py-1 text-right">{campaign.expired}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {couponDashboard.buildingUnits && (
                <div className="mt-3 space-y-2">
                  <p className="text-[11px] font-semibold text-indigo-900">Building unit cards</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-[10px]">
                    <div className="rounded bg-white border border-indigo-100 px-2 py-1">
                      <p className="text-indigo-700">Hotel Wing (HW)</p>
                      <p className="font-semibold">{couponDashboard.buildingUnits.hotelWingIssued}</p>
                    </div>
                    <div className="rounded bg-white border border-indigo-100 px-2 py-1">
                      <p className="text-indigo-700">Hotel Wing used</p>
                      <p className="font-semibold">{couponDashboard.buildingUnits.hotelWingEverUsed}</p>
                    </div>
                    <div className="rounded bg-white border border-indigo-100 px-2 py-1">
                      <p className="text-indigo-700">Tower Wing (TW)</p>
                      <p className="font-semibold">{couponDashboard.buildingUnits.towerWingIssued}</p>
                    </div>
                    <div className="rounded bg-white border border-indigo-100 px-2 py-1">
                      <p className="text-indigo-700">Tower Wing used</p>
                      <p className="font-semibold">{couponDashboard.buildingUnits.towerWingEverUsed}</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-indigo-800">
                    Never used: {couponDashboard.buildingUnits.neverUsed} · Discount in range:{' '}
                    {formatCurrency(couponDashboard.buildingUnits.totalDiscountInRange)}
                  </p>
                  {couponDashboard.buildingUnits.perUnit.length > 0 && (
                    <div className="max-h-32 overflow-y-auto bg-white rounded border border-indigo-100">
                      <table className="min-w-full text-xs">
                        <thead className="bg-indigo-100/60 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left">Unit</th>
                            <th className="px-2 py-1 text-right">Uses</th>
                            <th className="px-2 py-1 text-right">Total off</th>
                          </tr>
                        </thead>
                        <tbody>
                          {couponDashboard.buildingUnits.perUnit.slice(0, 15).map((row) => (
                            <tr key={row.unitLabel} className="border-t border-indigo-50">
                              <td className="px-2 py-1">{row.unitLabel}</td>
                              <td className="px-2 py-1 text-right">{row.redemptionCount}</td>
                              <td className="px-2 py-1 text-right">{formatCurrency(row.totalDiscount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {couponUsageByStaffSorted.length > 0 && (
                <div className="mt-2 max-h-28 overflow-y-auto overflow-x-auto bg-white rounded border border-indigo-100">
                  <p className="px-2 py-1 text-[11px] font-semibold text-indigo-900 bg-indigo-100/40">Staff applications</p>
                  <table className="min-w-full text-xs">
                    <thead className="bg-indigo-100/60">
                      <tr>
                        <th className="px-2 py-1 text-left">Staff</th>
                        <th className="px-2 py-1 text-right">Apps</th>
                        <th className="px-2 py-1 text-right">Discount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {couponUsageByStaffSorted.map((row) => (
                        <tr key={row.staffId} className="border-t border-indigo-50">
                          <td className="px-2 py-1">{staffMap.get(row.staffId) ?? `Staff #${row.staffId}`}</td>
                          <td className="px-2 py-1 text-right">{row.redemptions}</td>
                          <td className="px-2 py-1 text-right">{formatCurrency(row.discountAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No coupon data for the selected range. Expand Sales Insights and click Load insights.</p>
          )}
        </CollapsibleReportSection>}

        {canViewPayrollReconciliation && showFinancial && (
        <CollapsibleReportSection
          id="report-payroll-reconciliation"
          title="Payroll vs revenue & GST"
          subtitle={
            <>
              Import a payroll JSON export, then compare gross pay to POS revenue and GST. <strong>Revenue</strong> here
              uses the same rules as <strong>Trends and Forecast</strong>: only fully paid orders, each sale counted on
              the <strong>last payment</strong> date in your business timezone, and amounts from line items. GST
              claimable is summed only for <strong>the imported pay period dates</strong> (not your full month-to-date
              unless they match). This block loads in a separate bundle so the rest of Reports stays responsive.
            </>
          }
          emphasizeExpandable
          className="mb-6"
        >
          <ReportErrorBoundary title="Payroll vs revenue & GST">
            <Suspense
              fallback={
                <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-6 text-sm text-gray-600">
                  Loading payroll tools…
                </div>
              }
            >
              <PayrollReconciliationPanel currentUser={currentUser} businessTimeZone={businessTimeZone} />
            </Suspense>
          </ReportErrorBoundary>
        </CollapsibleReportSection>
        )}

        {showFoodCost && <CollapsibleReportSection
          id="report-food-cost"
          title="Food cost report"
          subtitle="Theoretical vs. actual food cost — variance by category; loss points &gt; 2%. Data loads when you run Load (on-demand)."
          emphasizeExpandable
          className="mb-6"
        >
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <input type="date" value={foodCostStartDate} onChange={(e) => setFoodCostStartDate(e.target.value)} className="border rounded px-3 py-2" />
            <input type="date" value={foodCostEndDate} onChange={(e) => setFoodCostEndDate(e.target.value)} className="border rounded px-3 py-2" />
            <button onClick={loadFoodCostReport} className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
              Load Food Cost Report
            </button>
          </div>
          {foodCostReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                <div><p className="text-xs text-gray-500">Theoretical COGS</p><p className="font-semibold">{formatCurrency(foodCostReport.theoreticalCOGSTotal)}</p></div>
                <div><p className="text-xs text-gray-500">Actual COGS</p><p className="font-semibold">{formatCurrency(foodCostReport.actualCOGSTotal)}</p></div>
                <div><p className="text-xs text-gray-500">Variance</p><p className="font-semibold">{formatCurrency(foodCostReport.varianceTotal)} ({foodCostReport.variancePercentTotal.toFixed(1)}%)</p></div>
                <div><p className="text-xs text-gray-500">Loss points (&gt;2%)</p><p className="font-semibold text-amber-700">{foodCostReport.lossPoints.length} categories</p></div>
              </div>
              <p className="text-xs text-gray-500">Theoretical = recipe costing × items sold. Actual = usage × cost (set costPerUnit on inventory for full COGS). Loss points identify waste, theft, or over-portioning.</p>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50"><tr><th className="px-4 py-2 text-left">Category</th><th className="px-4 py-2 text-right">Theoretical</th><th className="px-4 py-2 text-right">Actual</th><th className="px-4 py-2 text-right">Variance %</th><th className="px-4 py-2">Loss</th></tr></thead>
                <tbody className="divide-y">
                  {foodCostReport.byCategory.map((r) => (
                    <tr key={r.category} className={r.lossPoint ? 'bg-amber-50' : ''}>
                      <td className="px-4 py-2">{r.category}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(r.theoreticalCOGS)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(r.actualCOGS)}</td>
                      <td className="px-4 py-2 text-right">{r.variancePercent.toFixed(1)}%</td>
                      <td className="px-4 py-2">{r.lossPoint ? 'Yes' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {foodCostReport.byMenuItem.length > 0 && (
                <>
                  <h3 className="text-sm font-semibold text-gray-800 mt-6 mb-2">Food cost by menu item (with recipe)</h3>
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">Menu item</th>
                        <th className="px-4 py-2 text-right">Qty sold</th>
                        <th className="px-4 py-2 text-right">Theoretical COGS</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {foodCostReport.byMenuItem.map((r) => (
                        <tr key={r.menuItemId}>
                          <td className="px-4 py-2">{r.menuItemName}</td>
                          <td className="px-4 py-2 text-right">{r.quantitySold}</td>
                          <td className="px-4 py-2 text-right">{formatCurrency(r.theoreticalCOGS)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </CollapsibleReportSection>}

        {showReconciliation && <CollapsibleReportSection
          id="report-sales-invoice"
          title="Sales by invoice"
          subtitle="Paid orders with cash/card breakdown. Nothing loads until you pick dates and click Load."
          emphasizeExpandable
          className="mb-6"
        >
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="min-w-[16rem]">
              {salesByInvoice.length > 0 && (
                <p className="text-sm text-gray-800 mb-1">
                  <span className="font-semibold">{salesByInvoice.length}</span> invoice
                  {salesByInvoice.length !== 1 ? 's' : ''} · Total{' '}
                  <span className="font-semibold">{formatCurrency(salesByInvoiceTotals.total)}</span> · Cash{' '}
                  <span className="font-semibold">{formatCurrency(salesByInvoiceTotals.cash)}</span> · Card{' '}
                  <span className="font-semibold">{formatCurrency(salesByInvoiceTotals.card)}</span> · Other{' '}
                  <span className="font-semibold">{formatCurrency(salesByInvoiceTotals.other)}</span>
                </p>
              )}
              <p className="text-sm text-gray-500">
                Every paid order with cash/card breakdown for reconciliation. Orders with a blank Session ID were not linked
                to a register when placed (blank Session ID = order was not linked to a register when placed).
              </p>
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-3">
              {renderDateInputs(
                salesByInvoiceStartDate,
                salesByInvoiceEndDate,
                setSalesByInvoiceStartDate,
                setSalesByInvoiceEndDate,
              )}
              <button
                type="button"
                onClick={() => void processSalesByInvoice()}
                disabled={salesByInvoiceLoading}
                className="px-3 py-2 text-sm rounded border border-pink-600 bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-50"
              >
                {salesByInvoiceLoading ? 'Loading…' : 'Load'}
              </button>
              <button
                onClick={() => exportToCSV('sales-by-invoice', salesByInvoiceColumns, salesByInvoice)}
                className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                disabled={salesByInvoiceLoading || !salesByInvoiceLoadedForCurrentRange || salesByInvoice.length === 0}
              >
                Export CSV
              </button>
            </div>
          </div>
          {salesByInvoiceError && <p className="text-sm text-red-600 mb-2">{salesByInvoiceError}</p>}
          <p className="text-xs text-slate-600 mb-3">
            Nothing is loaded until you choose a start and end date and click <span className="font-medium">Load</span> (saves a database round trip on first visit). Changing a date clears the list until you load again.
          </p>
          {salesByInvoiceLoading ? (
            <p className="text-gray-500">Loading sales by invoice...</p>
          ) : !salesByInvoiceLoadedForCurrentRange ? (
            <p className="text-gray-500">
              {!salesByInvoiceStartDate || !salesByInvoiceEndDate
                ? 'Select a start date and an end date, then click Load to fetch paid invoices for that range.'
                : 'Click Load to fetch invoices for the selected range.'}
            </p>
          ) : salesByInvoice.length === 0 ? (
            <p className="text-gray-500">No paid orders for the selected range.</p>
          ) : (
            <div className="space-y-3">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Invoice / Order ID</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Time</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Type</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-600">Total</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-600">Cash</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-600">Card</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-600">Other</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Session</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {salesByInvoice.map((row) => (
                    <tr key={row.orderId}>
                      <td className="px-4 py-2 font-medium text-gray-900">{row.orderId}</td>
                      <td className="px-4 py-2 text-gray-700">{row.date}</td>
                      <td className="px-4 py-2 text-gray-700">
                        {row.dateTime.includes(' ') ? row.dateTime.slice(row.dateTime.indexOf(' ') + 1) : '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-700 capitalize">{row.orderType}</td>
                      <td className="px-4 py-2 text-right font-medium text-gray-900">
                        {formatCurrency(row.totalAmount)}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-700">{formatCurrency(row.cashAmount)}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{formatCurrency(row.cardAmount)}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{formatCurrency(row.otherAmount)}</td>
                      <td className="px-4 py-2 text-gray-500 text-xs">{row.sessionId ?? '—'}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2 font-semibold text-gray-900" colSpan={4}>
                      Totals ({salesByInvoice.length} invoice{salesByInvoice.length !== 1 ? 's' : ''})
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-900">
                      {formatCurrency(salesByInvoiceTotals.total)}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-900">
                      {formatCurrency(salesByInvoiceTotals.cash)}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-900">
                      {formatCurrency(salesByInvoiceTotals.card)}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-900">
                      {formatCurrency(salesByInvoiceTotals.other)}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">—</td>
                  </tr>
                </tbody>
                </table>
              </div>
              {salesByInvoiceCursor && (
                <button
                  type="button"
                  onClick={() => void loadMoreSalesByInvoice()}
                  disabled={salesByInvoiceLoadingMore}
                  className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {salesByInvoiceLoadingMore ? 'Loading more…' : 'Load more'}
                </button>
              )}
            </div>
          )}
        </CollapsibleReportSection>}

        {showReconciliation && <CollapsibleReportSection
          id="report-uber-doordash"
          title="Uber / DoorDash sales"
          subtitle="Same date range and load state as Sales by invoice above."
          emphasizeExpandable
          className="mb-6 border-l-4 border-green-600"
        >
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="min-w-[16rem]">
              {salesByInvoiceUberDoordash.length > 0 && (
                <p className="text-sm text-gray-800 mb-1">
                  <span className="font-semibold">{salesByInvoiceUberDoordash.length}</span> order
                  {salesByInvoiceUberDoordash.length !== 1 ? 's' : ''} · Total{' '}
                  <span className="font-semibold">{formatCurrency(salesByInvoiceUberDoordashTotals.total)}</span>
                </p>
              )}
              <p className="text-sm text-gray-500">
                Paid orders received via Uber Eats / DoorDash (payment collected by platform). Use for reconciliation with platform payouts.
              </p>
            </div>
          </div>
          {!salesByInvoiceLoadedForCurrentRange ? (
            <p className="text-gray-500">Set start and end dates, then use Load in Sales by invoice above. This list uses the same range.</p>
          ) : salesByInvoiceUberDoordash.length === 0 ? (
            <p className="text-gray-500">No Uber/DoorDash orders for the selected range.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Order ID</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Time</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-600">Total</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-600">Session</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {salesByInvoiceUberDoordash.map((row) => (
                    <tr key={row.orderId}>
                      <td className="px-4 py-2 font-medium text-gray-900">{row.orderId}</td>
                      <td className="px-4 py-2 text-gray-700">{row.date}</td>
                      <td className="px-4 py-2 text-gray-700">
                        {row.dateTime.includes(' ') ? row.dateTime.slice(row.dateTime.indexOf(' ') + 1) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-gray-900">
                        {formatCurrency(row.totalAmount)}
                      </td>
                      <td className="px-4 py-2 text-gray-500 text-xs">{row.sessionId ?? '—'}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2 font-semibold text-gray-900" colSpan={3}>
                      Totals ({salesByInvoiceUberDoordash.length} order{salesByInvoiceUberDoordash.length !== 1 ? 's' : ''})
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-900">
                      {formatCurrency(salesByInvoiceUberDoordashTotals.total)}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CollapsibleReportSection>}

        {showReconciliation && <CollapsibleReportSection
          id="report-register-logs"
          title="Register logs"
          subtitle="Sessions for the date range. Data loads the first time you expand this section."
          emphasizeExpandable
          onFirstExpand={ensureRegisterLogsLoaded}
          className="mb-6"
        >
          <div className="flex flex-wrap items-center gap-4 mb-4">
            {renderDateInputs(registerStartDate, registerEndDate, setRegisterStartDate, setRegisterEndDate)}
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1">Terminal</label>
              <select
                value={selectedTerminal}
                onChange={(e) => setSelectedTerminal(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2"
              >
                <option value="all">All Terminals</option>
                {terminalOptions.map((terminal) => (
                  <option key={terminal} value={terminal}>
                    {terminal}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-2 ml-auto">
              <button
                onClick={() => exportToCSV('register-logs', registerColumns, filteredRegisterLogs)}
                className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Export CSV
              </button>
              <button
                onClick={() => exportToExcel('register-logs', registerColumns, filteredRegisterLogs)}
                className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Export Excel
              </button>
              <button
                onClick={() => exportToPDF('register-logs', 'Register Logs', registerColumns, filteredRegisterLogs)}
                className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Export PDF
              </button>
            </div>
          </div>

          {registerLoading ? (
            <p className="text-gray-500">Loading register logs...</p>
          ) : registerError ? (
            <p className="text-red-500">{registerError}</p>
          ) : filteredRegisterLogs.length === 0 ? (
            <p className="text-gray-500">No register sessions for the selected range.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {registerColumns.map((col) => (
                      <th key={col.label} className="px-4 py-2 text-left font-semibold text-gray-600">
                        {col.label}
                      </th>
                    ))}
                    {isAdmin && (
                      <th className="px-4 py-2 text-right font-semibold text-gray-600">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredRegisterLogs.map((log) => (
                    <tr key={log.id}>
                      {registerColumns.map((col) => (
                        <td key={col.label} className="px-4 py-2 text-gray-700">
                          {col.formatter ? col.formatter((log as any)[col.key as keyof RegisterLogRow], log) : (log as any)[col.key as keyof RegisterLogRow] ?? '—'}
                        </td>
                      ))}
                      {isAdmin && (
                        <td className="px-4 py-2 text-right">
                          {!log.isActive && (
                            <button
                              type="button"
                              onClick={() => openEditRegisterLog(log)}
                              className="text-pink-600 hover:text-pink-800 font-medium text-sm"
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CollapsibleReportSection>}

        {editRegisterLog && isAdmin && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-40 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Edit Register Log</h3>
                <button type="button" onClick={closeEditRegisterLog} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
              <div className="px-6 py-4 space-y-4">
                <p className="text-sm text-gray-600">
                  {editRegisterLog.terminalName || editRegisterLog.terminalId} · {editRegisterLog.date} · {editRegisterLog.cashierName}
                </p>
                <p className="text-xs text-amber-700">Admin only. Use to correct amounts if closing was entered with actual cash only.</p>
                <div className="grid grid-cols-1 gap-4">
                  <label className="text-sm text-gray-700">
                    Opening Float
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editRegisterOpeningFloat}
                      onChange={(e) => setEditRegisterOpeningFloat(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Closing Float
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editRegisterClosingFloat}
                      onChange={(e) => setEditRegisterClosingFloat(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Cash Sales
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editRegisterCashSales}
                      onChange={(e) => setEditRegisterCashSales(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Card Sales
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editRegisterCardSales}
                      onChange={(e) => setEditRegisterCardSales(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </label>
                  <div className="text-sm text-gray-600">
                    Variance: {editRegisterClosingFloat !== '' && editRegisterOpeningFloat !== '' && editRegisterCashSales !== ''
                      ? `$${(Number(editRegisterClosingFloat) - Number(editRegisterOpeningFloat) - Number(editRegisterCashSales)).toFixed(2)}`
                      : '—'}
                  </div>
                </div>
                {editRegisterError && <p className="text-sm text-red-600">{editRegisterError}</p>}
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={closeEditRegisterLog} className="px-4 py-2 rounded border border-gray-300 text-gray-700" disabled={editRegisterSaving}>Cancel</button>
                  <button type="button" onClick={saveEditRegisterLog} className="px-4 py-2 rounded bg-pink-600 text-white disabled:opacity-50" disabled={editRegisterSaving}>
                    {editRegisterSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </main>
    </div>
      );
    })()
  );

  return renderDetailedView();
};

export default ReportsPage;
